import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Platform,
  Clipboard,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../DirectionalIcon';
import { colors, spacing, typography } from '../../theme';
import { apiService } from '../../services/api';
import { MediaService } from '../../services/media.service';
import { MapLocationPicker } from '../MapLocationPicker';
import { VoiceRecorder } from '../VoiceRecorder';
import { BusinessUserPicker } from '../BusinessUserPicker';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ApiKey {
  _id: string;
  label: string;
  keyPrefix: string;
  status: number;
  expiresAt?: string;
  lastUsedAt?: string;
  createdAt: string;
}

interface Webhook {
  _id: string;
  title: string;
  channel: string;
  webhookUrl: string;
  secretPrefix: string;
  status: number;
  events: string[];
  createdAt: string;
  updatedAt: string;
}

/** Mirrors ASSIGNMENT_TYPES from assignment-webhook.entity.ts on the backend. */
const ASSIGNMENT_TYPES = [
  'contact', 'conversation', 'task', 'ticket', 'calendar', 'email', 'lead', 'deal', 'project', 'data_list_record',
] as const;

interface AssignmentWebhook {
  _id: string;
  title: string;
  webhookUrl: string;
  secretPrefix: string;
  status: number;
  assignmentTypes: string[];
  allAssignments?: boolean;
  userIds?: string[];
  departmentIds?: string[];
  createdAt: string;
  updatedAt: string;
}

type ActiveTab = 'keys' | 'webhooks' | 'docs';

/**
 * Webhooks aren't only a "channels" concept — Channels is just the first
 * resource that has webhooks (message.received, call.started, etc.).
 * Assignments is a second, independent resource (fires on Assignment
 * creation, not tied to any channel). Modeled as sub-tabs within the single
 * Webhooks tab so future resources (e.g. Products, Calendar) can be added
 * the same way without the top-level tab bar growing per-resource.
 */
type WebhooksSubTab = 'channels' | 'assignments';

interface Props {
  onBack?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────


/** Events each webhook channel can subscribe to (mirrors the checkboxes below). */
const WEBHOOK_CHANNEL_EVENTS: Record<string, string[]> = {
  whatsapp: ['message.received', 'message.status'],
  phone: ['call.started', 'call.status_changed', 'call.note_added'],
  catalog: ['catalog.created', 'catalog.updated', 'catalog.deleted'],
  product: ['product.created', 'product.updated', 'product.deleted'],
  order: ['order.created', 'order.updated', 'order.status_changed', 'order.cancelled', 'order.deleted'],
  vendor: ['vendor.created', 'vendor.updated', 'vendor.deleted'],
  quotation: ['quotation.created', 'quotation.updated', 'quotation.sent', 'quotation.accepted', 'quotation.rejected', 'quotation.converted', 'quotation.deleted'],
};

export const DeveloperAccessSettings: React.FC<Props> = ({ onBack }) => {
  const styles = useThemedStyles(getStyles);
  const [activeTab, setActiveTab] = useState<ActiveTab>('keys');

  // Access error (e.g. plan not enabled)
  const [accessError, setAccessError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [copyConfirm, setCopyConfirm] = useState<string | null>(null); // shows brief "Copied!" label

  // API Keys state
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState<{ keyId: string; rawKey: string } | null>(null);
  const [keyLabelError, setKeyLabelError] = useState<string | null>(null);
  const [webhookUrlError, setWebhookUrlError] = useState<string | null>(null);

  // Webhooks state
  const [webhooksSubTab, setWebhooksSubTab] = useState<WebhooksSubTab>('channels');
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [webhookForm, setWebhookForm] = useState({
    title: '',
    channel: 'whatsapp',
    webhookUrl: '',
    events: ['message.received', 'message.status'] as string[],
  });
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<{ channel: string; rawSecret: string } | null>(null);
  const [editingWebhook, setEditingWebhook] = useState<string | null>(null);

  // Assignment Webhooks state — channel-independent, separate resource
  const [assignmentWebhooks, setAssignmentWebhooks] = useState<AssignmentWebhook[]>([]);
  const [assignmentWebhooksLoading, setAssignmentWebhooksLoading] = useState(false);
  const [assignmentWebhookForm, setAssignmentWebhookForm] = useState({
    title: '',
    webhookUrl: '',
    assignmentTypes: [] as string[],
    allAssignments: false,
    userIds: [] as string[],
    departmentIds: [] as string[],
  });
  const [savingAssignmentWebhook, setSavingAssignmentWebhook] = useState(false);
  const [revealedAssignmentSecret, setRevealedAssignmentSecret] = useState<{ rawSecret: string } | null>(null);
  const [editingAssignmentWebhook, setEditingAssignmentWebhook] = useState<string | null>(null);
  const [showAssignedUserPicker, setShowAssignedUserPicker] = useState(false);
  const [showAssignedDeptPicker, setShowAssignedDeptPicker] = useState(false);

  // Inline delete confirmation state
  const [confirmingDelete, setConfirmingDelete] = useState<{ type: 'key' | 'webhook' | 'assignmentWebhook'; id: string } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Live playground state
  const [playSelectedKeyId, setPlaySelectedKeyId] = useState<string>('');
  const [playRawKeyInput, setPlayRawKeyInput] = useState('');
  const [playPhone, setPlayPhone] = useState('');
  const [playMsgType, setPlayMsgType] = useState<'text' | 'image' | 'video' | 'audio' | 'document' | 'location' | 'template'>('text');
  const [playWindowMode, setPlayWindowMode] = useState<'free' | 'template'>('free');
  // free message fields
  const [playText, setPlayText] = useState('Hello from DISLIX API!');
  const [playReplyMode, setPlayReplyMode] = useState<'none' | 'items' | 'set'>('none');
  const [playReplyItemsInput, setPlayReplyItemsInput] = useState('Yes\nNo');
  const [playReplySets, setPlayReplySets] = useState<any[]>([]);
  const [playReplySetsLoading, setPlayReplySetsLoading] = useState(false);
  const [playSelectedReplySetId, setPlaySelectedReplySetId] = useState('');
  const [playReplySetItems, setPlayReplySetItems] = useState<any[]>([]);
  const [playReplyListButtonText, setPlayReplyListButtonText] = useState('Choose an option');
  const [playMediaUrl, setPlayMediaUrl] = useState('');
  const [playMediaCaption, setPlayMediaCaption] = useState('');
  const [playMediaFilename, setPlayMediaFilename] = useState('');
  const [playMediaFile, setPlayMediaFile] = useState<{ name: string; size: number } | null>(null);
  const [playMediaMimeType, setPlayMediaMimeType] = useState('');
  const [playMediaUploading, setPlayMediaUploading] = useState(false);
  const [playLat, setPlayLat] = useState('');
  const [playLng, setPlayLng] = useState('');
  const [playLocationAddress, setPlayLocationAddress] = useState('');
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  // template fields
  const [playTemplates, setPlayTemplates] = useState<any[]>([]);
  const [playTemplatesLoading, setPlayTemplatesLoading] = useState(false);
  const [playSelectedTemplate, setPlaySelectedTemplate] = useState<any | null>(null);
  const [playTemplateParams, setPlayTemplateParams] = useState<Record<string, string>>({});
  const [playTemplateHeaderMedia, setPlayTemplateHeaderMedia] = useState<{ url: string; mimeType: string; name: string } | null>(null);
  const [playTemplateHeaderUploading, setPlayTemplateHeaderUploading] = useState(false);
  const [playSending, setPlaySending] = useState(false);
  const [playResponse, setPlayResponse] = useState<{ ok: boolean; status: number; body: string } | null>(null);

  // GET endpoint playground state
  const [expandedEndpoint, setExpandedEndpoint] = useState<string | null>(null);
  const [epSearch, setEpSearch] = useState('');
  const scrollViewRef = useRef<ScrollView>(null);

  const epItemRefs = useRef<Record<string, any>>({});

  useEffect(() => {
    if (!expandedEndpoint) return;
    const itemRef = epItemRefs.current[expandedEndpoint];
    if (!itemRef || !scrollViewRef.current) return;
    setTimeout(() => {
      itemRef.measureLayout(
        (scrollViewRef.current as any)?.getInnerViewRef?.() ?? (scrollViewRef.current as any),
        (_x: number, y: number) => {
          scrollViewRef.current?.scrollTo({ y: y - 16, animated: true });
        },
        () => {}
      );
    }, 120);
  }, [expandedEndpoint]);
  // Per-endpoint inputs keyed by endpoint id
  const [epInputs, setEpInputs] = useState<Record<string, Record<string, string>>>({});
  const [epSending, setEpSending] = useState<Record<string, boolean>>({});
  const [epResponse, setEpResponse] = useState<Record<string, { ok: boolean; status: number; body: string }>>({});

  // ─── Loaders ───────────────────────────────────────────────────────

  const loadApiKeys = useCallback(async (reportError = false) => {
    setKeysLoading(true);
    try {
      const res = await apiService.listApiKeys();
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setApiKeys(res.data);
      } else if (reportError) {
        setAccessError(res.errors?.[0]?.message || 'Failed to load API keys.');
      }
    } catch (err: any) {
      if (reportError) setAccessError(err?.message || 'Failed to load API keys.');
    } finally {
      setKeysLoading(false);
    }
  }, []);

  const loadWebhooks = useCallback(async (reportError = false) => {
    setWebhooksLoading(true);
    try {
      const res = await apiService.listWebhooks();
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setWebhooks(res.data);
      } else if (reportError) {
        setAccessError(res.errors?.[0]?.message || 'Failed to load webhooks.');
      }
    } catch (err: any) {
      if (reportError) setAccessError(err?.message || 'Failed to load webhooks.');
    } finally {
      setWebhooksLoading(false);
    }
  }, []);

  const loadAssignmentWebhooks = useCallback(async (reportError = false) => {
    setAssignmentWebhooksLoading(true);
    try {
      const res = await apiService.listAssignmentWebhooks();
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setAssignmentWebhooks(res.data);
      } else if (reportError) {
        setAccessError(res.errors?.[0]?.message || 'Failed to load assignment webhooks.');
      }
    } catch (err: any) {
      if (reportError) setAccessError(err?.message || 'Failed to load assignment webhooks.');
    } finally {
      setAssignmentWebhooksLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setInitialLoading(true);
      await Promise.all([loadApiKeys(true), loadWebhooks(true), loadAssignmentWebhooks(true)]);
      setInitialLoading(false);
    };
    init();
  }, [loadApiKeys, loadWebhooks, loadAssignmentWebhooks]);

  // ─── API Key actions ────────────────────────────────────────────────

  const handleCreateKey = async () => {
    const label = newKeyLabel.trim();
    if (!label) {
      setKeyLabelError('Please enter a label for the API key.');
      return;
    }
    setKeyLabelError(null);
    setCreatingKey(true);
    try {
      const res = await apiService.createApiKey(label);
      if (res.statusCode === 1000 && res.data) {
        const { rawKey, apiKey } = res.data as any;
        setRevealedKey({ keyId: apiKey._id, rawKey });
        setNewKeyLabel('');
        await loadApiKeys();
      } else {
        setKeyLabelError(res.errors?.[0]?.message || 'Failed to create API key.');
      }
    } catch (err: any) {
      setKeyLabelError(err.message || 'Failed to create API key.');
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeKey = (key: ApiKey) => {
    setConfirmingDelete({ type: 'key', id: key._id });
  };

  const confirmRevokeKey = async (keyId: string) => {
    setDeletingId(keyId);
    setConfirmingDelete(null);
    try {
      const res = await apiService.revokeApiKey(keyId);
      if (res.statusCode === 1000) {
        await loadApiKeys();
      }
    } catch {
      // silently fail — list will remain unchanged
    } finally {
      setDeletingId(null);
    }
  };

  // ─── Webhook actions ─────────────────────────────────────────────────

  const handleSaveWebhook = async () => {
    const title = webhookForm.title.trim();
    const url = webhookForm.webhookUrl.trim();
    if (!title) {
      setWebhookUrlError('Webhook title is required');
      return;
    }
    if (!url.startsWith('https://')) {
      setWebhookUrlError('Webhook URL must start with https://');
      return;
    }
    setWebhookUrlError(null);
    setSavingWebhook(true);
    try {
      const events = webhookForm.events;

      let res: any;
      if (editingWebhook) {
        res = await apiService.updateWebhook(editingWebhook, { title, webhookUrl: url, events });
      } else {
        res = await apiService.upsertWebhook({ title, channel: webhookForm.channel, webhookUrl: url, events });
      }

      if (res.statusCode === 1000 && res.data) {
        if (res.data.rawSecret) {
          setRevealedSecret({ channel: webhookForm.channel, rawSecret: res.data.rawSecret });
        }
        setEditingWebhook(null);
        setWebhookForm({
          title: '',
          channel: 'whatsapp',
          webhookUrl: '',
          events: ['message.received', 'message.status'],
        });
        await loadWebhooks();
      } else {
        setWebhookUrlError(res.errors?.[0]?.message || 'Failed to save webhook.');
      }
    } catch (err: any) {
      setWebhookUrlError(err.message || 'Failed to save webhook.');
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleDeleteWebhook = (webhook: Webhook) => {
    setConfirmingDelete({ type: 'webhook', id: webhook._id });
  };

  const confirmDeleteWebhook = async (webhookId: string) => {
    setDeletingId(webhookId);
    setConfirmingDelete(null);
    try {
      const res = await apiService.deleteWebhook(webhookId);
      if (res.statusCode === 1000) {
        await loadWebhooks();
      }
    } catch {
      // silently fail — list will remain unchanged
    } finally {
      setDeletingId(null);
    }
  };

  const handleRegenerateWebhookSecret = async (webhook: Webhook) => {
    try {
      const res = await apiService.updateWebhook(webhook._id, { regenerateSecret: true });
      if (res.statusCode === 1000 && (res.data as any)?.rawSecret) {
        setRevealedSecret({ channel: webhook.channel, rawSecret: (res.data as any).rawSecret });
        await loadWebhooks();
      }
    } catch {
      // list stays unchanged — the old secret remains valid
    }
  };

  const handleEditWebhook = (webhook: Webhook) => {
    setWebhookForm({
      title: webhook.title || '',
      channel: webhook.channel,
      webhookUrl: webhook.webhookUrl,
      events: webhook.events,
    });
    setEditingWebhook(webhook._id);
    setActiveTab('webhooks');
  };

  // ─── Assignment Webhook actions ──────────────────────────────────────

  const handleSaveAssignmentWebhook = async () => {
    const title = assignmentWebhookForm.title.trim();
    const url = assignmentWebhookForm.webhookUrl.trim();
    if (!title) {
      setWebhookUrlError('Webhook title is required');
      return;
    }
    if (!url.startsWith('https://')) {
      setWebhookUrlError('Webhook URL must start with https://');
      return;
    }
    setWebhookUrlError(null);
    setSavingAssignmentWebhook(true);
    try {
      const data = {
        title,
        webhookUrl: url,
        assignmentTypes: assignmentWebhookForm.assignmentTypes,
        allAssignments: assignmentWebhookForm.allAssignments,
        userIds: assignmentWebhookForm.userIds,
        departmentIds: assignmentWebhookForm.departmentIds,
      };

      const res = editingAssignmentWebhook
        ? await apiService.updateAssignmentWebhook(editingAssignmentWebhook, data)
        : await apiService.upsertAssignmentWebhook(data);

      if (res.statusCode === 1000 && res.data) {
        if ((res.data as any).rawSecret) {
          setRevealedAssignmentSecret({ rawSecret: (res.data as any).rawSecret });
        }
        setEditingAssignmentWebhook(null);
        setAssignmentWebhookForm({
          title: '',
          webhookUrl: '',
          assignmentTypes: [],
          allAssignments: false,
          userIds: [],
          departmentIds: [],
        });
        await loadAssignmentWebhooks();
      } else {
        setWebhookUrlError(res.errors?.[0]?.message || 'Failed to save webhook.');
      }
    } catch (err: any) {
      setWebhookUrlError(err.message || 'Failed to save webhook.');
    } finally {
      setSavingAssignmentWebhook(false);
    }
  };

  const handleDeleteAssignmentWebhook = (webhook: AssignmentWebhook) => {
    setConfirmingDelete({ type: 'assignmentWebhook', id: webhook._id });
  };

  const confirmDeleteAssignmentWebhook = async (webhookId: string) => {
    setDeletingId(webhookId);
    setConfirmingDelete(null);
    try {
      const res = await apiService.deleteAssignmentWebhook(webhookId);
      if (res.statusCode === 1000) {
        await loadAssignmentWebhooks();
      }
    } catch {
      // silently fail — list will remain unchanged
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditAssignmentWebhook = (webhook: AssignmentWebhook) => {
    setAssignmentWebhookForm({
      title: webhook.title || '',
      webhookUrl: webhook.webhookUrl,
      assignmentTypes: webhook.assignmentTypes || [],
      allAssignments: webhook.allAssignments || false,
      userIds: webhook.userIds || [],
      departmentIds: webhook.departmentIds || [],
    });
    setEditingAssignmentWebhook(webhook._id);
    setActiveTab('webhooks');
    setWebhooksSubTab('assignments');
  };

  // ─── Clipboard helper ────────────────────────────────────────────────

  const copyToClipboard = (text: string, confirmKey: string) => {
    if (Platform.OS === 'web') {
      navigator.clipboard?.writeText(text).catch(() => {});
    } else {
      Clipboard.setString(text);
    }
    setCopyConfirm(confirmKey);
    setTimeout(() => setCopyConfirm(null), 2000);
  };

  // ─── Format helpers ──────────────────────────────────────────────────

  const fmtDate = (iso: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatWebhookChannel = (channel: string) => {
    const normalized = (channel || '').toLowerCase();
    if (normalized === 'whatsapp') return 'WhatsApp';
    if (normalized === 'facebook_messenger' || normalized === 'facebook-messenger') return 'Facebook Messenger';
    if (normalized === 'x_twitter' || normalized === 'x-twitter') return 'X (Twitter)';
    if (normalized === 'phone') return 'Phone';
    if (normalized === 'catalog') return 'Catalog';
    if (normalized === 'product') return 'Product';
    if (normalized === 'order') return 'Order';
    if (normalized === 'vendor') return 'Vendor';
    if (normalized === 'quotation') return 'Quotation';
    return channel;
  };

  // ─── Render tabs ─────────────────────────────────────────────────────

  const renderTab = (id: ActiveTab, label: string, icon: string) => (
    <TouchableOpacity
      key={id}
      style={[styles.tab, activeTab === id && styles.tabActive]}
      onPress={() => setActiveTab(id)}
    >
      <Ionicons name={icon as any} size={16} color={activeTab === id ? colors.primary : colors.text.secondary} />
      <Text style={[styles.tabLabel, activeTab === id && styles.tabLabelActive]}>{label}</Text>
    </TouchableOpacity>
  );

  // ─── API Keys tab ────────────────────────────────────────────────────

  const renderKeysTab = () => (
    <View>
      {/* One-time revealed key banner */}
      {revealedKey && (
        <View style={styles.secretBanner}>
          <View style={styles.secretBannerHeader}>
            <Ionicons name="key" size={16} color={colors.warning} />
            <Text style={styles.secretBannerTitle}>Save your API key — shown once only</Text>
          </View>
          <Text style={styles.secretBannerSub}>
            Copy it now. You won't be able to see the full key again after closing this.
          </Text>
          <View style={styles.secretBox}>
            <Text style={styles.secretText} selectable numberOfLines={1}>{revealedKey.rawKey}</Text>
          </View>
          <View style={styles.secretActions}>
            <TouchableOpacity
              style={[styles.primaryBtn, { flex: 1 }]}
              onPress={() => copyToClipboard(revealedKey.rawKey, 'revealedKey')}
            >
              <Ionicons
                name={copyConfirm === 'revealedKey' ? 'checkmark' : 'copy-outline'}
                size={16}
                color={colors.white}
              />
              <Text style={styles.primaryBtnText}>
                {copyConfirm === 'revealedKey' ? 'Copied!' : 'Copy API Key'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.outlineBtn} onPress={() => setRevealedKey(null)}>
              <Text style={styles.outlineBtnText}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Create key form */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Generate API Key</Text>
        <Text style={styles.cardDesc}>
          Use API keys to authenticate requests from your server or integration. Each key is tied to this business account.
        </Text>
        <View style={styles.inlineRow}>
          <TextInput
            style={[styles.input, { flex: 1, marginBottom: 0 }, keyLabelError ? styles.inputError : null]}
            placeholder="Label (e.g. Production Server)"
            placeholderTextColor={colors.text.disabled}
            value={newKeyLabel}
            onChangeText={v => { setNewKeyLabel(v); setKeyLabelError(null); }}
          />
          <TouchableOpacity
            style={[styles.primaryBtn, { marginLeft: spacing.sm }, creatingKey && styles.primaryBtnDisabled]}
            onPress={handleCreateKey}
            disabled={creatingKey}
          >
            {creatingKey ? (
              <ActivityIndicator size="small" color={colors.white} />
            ) : (
              <Text style={styles.primaryBtnText}>Generate</Text>
            )}
          </TouchableOpacity>
        </View>
        {keyLabelError && <Text style={styles.fieldError}>{keyLabelError}</Text>}
      </View>

      {/* Keys list */}
      <View style={styles.card}>
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>Active Keys</Text>
          {keysLoading && <ActivityIndicator size="small" color={colors.primary} />}
        </View>
        {apiKeys.length === 0 && !keysLoading ? (
          <View style={styles.emptyState}>
            <Ionicons name="key-outline" size={32} color={colors.text.disabled} />
            <Text style={styles.emptyText}>No API keys yet</Text>
          </View>
        ) : (
          apiKeys.map(key => {
            const isConfirming = confirmingDelete?.type === 'key' && confirmingDelete.id === key._id;
            const isDeleting = deletingId === key._id;
            return (
              <View key={key._id} style={styles.listItem}>
                <View style={styles.listItemIcon}>
                  <Ionicons name="key-outline" size={18} color={colors.primary} />
                </View>
                <View style={styles.listItemContent}>
                  <Text style={styles.listItemTitle}>{key.label}</Text>
                  <Text style={styles.listItemMeta}>
                    {key.keyPrefix}•••••  ·  Created {fmtDate(key.createdAt)}
                    {key.lastUsedAt ? `  ·  Last used ${fmtDate(key.lastUsedAt)}` : '  ·  Never used'}
                  </Text>
                  {isConfirming && (
                    <View style={styles.inlineConfirm}>
                      <Text style={styles.inlineConfirmText}>Revoke this key?</Text>
                      <TouchableOpacity style={styles.inlineConfirmCancel} onPress={() => setConfirmingDelete(null)}>
                        <Text style={styles.inlineConfirmCancelText}>Cancel</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.inlineConfirmDanger} onPress={() => confirmRevokeKey(key._id)}>
                        <Text style={styles.inlineConfirmDangerText}>Revoke</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
                {!isConfirming && (
                  isDeleting ? (
                    <ActivityIndicator size="small" color={colors.error} style={{ marginLeft: spacing.xs }} />
                  ) : (
                    <TouchableOpacity onPress={() => handleRevokeKey(key)} style={styles.dangerIconBtn}>
                      <Ionicons name="trash-outline" size={18} color={colors.error} />
                    </TouchableOpacity>
                  )
                )}
              </View>
            );
          })
        )}
      </View>
    </View>
  );

  // ─── Webhooks tab ────────────────────────────────────────────────────
  // Webhooks aren't a "channels-only" concept — Channels is the first
  // resource with webhooks, Assignments is a second independent one (fires
  // on Assignment creation, not tied to any channel). Sub-tabbed within
  // this one Webhooks tab so future resources can be added the same way.

  const renderWebhooksSubTab = (id: WebhooksSubTab, label: string) => (
    <TouchableOpacity
      key={id}
      style={[styles.segmentBtn, webhooksSubTab === id && styles.segmentBtnActive]}
      onPress={() => setWebhooksSubTab(id)}
    >
      <Text style={[styles.segmentBtnText, webhooksSubTab === id && styles.segmentBtnTextActive]}>{label}</Text>
    </TouchableOpacity>
  );

  const renderWebhooksTab = () => (
    <View>
      <View style={[styles.segmentRow, { marginBottom: spacing.md }]}>
        {renderWebhooksSubTab('channels', 'Channels')}
        {renderWebhooksSubTab('assignments', 'Assignments')}
      </View>
      {webhooksSubTab === 'channels' ? renderChannelWebhooksSection() : renderAssignmentWebhooksSection()}
    </View>
  );

  const renderChannelWebhooksSection = () => (
    <View>
      {/* One-time revealed secret banner */}
      {revealedSecret && (
        <View style={styles.secretBanner}>
          <View style={styles.secretBannerHeader}>
            <Ionicons name="lock-closed" size={16} color={colors.warning} />
            <Text style={styles.secretBannerTitle}>Save your webhook secret — shown once only</Text>
          </View>
          <Text style={styles.secretBannerSub}>
            Use this to verify incoming webhook signatures. Check the{' '}
            <Text style={{ fontFamily: typography.fontFamily.medium }}>X-Sendifier-Signature</Text> header:
            {'\n'}sha256=HMAC-SHA256(secret, rawBody)
          </Text>
          <View style={styles.secretBox}>
            <Text style={styles.secretText} selectable numberOfLines={1}>{revealedSecret.rawSecret}</Text>
          </View>
          <View style={styles.secretActions}>
            <TouchableOpacity
              style={[styles.primaryBtn, { flex: 1 }]}
              onPress={() => copyToClipboard(revealedSecret.rawSecret, 'revealedSecret')}
            >
              <Ionicons
                name={copyConfirm === 'revealedSecret' ? 'checkmark' : 'copy-outline'}
                size={16}
                color={colors.white}
              />
              <Text style={styles.primaryBtnText}>
                {copyConfirm === 'revealedSecret' ? 'Copied!' : 'Copy Webhook Secret'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.outlineBtn} onPress={() => setRevealedSecret(null)}>
              <Text style={styles.outlineBtnText}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Webhook form */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>{editingWebhook ? 'Update Webhook' : 'Register Webhook'}</Text>
        <Text style={styles.cardDesc}>
          DISLIX will POST a signed JSON payload to your URL whenever a message is received on this channel.
        </Text>

        {/* Channel selector */}
        <Text style={styles.fieldLabel}>Channel</Text>
        <View style={styles.segmentRow}>
          {(['whatsapp', 'facebook_messenger', 'x_twitter', 'phone', 'catalog', 'product', 'order', 'vendor', 'quotation'] as const).map(ch => (
            <TouchableOpacity
              key={ch}
              style={[styles.segmentBtn, webhookForm.channel === ch && styles.segmentBtnActive]}
              onPress={() => setWebhookForm(f => (
                f.channel === ch ? f : {
                  ...f,
                  channel: ch,
                  // Events are per channel — switching starts with all of the
                  // new channel's events ticked instead of keeping the old
                  // channel's (which never fire there).
                  events: WEBHOOK_CHANNEL_EVENTS[ch as string] ? [...WEBHOOK_CHANNEL_EVENTS[ch as string]] : f.events,
                }
              ))}
              disabled={!!editingWebhook}
            >
              <Text style={[styles.segmentBtnText, webhookForm.channel === ch && styles.segmentBtnTextActive]}>
                {formatWebhookChannel(ch)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <Text style={styles.fieldLabel}>Webhook Title</Text>
        <TextInput
          style={[styles.input, webhookUrlError ? styles.inputError : null]}
          placeholder={
            webhookForm.channel === 'x_twitter'
              ? 'My X Mentions Hook'
              : webhookForm.channel === 'facebook_messenger'
                ? 'My Facebook CRM Hook'
                : webhookForm.channel === 'phone'
                  ? 'My Call Tracking Hook'
                  : webhookForm.channel === 'catalog'
                    ? 'My Catalog Sync Hook'
                    : webhookForm.channel === 'product'
                      ? 'My Inventory Sync Hook'
                      : webhookForm.channel === 'order'
                        ? 'My Order Fulfillment Hook'
                        : webhookForm.channel === 'vendor'
                          ? 'My Vendor Sync Hook'
                          : webhookForm.channel === 'quotation'
                            ? 'My Quotation Lifecycle Hook'
                            : 'My WhatsApp CRM Hook'
          }
          placeholderTextColor={colors.text.disabled}
          value={webhookForm.title}
          onChangeText={v => { setWebhookForm(f => ({ ...f, title: v })); setWebhookUrlError(null); }}
        />

        <Text style={styles.fieldLabel}>Webhook URL (HTTPS only)</Text>
        <TextInput
          style={[styles.input, webhookUrlError ? styles.inputError : null]}
          placeholder="https://yourserver.com/webhook"
          placeholderTextColor={colors.text.disabled}
          value={webhookForm.webhookUrl}
          onChangeText={v => { setWebhookForm(f => ({ ...f, webhookUrl: v })); setWebhookUrlError(null); }}
          autoCapitalize="none"
          keyboardType="url"
        />
        {webhookUrlError && <Text style={styles.fieldError}>{webhookUrlError}</Text>}

        <Text style={styles.fieldLabel}>Events</Text>
        {webhookForm.channel === 'whatsapp' && (['message.received', 'message.status'] as const).map(ev => {
          const checked = webhookForm.events.includes(ev);
          return (
            <TouchableOpacity
              key={ev}
              onPress={() =>
                setWebhookForm(f => ({
                  ...f,
                  events: checked
                    ? f.events.filter(e => e !== ev)
                    : [...f.events, ev],
                }))
              }
              style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}
            >
              <View style={{
                width: 20, height: 20, borderRadius: 4,
                borderWidth: 2, borderColor: checked ? colors.primary : colors.border,
                backgroundColor: checked ? colors.primary : 'transparent',
                alignItems: 'center', justifyContent: 'center',
              }}>
                {checked && <Ionicons name="checkmark" size={13} color="#fff" />}
              </View>
              <Text style={{ fontSize: 14, color: colors.text.primary }}>{ev}</Text>
            </TouchableOpacity>
          );
        })}
        {webhookForm.channel === 'phone' && (['call.started', 'call.status_changed', 'call.note_added'] as const).map(ev => {
          const checked = webhookForm.events.includes(ev);
          return (
            <TouchableOpacity
              key={ev}
              onPress={() =>
                setWebhookForm(f => ({
                  ...f,
                  events: checked
                    ? f.events.filter(e => e !== ev)
                    : [...f.events, ev],
                }))
              }
              style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}
            >
              <View style={{
                width: 20, height: 20, borderRadius: 4,
                borderWidth: 2, borderColor: checked ? colors.primary : colors.border,
                backgroundColor: checked ? colors.primary : 'transparent',
                alignItems: 'center', justifyContent: 'center',
              }}>
                {checked && <Ionicons name="checkmark" size={13} color="#fff" />}
              </View>
              <Text style={{ fontSize: 14, color: colors.text.primary }}>{ev}</Text>
            </TouchableOpacity>
          );
        })}
        {webhookForm.channel === 'catalog' && (['catalog.created', 'catalog.updated', 'catalog.deleted'] as const).map(ev => {
          const checked = webhookForm.events.includes(ev);
          return (
            <TouchableOpacity
              key={ev}
              onPress={() =>
                setWebhookForm(f => ({
                  ...f,
                  events: checked
                    ? f.events.filter(e => e !== ev)
                    : [...f.events, ev],
                }))
              }
              style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}
            >
              <View style={{
                width: 20, height: 20, borderRadius: 4,
                borderWidth: 2, borderColor: checked ? colors.primary : colors.border,
                backgroundColor: checked ? colors.primary : 'transparent',
                alignItems: 'center', justifyContent: 'center',
              }}>
                {checked && <Ionicons name="checkmark" size={13} color="#fff" />}
              </View>
              <Text style={{ fontSize: 14, color: colors.text.primary }}>{ev}</Text>
            </TouchableOpacity>
          );
        })}
        {webhookForm.channel === 'product' && (['product.created', 'product.updated', 'product.deleted'] as const).map(ev => {
          const checked = webhookForm.events.includes(ev);
          return (
            <TouchableOpacity
              key={ev}
              onPress={() =>
                setWebhookForm(f => ({
                  ...f,
                  events: checked
                    ? f.events.filter(e => e !== ev)
                    : [...f.events, ev],
                }))
              }
              style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}
            >
              <View style={{
                width: 20, height: 20, borderRadius: 4,
                borderWidth: 2, borderColor: checked ? colors.primary : colors.border,
                backgroundColor: checked ? colors.primary : 'transparent',
                alignItems: 'center', justifyContent: 'center',
              }}>
                {checked && <Ionicons name="checkmark" size={13} color="#fff" />}
              </View>
              <Text style={{ fontSize: 14, color: colors.text.primary }}>{ev}</Text>
            </TouchableOpacity>
          );
        })}
        {webhookForm.channel === 'order' && (['order.created', 'order.updated', 'order.status_changed', 'order.cancelled', 'order.deleted'] as const).map(ev => {
          const checked = webhookForm.events.includes(ev);
          return (
            <TouchableOpacity
              key={ev}
              onPress={() =>
                setWebhookForm(f => ({
                  ...f,
                  events: checked
                    ? f.events.filter(e => e !== ev)
                    : [...f.events, ev],
                }))
              }
              style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}
            >
              <View style={{
                width: 20, height: 20, borderRadius: 4,
                borderWidth: 2, borderColor: checked ? colors.primary : colors.border,
                backgroundColor: checked ? colors.primary : 'transparent',
                alignItems: 'center', justifyContent: 'center',
              }}>
                {checked && <Ionicons name="checkmark" size={13} color="#fff" />}
              </View>
              <Text style={{ fontSize: 14, color: colors.text.primary }}>{ev}</Text>
            </TouchableOpacity>
          );
        })}
        {webhookForm.channel === 'vendor' && (['vendor.created', 'vendor.updated', 'vendor.deleted'] as const).map(ev => {
          const checked = webhookForm.events.includes(ev);
          return (
            <TouchableOpacity
              key={ev}
              onPress={() =>
                setWebhookForm(f => ({
                  ...f,
                  events: checked
                    ? f.events.filter(e => e !== ev)
                    : [...f.events, ev],
                }))
              }
              style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}
            >
              <View style={{
                width: 20, height: 20, borderRadius: 4,
                borderWidth: 2, borderColor: checked ? colors.primary : colors.border,
                backgroundColor: checked ? colors.primary : 'transparent',
                alignItems: 'center', justifyContent: 'center',
              }}>
                {checked && <Ionicons name="checkmark" size={13} color="#fff" />}
              </View>
              <Text style={{ fontSize: 14, color: colors.text.primary }}>{ev}</Text>
            </TouchableOpacity>
          );
        })}
        {webhookForm.channel === 'quotation' && (['quotation.created', 'quotation.updated', 'quotation.sent', 'quotation.accepted', 'quotation.rejected', 'quotation.converted', 'quotation.deleted'] as const).map(ev => {
          const checked = webhookForm.events.includes(ev);
          return (
            <TouchableOpacity
              key={ev}
              onPress={() =>
                setWebhookForm(f => ({
                  ...f,
                  events: checked
                    ? f.events.filter(e => e !== ev)
                    : [...f.events, ev],
                }))
              }
              style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}
            >
              <View style={{
                width: 20, height: 20, borderRadius: 4,
                borderWidth: 2, borderColor: checked ? colors.primary : colors.border,
                backgroundColor: checked ? colors.primary : 'transparent',
                alignItems: 'center', justifyContent: 'center',
              }}>
                {checked && <Ionicons name="checkmark" size={13} color="#fff" />}
              </View>
              <Text style={{ fontSize: 14, color: colors.text.primary }}>{ev}</Text>
            </TouchableOpacity>
          );
        })}

        <Text style={styles.fieldHint}>Only selected events will be delivered. If none are selected, this webhook will not receive events.</Text>

        <View style={styles.inlineRow}>
          <TouchableOpacity
            style={[styles.primaryBtn, { flex: 1 }, savingWebhook && styles.primaryBtnDisabled]}
            onPress={handleSaveWebhook}
            disabled={savingWebhook}
          >
            {savingWebhook ? (
              <ActivityIndicator size="small" color={colors.white} />
            ) : (
              <Text style={styles.primaryBtnText}>{editingWebhook ? 'Update Webhook' : 'Register Webhook'}</Text>
            )}
          </TouchableOpacity>
          {editingWebhook && (
            <TouchableOpacity
              style={[styles.outlineBtn, { marginLeft: spacing.sm }]}
              onPress={() => {
                setEditingWebhook(null);
                setWebhookForm({
                  title: '',
                  channel: 'whatsapp',
                  webhookUrl: '',
                  events: ['message.received', 'message.status'],
                });
              }}
            >
              <Text style={styles.outlineBtnText}>Cancel</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Webhooks list */}
      <View style={styles.card}>
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>Registered Webhooks</Text>
          {webhooksLoading && <ActivityIndicator size="small" color={colors.primary} />}
        </View>
        {webhooks.length === 0 && !webhooksLoading ? (
          <View style={styles.emptyState}>
            <Ionicons name="git-network-outline" size={32} color={colors.text.disabled} />
            <Text style={styles.emptyText}>No webhooks registered</Text>
          </View>
        ) : (
          webhooks.map(wh => {
            const isConfirming = confirmingDelete?.type === 'webhook' && confirmingDelete.id === wh._id;
            const isDeleting = deletingId === wh._id;
            return (
              <View key={wh._id} style={styles.listItem}>
                <View style={styles.listItemIcon}>
                  <Ionicons name="git-network-outline" size={18} color={colors.primary} />
                </View>
                <View style={styles.listItemContent}>
                  <Text style={styles.listItemTitle}>{wh.title || 'Untitled Webhook'}</Text>
                  <Text style={styles.listItemMeta}>{formatWebhookChannel(wh.channel)}</Text>
                  <Text style={styles.listItemMeta} numberOfLines={1}>{wh.webhookUrl}</Text>
                  <Text style={styles.listItemMeta}>
                    Secret: {wh.secretPrefix}•••  ·  Events: {wh.events.join(', ')}
                  </Text>
                  {isConfirming && (
                    <View style={styles.inlineConfirm}>
                      <Text style={styles.inlineConfirmText}>Delete this webhook?</Text>
                      <TouchableOpacity style={styles.inlineConfirmCancel} onPress={() => setConfirmingDelete(null)}>
                        <Text style={styles.inlineConfirmCancelText}>Cancel</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.inlineConfirmDanger} onPress={() => confirmDeleteWebhook(wh._id)}>
                        <Text style={styles.inlineConfirmDangerText}>Delete</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
                {!isConfirming && (
                  <>
                    <TouchableOpacity onPress={() => handleRegenerateWebhookSecret(wh)} style={styles.iconBtn}>
                      <Ionicons name="key-outline" size={17} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => handleEditWebhook(wh)} style={styles.iconBtn}>
                      <Ionicons name="pencil-outline" size={17} color={colors.primary} />
                    </TouchableOpacity>
                    {isDeleting ? (
                      <ActivityIndicator size="small" color={colors.error} style={{ marginLeft: spacing.xs }} />
                    ) : (
                      <TouchableOpacity onPress={() => handleDeleteWebhook(wh)} style={styles.dangerIconBtn}>
                        <Ionicons name="trash-outline" size={17} color={colors.error} />
                      </TouchableOpacity>
                    )}
                  </>
                )}
              </View>
            );
          })
        )}
      </View>
    </View>
  );

  const formatAssignmentType = (type: string) => {
    if (type === 'data_list_record') return 'Data List Record';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const renderAssignmentWebhooksSection = () => {
    const userCount = assignmentWebhookForm.userIds.length;
    const deptCount = assignmentWebhookForm.departmentIds.length;

    return (
      <View>
        {/* One-time revealed secret banner */}
        {revealedAssignmentSecret && (
          <View style={styles.secretBanner}>
            <View style={styles.secretBannerHeader}>
              <Ionicons name="lock-closed" size={16} color={colors.warning} />
              <Text style={styles.secretBannerTitle}>Save your webhook secret — shown once only</Text>
            </View>
            <Text style={styles.secretBannerSub}>
              Use this to verify incoming webhook signatures. Check the{' '}
              <Text style={{ fontFamily: typography.fontFamily.medium }}>X-Sendifier-Signature</Text> header:
              {'\n'}sha256=HMAC-SHA256(secret, rawBody)
            </Text>
            <View style={styles.secretBox}>
              <Text style={styles.secretText} selectable numberOfLines={1}>{revealedAssignmentSecret.rawSecret}</Text>
            </View>
            <View style={styles.secretActions}>
              <TouchableOpacity
                style={[styles.primaryBtn, { flex: 1 }]}
                onPress={() => copyToClipboard(revealedAssignmentSecret.rawSecret, 'revealedAssignmentSecret')}
              >
                <Ionicons
                  name={copyConfirm === 'revealedAssignmentSecret' ? 'checkmark' : 'copy-outline'}
                  size={16}
                  color={colors.white}
                />
                <Text style={styles.primaryBtnText}>
                  {copyConfirm === 'revealedAssignmentSecret' ? 'Copied!' : 'Copy Webhook Secret'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.outlineBtn} onPress={() => setRevealedAssignmentSecret(null)}>
                <Text style={styles.outlineBtnText}>Done</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Assignment webhook form */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{editingAssignmentWebhook ? 'Update Assignment Webhook' : 'Register Assignment Webhook'}</Text>
          <Text style={styles.cardDesc}>
            DISLIX will POST a signed JSON payload to your URL whenever an assignment is created — channel-independent,
            not tied to WhatsApp/phone/etc. Event name sent is assignment.&lt;type&gt; (e.g. assignment.contact, assignment.task).
          </Text>

          <Text style={styles.fieldLabel}>Webhook Title</Text>
          <TextInput
            style={[styles.input, webhookUrlError ? styles.inputError : null]}
            placeholder="My Assignment Notifications Hook"
            placeholderTextColor={colors.text.disabled}
            value={assignmentWebhookForm.title}
            onChangeText={v => { setAssignmentWebhookForm(f => ({ ...f, title: v })); setWebhookUrlError(null); }}
          />

          <Text style={styles.fieldLabel}>Webhook URL (HTTPS only)</Text>
          <TextInput
            style={[styles.input, webhookUrlError ? styles.inputError : null]}
            placeholder="https://yourserver.com/webhook"
            placeholderTextColor={colors.text.disabled}
            value={assignmentWebhookForm.webhookUrl}
            onChangeText={v => { setAssignmentWebhookForm(f => ({ ...f, webhookUrl: v })); setWebhookUrlError(null); }}
            autoCapitalize="none"
            keyboardType="url"
          />
          {webhookUrlError && <Text style={styles.fieldError}>{webhookUrlError}</Text>}

          <Text style={styles.fieldLabel}>Assignment Types</Text>
          <Text style={styles.fieldHint}>
            Leave all unchecked to fire for every assignment type.
          </Text>
          <View style={[styles.segmentRow, { flexWrap: 'wrap' }]}>
            {ASSIGNMENT_TYPES.map(t => {
              const checked = assignmentWebhookForm.assignmentTypes.includes(t);
              return (
                <TouchableOpacity
                  key={t}
                  onPress={() =>
                    setAssignmentWebhookForm(f => ({
                      ...f,
                      assignmentTypes: checked
                        ? f.assignmentTypes.filter(x => x !== t)
                        : [...f.assignmentTypes, t],
                    }))
                  }
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8, marginRight: 16 }}
                >
                  <View style={{
                    width: 20, height: 20, borderRadius: 4,
                    borderWidth: 2, borderColor: checked ? colors.primary : colors.border,
                    backgroundColor: checked ? colors.primary : 'transparent',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    {checked && <Ionicons name="checkmark" size={13} color="#fff" />}
                  </View>
                  <Text style={{ fontSize: 14, color: colors.text.primary }}>{formatAssignmentType(t)}</Text>
                </TouchableOpacity>
              );
            })}
          </View>

          <Text style={[styles.fieldLabel, { marginTop: 8 }]}>Trigger</Text>
          <TouchableOpacity
            onPress={() => setAssignmentWebhookForm(f => ({ ...f, allAssignments: !f.allAssignments }))}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 4, marginBottom: 8 }}
          >
            <View style={{
              width: 20, height: 20, borderRadius: 4,
              borderWidth: 2, borderColor: assignmentWebhookForm.allAssignments ? colors.primary : colors.border,
              backgroundColor: assignmentWebhookForm.allAssignments ? colors.primary : 'transparent',
              alignItems: 'center', justifyContent: 'center',
            }}>
              {assignmentWebhookForm.allAssignments && <Ionicons name="checkmark" size={13} color="#fff" />}
            </View>
            <Text style={{ fontSize: 14, color: colors.text.primary, fontWeight: '600' }}>All assignments</Text>
          </TouchableOpacity>
          <Text style={styles.fieldHint}>
            Fires for every matching-type assignment regardless of who it's assigned to. Leave off and leave the
            pickers below empty to also fire for every assignment — the pickers only narrow things down once you
            select someone.
          </Text>

          {!assignmentWebhookForm.allAssignments && (
            <>
              <Text style={[styles.fieldLabel, { marginTop: 10 }]}>Only fire when assigned to</Text>
              <TouchableOpacity style={styles.outlineBtn} onPress={() => setShowAssignedUserPicker(true)}>
                <Text style={styles.outlineBtnText}>
                  {userCount > 0 ? `${userCount} user${userCount === 1 ? '' : 's'} selected` : 'Choose users... (none = everyone)'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity style={[styles.outlineBtn, { marginTop: 8 }]} onPress={() => setShowAssignedDeptPicker(true)}>
                <Text style={styles.outlineBtnText}>
                  {deptCount > 0 ? `${deptCount} department${deptCount === 1 ? '' : 's'} selected` : 'Choose departments... (none = everyone)'}
                </Text>
              </TouchableOpacity>
              <Text style={styles.fieldHint}>
                Fires only if the assignment's assignedTo/department is one of the selected users or departments.
                Leave both empty to fire for every assignment (no user/department filter applied).
              </Text>
            </>
          )}

          <View style={styles.inlineRow}>
            <TouchableOpacity
              style={[styles.primaryBtn, { flex: 1 }, savingAssignmentWebhook && styles.primaryBtnDisabled]}
              onPress={handleSaveAssignmentWebhook}
              disabled={savingAssignmentWebhook}
            >
              {savingAssignmentWebhook ? (
                <ActivityIndicator size="small" color={colors.white} />
              ) : (
                <Text style={styles.primaryBtnText}>{editingAssignmentWebhook ? 'Update Webhook' : 'Register Webhook'}</Text>
              )}
            </TouchableOpacity>
            {editingAssignmentWebhook && (
              <TouchableOpacity
                style={[styles.outlineBtn, { marginLeft: spacing.sm }]}
                onPress={() => {
                  setEditingAssignmentWebhook(null);
                  setAssignmentWebhookForm({
                    title: '',
                    webhookUrl: '',
                    assignmentTypes: [],
                    allAssignments: false,
                    userIds: [],
                    departmentIds: [],
                  });
                }}
              >
                <Text style={styles.outlineBtnText}>Cancel</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Assignment webhooks list */}
        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardTitle}>Registered Assignment Webhooks</Text>
            {assignmentWebhooksLoading && <ActivityIndicator size="small" color={colors.primary} />}
          </View>
          {assignmentWebhooks.length === 0 && !assignmentWebhooksLoading ? (
            <View style={styles.emptyState}>
              <Ionicons name="people-outline" size={32} color={colors.text.disabled} />
              <Text style={styles.emptyText}>No assignment webhooks registered</Text>
            </View>
          ) : (
            assignmentWebhooks.map(wh => {
              const isConfirming = confirmingDelete?.type === 'assignmentWebhook' && confirmingDelete.id === wh._id;
              const isDeleting = deletingId === wh._id;
              const typesLabel = wh.assignmentTypes?.length
                ? wh.assignmentTypes.map(formatAssignmentType).join(', ')
                : 'All types';
              const targetLabel = wh.allAssignments
                ? 'All assignments'
                : (wh.userIds?.length || wh.departmentIds?.length)
                  ? `${wh.userIds?.length || 0} user(s), ${wh.departmentIds?.length || 0} department(s)`
                  : 'All assignments (no filter set)';
              return (
                <View key={wh._id} style={styles.listItem}>
                  <View style={styles.listItemIcon}>
                    <Ionicons name="people-outline" size={18} color={colors.primary} />
                  </View>
                  <View style={styles.listItemContent}>
                    <Text style={styles.listItemTitle}>{wh.title || 'Untitled Webhook'}</Text>
                    <Text style={styles.listItemMeta}>Types: {typesLabel}</Text>
                    <Text style={styles.listItemMeta}>Trigger: {targetLabel}</Text>
                    <Text style={styles.listItemMeta} numberOfLines={1}>{wh.webhookUrl}</Text>
                    <Text style={styles.listItemMeta}>Secret: {wh.secretPrefix}•••</Text>
                    {isConfirming && (
                      <View style={styles.inlineConfirm}>
                        <Text style={styles.inlineConfirmText}>Delete this webhook?</Text>
                        <TouchableOpacity style={styles.inlineConfirmCancel} onPress={() => setConfirmingDelete(null)}>
                          <Text style={styles.inlineConfirmCancelText}>Cancel</Text>
                        </TouchableOpacity>
                        <TouchableOpacity style={styles.inlineConfirmDanger} onPress={() => confirmDeleteAssignmentWebhook(wh._id)}>
                          <Text style={styles.inlineConfirmDangerText}>Delete</Text>
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                  {!isConfirming && (
                    <>
                      <TouchableOpacity onPress={() => handleEditAssignmentWebhook(wh)} style={styles.iconBtn}>
                        <Ionicons name="pencil-outline" size={17} color={colors.primary} />
                      </TouchableOpacity>
                      {isDeleting ? (
                        <ActivityIndicator size="small" color={colors.error} style={{ marginLeft: spacing.xs }} />
                      ) : (
                        <TouchableOpacity onPress={() => handleDeleteAssignmentWebhook(wh)} style={styles.dangerIconBtn}>
                          <Ionicons name="trash-outline" size={17} color={colors.error} />
                        </TouchableOpacity>
                      )}
                    </>
                  )}
                </View>
              );
            })
          )}
        </View>
      </View>
    );
  };

  // ─── Live playground ─────────────────────────────────────────────────

  const CODE_BASE_URL = (apiService.getBaseURL() || 'https://api.connect.dislix.com').replace(/\/$/, '');

  // If the user just created a key, pre-fill with revealed raw key
  const playRawKey = revealedKey && revealedKey.keyId === playSelectedKeyId
    ? revealedKey.rawKey
    : null;

  // Fetch templates when switching to template mode
  const handleSetWindowMode = async (mode: 'free' | 'template') => {
    setPlayWindowMode(mode);
    if (mode === 'free') {
      setPlayMsgType('text');
    } else {
      setPlayReplyMode('none');
      setPlayMsgType('template');
      if (playTemplates.length === 0) {
        setPlayTemplatesLoading(true);
        try {
          const res = await apiService.getWhatsAppCustomTemplates();
          const templates = res.data?.templates ?? res.data;
          if (res.statusCode === 1000 && Array.isArray(templates)) {
            setPlayTemplates(templates);
          }
        } catch { /* ignore */ } finally {
          setPlayTemplatesLoading(false);
        }
      }
    }
    setPlayResponse(null);
  };

  const loadPlayReplySets = async () => {
    setPlayReplySetsLoading(true);
    try {
      const res = await apiService.getQuickReplySets({ isActive: true, limit: 100, sortBy: 'updatedAt', sortOrder: 'desc' });
      const sets = res.data?.quickReplySets || [];
      setPlayReplySets(sets);
      if (!playSelectedReplySetId && sets.length > 0) {
        setPlaySelectedReplySetId(sets[0]._id);
      }
    } catch {
      setPlayReplySets([]);
    } finally {
      setPlayReplySetsLoading(false);
    }
  };

  useEffect(() => {
    if (playReplyMode !== 'set' || !playSelectedReplySetId) return;
    const loadSetItems = async () => {
      try {
        const res = await apiService.getQuickReplySetById(playSelectedReplySetId);
        if (res.statusCode === 1000 && res.data) {
          const sortedItems = (res.data.items || []).sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
          setPlayReplySetItems(sortedItems);
          setPlayReplyListButtonText(res.data.listButtonText || 'Choose an option');
        } else {
          setPlayReplySetItems([]);
        }
      } catch {
        setPlayReplySetItems([]);
      }
    };
    loadSetItems();
  }, [playReplyMode, playSelectedReplySetId]);

  const handleSetReplyMode = async (mode: 'none' | 'items' | 'set') => {
    setPlayReplyMode(mode);
    if (mode !== 'none') {
      setPlayMsgType('text');
    }
    if (mode === 'set' && playReplySets.length === 0 && !playReplySetsLoading) {
      await loadPlayReplySets();
    }
    setPlayResponse(null);
  };

  const getPlayReplyOptions = (): string[] => {
    if (playReplyMode === 'none') return [];

    const rawItems = playReplyMode === 'items'
      ? playReplyItemsInput.split('\n').map(x => x.trim()).filter(Boolean)
      : (playReplySetItems || []).map((item: any) => item?.displayText || item?.title || '').map((x: string) => x.trim()).filter(Boolean);

    const deduped = Array.from(new Set(rawItems));
    return deduped.slice(0, 10);
  };

  const buildInteractivePayload = (items: string[]) => {
    const bodyText = (playText || 'Please choose an option').trim().slice(0, 1024);

    if (items.length <= 3) {
      return {
        type: 'button',
        body: { text: bodyText },
        action: {
          buttons: items.map((item, index) => ({
            type: 'reply',
            reply: {
              id: `play_reply_${index + 1}_${item.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40)}`,
              title: item.slice(0, 20),
            },
          })),
        },
      };
    }

    return {
      type: 'list',
      body: { text: bodyText },
      action: {
        button: (playReplyListButtonText || 'Choose an option').slice(0, 20),
        sections: [
          {
            title: 'Options',
            rows: items.map((item, index) => ({
              id: `play_list_${index + 1}_${item.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40)}`,
              title: item.slice(0, 24),
              description: item.length > 24 ? item.slice(0, 72) : undefined,
            })),
          },
        ],
      },
    };
  };

  // Build the request body based on current playground state
  const buildPlayBody = (): object => {
    const phone = playPhone.trim();
    if (playWindowMode === 'template' && playSelectedTemplate) {
      const t = playSelectedTemplate;
      const headerAsset = t.headerAsset;
      let resolvedParams: Array<{ position: number; section: string }> = t.parameters || [];
      if (resolvedParams.length === 0 && t.hasParameters) {
        // Fallback: scan {{N}} from template text
        const scanText = (text: string | undefined, section: string) => {
          if (!text) return [];
          const out: any[] = []; let m: RegExpExecArray | null;
          const re = /\{\{(\d+)\}\}/g;
          while ((m = re.exec(text)) !== null) out.push({ position: parseInt(m[1], 10), section });
          return out;
        };
        const seen = new Set<number>();
        resolvedParams = [
          ...scanText(t.templateHeader, 'header'),
          ...scanText(t.templateBody, 'body'),
          ...scanText(t.templateFooter, 'footer'),
        ].filter(p => { if (seen.has(p.position)) return false; seen.add(p.position); return true; });
      }
      const headerParamDefs = resolvedParams.filter((p: any) => p.section === 'header').sort((a: any, b: any) => a.position - b.position);
      const bodyParamDefs = resolvedParams.filter((p: any) => p.section === 'body').sort((a: any, b: any) => a.position - b.position);
      const shouldSendHeaderParam = headerParamDefs.length > 0 || !!t.templateHeader;

      const components: any[] = [];

      // Header component — mirrors buildTemplateComponents in send-bulk-messages.handler.ts
      if (shouldSendHeaderParam || headerAsset) {
        if (playTemplateHeaderMedia && headerAsset?.type) {
          // User uploaded a new media file
          const mt = headerAsset.type;
          components.push({ type: 'header', parameters: [{ type: mt, [mt]: { link: playTemplateHeaderMedia.url } }] });
        } else if (headerAsset?.type === 'image') {
          const imageParam: any = headerAsset.mediaId ? { id: headerAsset.mediaId } : headerAsset.url ? { link: headerAsset.url } : null;
          if (imageParam) components.push({ type: 'header', parameters: [{ type: 'image', image: imageParam }] });
          else components.push({ type: 'header', parameters: [{ type: 'text', text: headerParamDefs[0] ? (playTemplateParams[String(headerParamDefs[0].position)] || '') : '' }] });
        } else if (headerAsset?.type === 'video' && headerAsset.url) {
          components.push({ type: 'header', parameters: [{ type: 'video', video: { link: headerAsset.url } }] });
        } else if (headerAsset?.type === 'document' && headerAsset.url) {
          components.push({ type: 'header', parameters: [{ type: 'document', document: { link: headerAsset.url } }] });
        } else {
          // Text header — dynamic params or static text fallback (mirrors buildTemplateComponents)
          if (headerParamDefs.length > 0) {
            const headerParams = headerParamDefs.map((p: any) => ({ type: 'text', text: playTemplateParams[String(p.position)] || '' }));
            components.push({ type: 'header', parameters: headerParams });
          } else if (t.templateHeader) {
            // Static header text — send as-is (Meta still expects the param count to match)
            const headerBase = t.templateHeader.replace(/\{\{\d+\}\}/g, '').trim();
            components.push({ type: 'header', parameters: [{ type: 'text', text: headerBase || t.templateHeader }] });
          }
        }
      }

      // Body component
      if (bodyParamDefs.length > 0) {
        const bodyParams = bodyParamDefs.map((p: any) => ({ type: 'text', text: playTemplateParams[String(p.position)] || '' }));
        components.push({ type: 'body', parameters: bodyParams });
      }
      return {
        to: phone || '+971500000000',
        type: 'template',
        template: {
          name: playSelectedTemplate.templateName,
          language: playSelectedTemplate.language || 'en',
          components,
        },
      };
    }
    const replyOptions = getPlayReplyOptions();
    if (playWindowMode === 'free' && replyOptions.length > 0 && playReplyMode !== 'none') {
      return {
        to: phone || '+971500000000',
        type: 'interactive',
        interactive: buildInteractivePayload(replyOptions),
      };
    }

    const base = { to: phone || '+971500000000', type: playMsgType };
    switch (playMsgType) {
      case 'text':
        return { ...base, text: { body: playText || 'Hello!' } };
      case 'image':
        return { ...base, image: { link: playMediaUrl || 'https://example.com/image.jpg', mimeType: playMediaMimeType || 'image/jpeg', caption: playMediaCaption || undefined } };
      case 'video':
        return { ...base, video: { link: playMediaUrl || 'https://example.com/video.mp4', mimeType: playMediaMimeType || 'video/mp4', caption: playMediaCaption || undefined } };
      case 'audio':
        return { ...base, audio: { link: playMediaUrl || 'https://example.com/audio.ogg', mimeType: playMediaMimeType || 'audio/mpeg' } };
      case 'document':
        return { ...base, document: { link: playMediaUrl || 'https://example.com/file.pdf', mimeType: playMediaMimeType || 'application/pdf', filename: playMediaFilename || 'document.pdf', caption: playMediaCaption || undefined } };
      case 'location':
        return { ...base, location: { latitude: parseFloat(playLat) || 25.2048, longitude: parseFloat(playLng) || 55.2708, address: playLocationAddress || undefined } };
      default:
        return base;
    }
  };

  const handlePlaySend = async () => {
    const rawKey = playRawKey || playRawKeyInput.trim();
    if (!rawKey) {
      setPlayResponse({ ok: false, status: 0, body: 'Paste your API key. Raw keys are only shown once at generation time.' });
      return;
    }
    if (!playPhone.trim()) {
      setPlayResponse({ ok: false, status: 0, body: 'Enter a recipient phone number in E.164 format (e.g. +971500000000).' });
      return;
    }
    setPlaySending(true);
    setPlayResponse(null);
    try {
      const res = await fetch(`${CODE_BASE_URL}/api/v1/whatsapp/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${rawKey}` },
        body: JSON.stringify(buildPlayBody()),
      });
      const text = await res.text();
      let body = text;
      try { body = JSON.stringify(JSON.parse(text), null, 2); } catch { /* keep raw */ }
      setPlayResponse({ ok: res.ok, status: res.status, body });
    } catch (err: any) {
      setPlayResponse({ ok: false, status: 0, body: err?.message || 'Network error' });
    } finally {
      setPlaySending(false);
    }
  };

  // ─── Docs tab ────────────────────────────────────────────────────────

  // Helper: render a response panel (shared by all endpoints)
  const renderResponsePanel = (epId: string) => {
    const resp = epResponse[epId];
    if (!resp) return null;
    return (
      <View style={[styles.playResponseBox, resp.ok ? styles.playResponseOk : styles.playResponseErr]}>
        <View style={styles.playResponseHeader}>
          <Ionicons name={resp.ok ? 'checkmark-circle' : 'close-circle'} size={16} color={resp.ok ? colors.success : colors.error} />
          <Text style={[styles.playResponseStatus, resp.ok ? styles.playResponseStatusOk : styles.playResponseStatusErr]}>
            {resp.status > 0 ? `HTTP ${resp.status}` : 'Error'}{resp.ok ? ' — Success' : ' — Failed'}
          </Text>
          <TouchableOpacity style={{ marginLeft: 'auto' }} onPress={() => copyToClipboard(resp.body, `resp_${epId}`)}>
            <Ionicons name={copyConfirm === `resp_${epId}` ? 'checkmark' : 'copy-outline'} size={14} color={colors.text.secondary} />
          </TouchableOpacity>
        </View>
        <ScrollView style={styles.playResponseScroll} nestedScrollEnabled>
          <Text style={styles.playResponseBody} selectable>{resp.body}</Text>
        </ScrollView>
      </View>
    );
  };

  // Helper: fire a GET request for an endpoint
  const handleEpSend = async (epId: string, urlFn: (inputs: Record<string, string>, rawKey: string) => string) => {
    const rawKey = playRawKey || playRawKeyInput.trim();
    if (!rawKey) {
      setEpResponse(prev => ({ ...prev, [epId]: { ok: false, status: 0, body: 'Paste your API key first.' } }));
      return;
    }
    const inputs = epInputs[epId] || {};
    setEpSending(prev => ({ ...prev, [epId]: true }));
    setEpResponse(prev => { const n = { ...prev }; delete n[epId]; return n; });
    try {
      const url = urlFn(inputs, rawKey);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${rawKey}` } });
      const text = await res.text();
      let body = text;
      try { body = JSON.stringify(JSON.parse(text), null, 2); } catch { /* keep raw */ }
      setEpResponse(prev => ({ ...prev, [epId]: { ok: res.ok, status: res.status, body } }));
    } catch (err: any) {
      setEpResponse(prev => ({ ...prev, [epId]: { ok: false, status: 0, body: err?.message || 'Network error' } }));
    } finally {
      setEpSending(prev => ({ ...prev, [epId]: false }));
    }
  };

  const handleEpPost = async (epId: string, url: string, body: object) => {
    const rawKey = playRawKey || playRawKeyInput.trim();
    if (!rawKey) {
      setEpResponse(prev => ({ ...prev, [epId]: { ok: false, status: 0, body: 'Paste your API key first.' } }));
      return;
    }
    setEpSending(prev => ({ ...prev, [epId]: true }));
    setEpResponse(prev => { const n = { ...prev }; delete n[epId]; return n; });
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${rawKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await res.text();
      let parsed = text;
      try { parsed = JSON.stringify(JSON.parse(text), null, 2); } catch { /* keep raw */ }
      setEpResponse(prev => ({ ...prev, [epId]: { ok: res.ok, status: res.status, body: parsed } }));
    } catch (err: any) {
      setEpResponse(prev => ({ ...prev, [epId]: { ok: false, status: 0, body: err?.message || 'Network error' } }));
    } finally {
      setEpSending(prev => ({ ...prev, [epId]: false }));
    }
  };

  const handleEpPatch = async (epId: string, url: string, body: object) => {
    const rawKey = playRawKey || playRawKeyInput.trim();
    if (!rawKey) {
      setEpResponse(prev => ({ ...prev, [epId]: { ok: false, status: 0, body: 'Paste your API key first.' } }));
      return;
    }
    setEpSending(prev => ({ ...prev, [epId]: true }));
    setEpResponse(prev => { const n = { ...prev }; delete n[epId]; return n; });
    try {
      const res = await fetch(url, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${rawKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await res.text();
      let parsed = text;
      try { parsed = JSON.stringify(JSON.parse(text), null, 2); } catch { /* keep raw */ }
      setEpResponse(prev => ({ ...prev, [epId]: { ok: res.ok, status: res.status, body: parsed } }));
    } catch (err: any) {
      setEpResponse(prev => ({ ...prev, [epId]: { ok: false, status: 0, body: err?.message || 'Network error' } }));
    } finally {
      setEpSending(prev => ({ ...prev, [epId]: false }));
    }
  };

  const handleEpDelete = async (epId: string, url: string) => {
    const rawKey = playRawKey || playRawKeyInput.trim();
    if (!rawKey) {
      setEpResponse(prev => ({ ...prev, [epId]: { ok: false, status: 0, body: 'Paste your API key first.' } }));
      return;
    }
    setEpSending(prev => ({ ...prev, [epId]: true }));
    setEpResponse(prev => { const n = { ...prev }; delete n[epId]; return n; });
    try {
      const res = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${rawKey}` },
      });
      const text = await res.text();
      let parsed = text;
      try { parsed = JSON.stringify(JSON.parse(text), null, 2); } catch { /* keep raw */ }
      setEpResponse(prev => ({ ...prev, [epId]: { ok: res.ok, status: res.status, body: parsed } }));
    } catch (err: any) {
      setEpResponse(prev => ({ ...prev, [epId]: { ok: false, status: 0, body: err?.message || 'Network error' } }));
    } finally {
      setEpSending(prev => ({ ...prev, [epId]: false }));
    }
  };

  const setEpInput = (epId: string, key: string, value: string) => {
    setEpInputs(prev => ({ ...prev, [epId]: { ...(prev[epId] || {}), [key]: value } }));
    setEpResponse(prev => { const n = { ...prev }; delete n[epId]; return n; });
  };

  const renderDocsTab = () => {
    const rawKey = playRawKey || playRawKeyInput.trim();
    const inp = (epId: string, key: string) => (epInputs[epId] || {})[key] || '';

    const EP_META: Record<string, { method: string; path: string; desc: string; group: string }> = {
      list_contacts:       { method: 'GET',    path: '/api/v1/whatsapp/contacts',                         desc: 'List contacts',                    group: 'whatsapp' },
      get_contact:         { method: 'GET',    path: '/api/v1/whatsapp/contacts/:id',                     desc: 'Get a contact',                    group: 'whatsapp' },
      list_conversations:  { method: 'GET',    path: '/api/v1/whatsapp/contacts/:id/conversations',       desc: 'List conversations',               group: 'whatsapp' },
      list_messages:       { method: 'GET',    path: '/api/v1/whatsapp/conversations/:id/messages',       desc: 'List messages',                    group: 'whatsapp' },
      send_message:        { method: 'POST',   path: '/api/v1/whatsapp/messages',                         desc: 'Send a message',                   group: 'whatsapp' },
      x_list_contacts:     { method: 'GET',    path: '/api/v1/x-twitter/contacts',                        desc: 'List X contacts',                  group: 'x-twitter' },
      x_get_contact:       { method: 'GET',    path: '/api/v1/x-twitter/contacts/:id',                    desc: 'Get X contact',                    group: 'x-twitter' },
      x_list_conversations:{ method: 'GET',    path: '/api/v1/x-twitter/contacts/:id/conversations',      desc: 'List X conversations',             group: 'x-twitter' },
      x_list_messages:     { method: 'GET',    path: '/api/v1/x-twitter/conversations/:id/messages',      desc: 'List X messages',                  group: 'x-twitter' },
      x_send_message:      { method: 'POST',   path: '/api/v1/x-twitter/messages',                        desc: 'Send X message',                   group: 'x-twitter' },
      create_user:         { method: 'POST',   path: '/api/v1/users',                                     desc: 'Create a user',                    group: 'users' },
      list_users:          { method: 'GET',    path: '/api/v1/users',                                     desc: 'List business members',            group: 'users' },
      delete_user:         { method: 'DELETE', path: '/api/v1/users/:userId',                             desc: 'Remove user from business',        group: 'users' },
      create_department:   { method: 'POST',   path: '/api/v1/departments',                               desc: 'Create a department',              group: 'departments' },
      list_departments:    { method: 'GET',    path: '/api/v1/departments',                               desc: 'List departments',                 group: 'departments' },
      delete_department:   { method: 'DELETE', path: '/api/v1/departments/:departmentId',                 desc: 'Delete a department',              group: 'departments' },
      assign_user_to_dept: { method: 'POST',   path: '/api/v1/assignments',                               desc: 'Assign user to a department',      group: 'departments' },
      list_dept_members:   { method: 'GET',    path: '/api/v1/assignments',                               desc: 'List user-department assignments', group: 'departments' },
      remove_dept_member:  { method: 'DELETE', path: '/api/v1/assignments/:id',                           desc: 'Remove user from department',      group: 'departments' },
      assign_contact_to_user:      { method: 'POST',   path: '/api/v1/assignments/contacts/:contactId/user', desc: 'Assign contact to a user',        group: 'users' },
      get_contact_assignments_u:  { method: 'GET',    path: '/api/v1/assignments/contacts/:contactId',     desc: 'Get contact assignments',          group: 'users' },
      update_assignment_status_u: { method: 'PATCH',  path: '/api/v1/assignments/:id',                     desc: 'Update assignment status (cancel/complete/start/transfer)', group: 'users' },
      free_contact_assignments_u: { method: 'DELETE', path: '/api/v1/assignments/contacts/:contactId',     desc: 'Free all assignments for a contact', group: 'users' },
      assign_contact_to_dept:     { method: 'POST',   path: '/api/v1/assignments/contacts/:contactId/department', desc: 'Assign contact to a department', group: 'departments' },
      get_contact_assignments_d:  { method: 'GET',    path: '/api/v1/assignments/contacts/:contactId',     desc: 'Get contact assignments',          group: 'departments' },
      update_assignment_status_d: { method: 'PATCH',  path: '/api/v1/assignments/:id',                     desc: 'Update assignment status (cancel/complete/start/transfer)', group: 'departments' },
      free_contact_assignments_d: { method: 'DELETE', path: '/api/v1/assignments/contacts/:contactId',     desc: 'Free all assignments for a contact', group: 'departments' },
      phone_create_call:   { method: 'POST',   path: '/api/v1/phone/calls',                               desc: 'Report a call placed/received outside this app', group: 'phone' },
      phone_list_calls:    { method: 'GET',    path: '/api/v1/phone/calls',                               desc: 'List calls',                       group: 'phone' },
      phone_calls_by_contact: { method: 'GET', path: '/api/v1/phone/contacts/:contactId/calls',           desc: 'List calls for a contact',         group: 'phone' },
      phone_calls_by_user: { method: 'GET',    path: '/api/v1/phone/users/:userId/calls',                 desc: 'List calls for user(s)',           group: 'phone' },
      phone_calls_by_dept: { method: 'GET',    path: '/api/v1/phone/departments/:departmentId/calls',     desc: 'List calls for department(s)',     group: 'phone' },
      phone_get_call:      { method: 'GET',    path: '/api/v1/phone/calls/:id',                           desc: 'Get a call',                       group: 'phone' },
      phone_update_call:   { method: 'PATCH',  path: '/api/v1/phone/calls/:id',                           desc: 'Update call status',               group: 'phone' },
      phone_add_note:      { method: 'POST',   path: '/api/v1/phone/calls/:id/notes',                     desc: 'Add a note to a call',             group: 'phone' },
      phone_list_notes:    { method: 'GET',    path: '/api/v1/phone/calls/:id/notes',                     desc: 'List notes on a call',             group: 'phone' },
      catalog_list:        { method: 'GET',    path: '/api/v1/catalogs',                                  desc: 'List catalogs',                    group: 'catalog' },
      catalog_get:         { method: 'GET',    path: '/api/v1/catalogs/:id',                              desc: 'Get a catalog',                    group: 'catalog' },
      catalog_create:      { method: 'POST',   path: '/api/v1/catalogs',                                  desc: 'Create a catalog',                 group: 'catalog' },
      catalog_update:      { method: 'PATCH',  path: '/api/v1/catalogs/:id',                              desc: 'Update a catalog',                 group: 'catalog' },
      catalog_delete:      { method: 'DELETE', path: '/api/v1/catalogs/:id',                              desc: 'Delete a catalog',                 group: 'catalog' },
      catalog_add_product: { method: 'POST',   path: '/api/v1/catalogs/:id/products',                     desc: 'Add a product to a catalog',       group: 'catalog' },
      catalog_remove_product: { method: 'DELETE', path: '/api/v1/catalogs/:id/products/:productId',       desc: 'Remove a product from a catalog',  group: 'catalog' },
      product_list:        { method: 'GET',    path: '/api/v1/products',                                  desc: 'List products',                    group: 'product' },
      product_get:         { method: 'GET',    path: '/api/v1/products/:id',                              desc: 'Get a product',                    group: 'product' },
      product_create:      { method: 'POST',   path: '/api/v1/products',                                  desc: 'Create a product',                 group: 'product' },
      product_update:      { method: 'PATCH',  path: '/api/v1/products/:id',                              desc: 'Update a product',                 group: 'product' },
      product_delete:      { method: 'DELETE', path: '/api/v1/products/:id',                              desc: 'Delete a product',                 group: 'product' },
      order_list:          { method: 'GET',    path: '/api/v1/orders',                                    desc: 'List orders',                      group: 'order' },
      order_get_by_number: { method: 'GET',    path: '/api/v1/orders/by-number/:orderNumber',             desc: 'Get an order by order number',     group: 'order' },
      order_get:           { method: 'GET',    path: '/api/v1/orders/:id',                                desc: 'Get an order',                     group: 'order' },
      order_create:        { method: 'POST',   path: '/api/v1/orders',                                    desc: 'Create an order',                  group: 'order' },
      order_update:        { method: 'PATCH',  path: '/api/v1/orders/:id',                                desc: 'Update an order (status, tracking, cancellation)', group: 'order' },
      vendor_list:         { method: 'GET',    path: '/api/v1/vendors',                                   desc: 'List vendors',                     group: 'vendor' },
      vendor_get:          { method: 'GET',    path: '/api/v1/vendors/:id',                               desc: 'Get a vendor',                     group: 'vendor' },
      vendor_create:       { method: 'POST',   path: '/api/v1/vendors',                                   desc: 'Create a vendor',                  group: 'vendor' },
      vendor_update:       { method: 'PATCH',  path: '/api/v1/vendors/:id',                               desc: 'Update a vendor',                  group: 'vendor' },
      vendor_delete:       { method: 'DELETE', path: '/api/v1/vendors/:id',                               desc: 'Delete a vendor',                  group: 'vendor' },
      vendor_add_product:  { method: 'POST',   path: '/api/v1/vendors/:id/products',                      desc: 'Link a product to a vendor',       group: 'vendor' },
      vendor_remove_product: { method: 'DELETE', path: '/api/v1/vendors/:id/products/:productId',         desc: 'Unlink a product from a vendor',   group: 'vendor' },
      quotation_list:      { method: 'GET',    path: '/api/v1/quotations',                                desc: 'List quotations',                  group: 'quotation' },
      quotation_get:       { method: 'GET',    path: '/api/v1/quotations/:id',                            desc: 'Get a quotation',                  group: 'quotation' },
      quotation_create:    { method: 'POST',   path: '/api/v1/quotations',                                desc: 'Create a quotation',               group: 'quotation' },
      quotation_update:    { method: 'PATCH',  path: '/api/v1/quotations/:id',                            desc: 'Update a quotation',               group: 'quotation' },
      quotation_delete:    { method: 'DELETE', path: '/api/v1/quotations/:id',                            desc: 'Delete a quotation',               group: 'quotation' },
      quotation_pdf:       { method: 'GET',    path: '/api/v1/quotations/:id/pdf',                        desc: 'Generate/fetch quotation PDF',     group: 'quotation' },
      quotation_convert:   { method: 'POST',   path: '/api/v1/quotations/:id/convert',                    desc: 'Convert quotation to an order',    group: 'quotation' },
    };

    const q = epSearch.trim().toLowerCase();
    const epVisible = (epId: string) => {
      if (!q) return true;
      const m = EP_META[epId];
      return m.path.toLowerCase().includes(q) || m.desc.toLowerCase().includes(q) || m.method.toLowerCase().includes(q);
    };
    const groupStyle = (group: string): object =>
      !q || Object.entries(EP_META).some(([id, m]) => m.group === group && epVisible(id))
        ? {}
        : { display: 'none' };

    return (
      <View>

        {/* ── Endpoint search ── */}
        <View style={[styles.card, { flexDirection: 'row', alignItems: 'center', gap: spacing.xs }]}>
          <Ionicons name="search-outline" size={16} color={colors.text.secondary} />
          <TextInput
            style={[styles.input, { flex: 1, marginBottom: 0 }]}
            placeholder="Search endpoints…"
            placeholderTextColor={colors.text.disabled}
            value={epSearch}
            onChangeText={setEpSearch}
            autoCapitalize="none"
            autoCorrect={false}
            clearButtonMode="while-editing"
          />
          {epSearch.length > 0 && (
            <TouchableOpacity onPress={() => setEpSearch('')}>
              <Ionicons name="close-circle" size={16} color={colors.text.secondary} />
            </TouchableOpacity>
          )}
        </View>

        {/* ── Shared key selector (used by all endpoint playgrounds) ── */}
        <View style={styles.card}>
          <View style={styles.playHeader}>
            <View style={styles.playLiveDot} />
            <Text style={styles.playTitle}>Live API Playground</Text>
          </View>
          <Text style={styles.cardDesc}>
            Select an API key then expand any endpoint below to try it live.
          </Text>

          <Text style={styles.fieldLabel}>API Key</Text>
          {apiKeys.length === 0 ? (
            <View style={styles.playNoKeys}>
              <Ionicons name="key-outline" size={16} color={colors.text.disabled} />
              <Text style={styles.playNoKeysText}>No active keys — generate one in the API Keys tab first.</Text>
            </View>
          ) : (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.keyPickerScroll}>
              <View style={styles.keyPickerRow}>
                {apiKeys.map(k => {
                  const isSelected = playSelectedKeyId === k._id;
                  return (
                    <TouchableOpacity
                      key={k._id}
                      style={[styles.keyChip, isSelected && styles.keyChipActive]}
                      onPress={() => { setPlaySelectedKeyId(k._id); setPlayRawKeyInput(''); setPlayResponse(null); setEpResponse({}); }}
                    >
                      <Ionicons name="key-outline" size={13} color={isSelected ? colors.white : colors.primary} />
                      <Text style={[styles.keyChipText, isSelected && styles.keyChipTextActive]} numberOfLines={1}>{k.label}</Text>
                      <Text style={[styles.keyChipPrefix, isSelected && styles.keyChipPrefixActive]}>{k.keyPrefix}•••</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </ScrollView>
          )}
          {playSelectedKeyId !== '' && !playRawKey && (
            <View>
              <Text style={styles.fieldLabel}>Paste Raw Key</Text>
              <Text style={[styles.fieldHint, { marginTop: 0 }]}>Raw keys are only shown once at generation time.</Text>
              <TextInput
                style={styles.input}
                placeholder="sk_live_xxxxxxxx..."
                placeholderTextColor={colors.text.disabled}
                value={playRawKeyInput}
                onChangeText={v => { setPlayRawKeyInput(v); setPlayResponse(null); }}
                autoCapitalize="none"
                autoCorrect={false}
              />
            </View>
          )}
          {playRawKey && (
            <View style={styles.playKeyReady}>
              <Ionicons name="checkmark-circle" size={14} color={colors.success} />
              <Text style={styles.playKeyReadyText}>Using the key you just generated</Text>
            </View>
          )}

          <View style={styles.codeBlock}>
            <Text style={styles.codeText} selectable>{`Base URL: ${CODE_BASE_URL}\nAuth:     Authorization: Bearer ${rawKey || 'sk_live_<your_key>'}`}</Text>
          </View>
        </View>

        {/* ── WhatsApp Endpoints group ── */}
        <View style={[styles.card, groupStyle('whatsapp')]}>
          <View style={styles.epGroupHeader}>
            <Ionicons name="logo-whatsapp" size={16} color="#25D366" />
            <Text style={styles.epGroupTitle}>WhatsApp Endpoints</Text>
          </View>

          {/* GET /contacts */}
          {(() => {
            const epId = 'list_contacts';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.search) p.set('search', inputs.search);
              if (inputs.page) p.set('page', inputs.page);
              if (inputs.limit) p.set('limit', inputs.limit);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/whatsapp/contacts${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/whatsapp/contacts</Text>
                    <Text style={styles.endpointDesc}>List contacts</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Search <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="name or phone" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'search')} onChangeText={v => setEpInput(epId, 'search', v)} autoCapitalize="none" />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Page</Text>
                        <TextInput style={styles.input} placeholder="1" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'page')} onChangeText={v => setEpInput(epId, 'page', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /contacts/:contactId */}
          {(() => {
            const epId = 'get_contact';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/whatsapp/contacts/${inputs.contactId || ':contactId'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/whatsapp/contacts/:contactId</Text>
                    <Text style={styles.endpointDesc}>Get a single contact</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Contact ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /contacts/:contactId/conversations */}
          {(() => {
            const epId = 'list_conversations';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.page) p.set('page', inputs.page);
              if (inputs.limit) p.set('limit', inputs.limit);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/whatsapp/contacts/${inputs.contactId || ':contactId'}/conversations${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/contacts/:contactId/conversations</Text>
                    <Text style={styles.endpointDesc}>List conversations for a contact</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Contact ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Page</Text>
                        <TextInput style={styles.input} placeholder="1" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'page')} onChangeText={v => setEpInput(epId, 'page', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /conversations/:conversationId/messages */}
          {(() => {
            const epId = 'list_messages';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.page) p.set('page', inputs.page);
              if (inputs.limit) p.set('limit', inputs.limit);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/whatsapp/conversations/${inputs.conversationId || ':conversationId'}/messages${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/conversations/:id/messages</Text>
                    <Text style={styles.endpointDesc}>List messages in a conversation</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Conversation ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'conversationId')} onChangeText={v => setEpInput(epId, 'conversationId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Page</Text>
                        <TextInput style={styles.input} placeholder="1" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'page')} onChangeText={v => setEpInput(epId, 'page', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* POST /messages */}
          {(() => {
            const epId = 'send_message';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            return (
              <View style={[styles.epItem, isOpen && styles.epItemActive]} ref={r => { epItemRefs.current[epId] = r; }}>
              <TouchableOpacity style={styles.epHeader} onPress={() => {
                setExpandedEndpoint(isOpen ? null : epId);
                if (!isOpen && playWindowMode === 'template' && playTemplates.length === 0) handleSetWindowMode('template');
              }}>
                <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.endpointPath}>/api/v1/whatsapp/messages</Text>
                  <Text style={styles.endpointDesc}>Send a WhatsApp message</Text>
                </View>
                <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
              </TouchableOpacity>
              {isOpen && (
                <View style={styles.epBody}>
                  {/* Recipient */}
                  <Text style={styles.fieldLabel}>Recipient Phone (E.164)</Text>
                  <TextInput style={styles.input} placeholder="+971500000000" placeholderTextColor={colors.text.disabled}
                    value={playPhone} onChangeText={v => { setPlayPhone(v); setPlayResponse(null); }}
                    keyboardType="phone-pad" autoCapitalize="none" />

                  {/* Window mode */}
                  <Text style={styles.fieldLabel}>Message Window</Text>
                  <View style={styles.segmentRow}>
                    <TouchableOpacity style={[styles.segmentBtn, { flex: 1 }, playWindowMode === 'free' && styles.segmentBtnActive]}
                      onPress={() => handleSetWindowMode('free')}>
                      <Text style={[styles.segmentBtnText, playWindowMode === 'free' && styles.segmentBtnTextActive]}>Free Message</Text>
                      <Text style={[styles.fieldHint, { marginTop: 0, marginBottom: 0, textAlign: 'center' }]}>Within 24h window</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={[styles.segmentBtn, { flex: 1 }, playWindowMode === 'template' && styles.segmentBtnActive]}
                      onPress={() => handleSetWindowMode('template')}>
                      <Text style={[styles.segmentBtnText, playWindowMode === 'template' && styles.segmentBtnTextActive]}>Template</Text>
                      <Text style={[styles.fieldHint, { marginTop: 0, marginBottom: 0, textAlign: 'center' }]}>After 24h / first contact</Text>
                    </TouchableOpacity>
                  </View>

                  {/* Free message fields */}
                  {playWindowMode === 'free' && (
                    <View>
                      <Text style={styles.fieldLabel}>Message Type</Text>
                      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: spacing.sm }}>
                        <View style={styles.keyPickerRow}>
                          {([
                            { type: 'text', icon: 'chatbubble-outline', label: 'Text' },
                            { type: 'image', icon: 'image-outline', label: 'Image' },
                            { type: 'video', icon: 'videocam-outline', label: 'Video' },
                            { type: 'audio', icon: 'mic-outline', label: 'Voice' },
                            { type: 'document', icon: 'document-outline', label: 'Document' },
                            { type: 'location', icon: 'location-outline', label: 'Location' },
                          ] as const).map(item => {
                            const isActive = playMsgType === item.type;
                            const isDisabled = playReplyMode !== 'none' && item.type !== 'text';
                            return (
                              <TouchableOpacity
                                key={item.type}
                                style={[styles.typeChip, isActive && styles.typeChipActive, isDisabled && { opacity: 0.45 }]}
                                onPress={() => {
                                  if (isDisabled) return;
                                  setPlayMsgType(item.type);
                                  setPlayResponse(null);
                                  setPlayMediaUrl('');
                                  setPlayMediaFile(null);
                                  setPlayMediaMimeType('');
                                  setShowVoiceRecorder(false);
                                }}>
                                <Ionicons name={item.icon as any} size={14} color={isActive ? colors.white : colors.primary} />
                                <Text style={[styles.keyChipText, isActive && styles.keyChipTextActive]}>{item.label}</Text>
                              </TouchableOpacity>
                            );
                          })}
                        </View>
                      </ScrollView>

                      <Text style={styles.fieldLabel}>Reply Actions (optional)</Text>
                      <View style={styles.segmentRow}>
                        <TouchableOpacity style={[styles.segmentBtn, { flex: 1 }, playReplyMode === 'none' && styles.segmentBtnActive]}
                          onPress={() => handleSetReplyMode('none')}>
                          <Text style={[styles.segmentBtnText, playReplyMode === 'none' && styles.segmentBtnTextActive]}>None</Text>
                        </TouchableOpacity>
                        <TouchableOpacity style={[styles.segmentBtn, { flex: 1 }, playReplyMode === 'items' && styles.segmentBtnActive]}
                          onPress={() => handleSetReplyMode('items')}>
                          <Text style={[styles.segmentBtnText, playReplyMode === 'items' && styles.segmentBtnTextActive]}>Reply Items</Text>
                        </TouchableOpacity>
                        <TouchableOpacity style={[styles.segmentBtn, { flex: 1 }, playReplyMode === 'set' && styles.segmentBtnActive]}
                          onPress={() => handleSetReplyMode('set')}>
                          <Text style={[styles.segmentBtnText, playReplyMode === 'set' && styles.segmentBtnTextActive]}>Reply Set</Text>
                        </TouchableOpacity>
                      </View>

                      {(playMsgType === 'text' || playReplyMode !== 'none') && (
                        <View>
                          <Text style={styles.fieldLabel}>{playReplyMode === 'none' ? 'Message Body' : 'Interactive Body Text'}</Text>
                          <TextInput style={[styles.input, styles.playTextArea]} placeholder="Hello from DISLIX API!"
                            placeholderTextColor={colors.text.disabled} value={playText}
                            onChangeText={v => { setPlayText(v); setPlayResponse(null); }} multiline numberOfLines={3} />
                          {playReplyMode !== 'none' && (
                            <Text style={styles.fieldHint}>When reply actions are enabled, this request is sent as WhatsApp interactive message.</Text>
                          )}
                        </View>
                      )}

                      {playReplyMode === 'items' && (
                        <View>
                          <Text style={styles.fieldLabel}>Reply Items (one per line)</Text>
                          <TextInput
                            style={[styles.input, styles.playTextArea]}
                            placeholder={'Yes\nNo\nTalk to sales'}
                            placeholderTextColor={colors.text.disabled}
                            value={playReplyItemsInput}
                            onChangeText={v => { setPlayReplyItemsInput(v); setPlayResponse(null); }}
                            multiline
                            numberOfLines={4}
                          />
                          <Text style={styles.fieldHint}>Up to 10 items. 1-3 items send as buttons, 4+ send as list.</Text>
                        </View>
                      )}

                      {playReplyMode === 'set' && (
                        <View>
                          <Text style={styles.fieldLabel}>Quick Reply Set</Text>
                          {playReplySetsLoading ? (
                            <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.sm }} />
                          ) : playReplySets.length === 0 ? (
                            <View style={styles.playNoKeys}>
                              <Ionicons name="chatbox-ellipses-outline" size={16} color={colors.text.disabled} />
                              <Text style={styles.playNoKeysText}>No quick reply sets found.</Text>
                            </View>
                          ) : (
                            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: spacing.sm }}>
                              <View style={styles.keyPickerRow}>
                                {playReplySets.map((set: any) => {
                                  const isSelected = playSelectedReplySetId === set._id;
                                  return (
                                    <TouchableOpacity
                                      key={set._id}
                                      style={[styles.keyChip, isSelected && styles.keyChipActive]}
                                      onPress={() => { setPlaySelectedReplySetId(set._id); setPlayResponse(null); }}>
                                      <Text style={[styles.keyChipText, isSelected && styles.keyChipTextActive]}>{set.name}</Text>
                                    </TouchableOpacity>
                                  );
                                })}
                              </View>
                            </ScrollView>
                          )}

                          <Text style={styles.fieldHint}>
                            {playReplySetItems.length > 0
                              ? `${Math.min(playReplySetItems.length, 10)} reply options loaded from set${playReplySetItems.length > 10 ? ' (max 10 will be sent)' : ''}.`
                              : 'Select a set to load reply options.'}
                          </Text>
                        </View>
                      )}

                      {(playMsgType === 'image' || playMsgType === 'video' || playMsgType === 'audio' || playMsgType === 'document') && (
                        <View>
                          {playMsgType === 'audio' ? (
                            showVoiceRecorder ? (
                              <VoiceRecorder
                                onRecordingCancel={() => setShowVoiceRecorder(false)}
                                onRecordingComplete={async (uri, _duration) => {
                                  setShowVoiceRecorder(false);
                                  setPlayMediaUploading(true);
                                  setPlayResponse(null);
                                  try {
                                    const mimeMatch = uri.match(/#mime=([^&]+)/);
                                    const mimeType = mimeMatch ? decodeURIComponent(mimeMatch[1]) : 'audio/ogg';
                                    const cleanUri = uri.split('#')[0];
                                    const fileName = `voice_note_${Date.now()}.${mimeType.split('/')[1] || 'ogg'}`;
                                    const up = await apiService.uploadMedia(cleanUri, fileName, mimeType);
                                    if (up.data?.mediaUrl) {
                                      setPlayMediaUrl(up.data.mediaUrl);
                                      setPlayMediaFilename(fileName);
                                      setPlayMediaFile({ name: fileName, size: 0 });
                                      setPlayMediaMimeType(up.data.mediaType || mimeType);
                                    }
                                  } catch { /* ignore */ } finally { setPlayMediaUploading(false); }
                                }}
                              />
                            ) : (
                              <TouchableOpacity style={styles.locationPickerButton} disabled={playMediaUploading}
                                onPress={() => { setPlayResponse(null); setShowVoiceRecorder(true); }}>
                                {playMediaUploading ? <ActivityIndicator size="small" color={colors.primary} />
                                  : <Ionicons name="mic-outline" size={18} color={colors.primary} />}
                                <Text style={styles.locationPickerButtonText}>
                                  {playMediaUploading ? 'Uploading…' : playMediaFile ? 'Record Again' : 'Record Voice Note'}
                                </Text>
                              </TouchableOpacity>
                            )
                          ) : (
                            <TouchableOpacity style={styles.locationPickerButton} disabled={playMediaUploading}
                              onPress={async () => {
                                const result = playMsgType === 'image' ? await MediaService.pickImageFromGallery()
                                  : playMsgType === 'video' ? await MediaService.pickVideo()
                                  : await MediaService.pickDocument();
                                if (!result) return;
                                setPlayMediaUploading(true);
                                setPlayResponse(null);
                                try {
                                  let mimeType = result.mimeType || '';
                                  if (!mimeType.includes('/')) mimeType = playMsgType === 'image' ? 'image/jpeg' : playMsgType === 'video' ? 'video/mp4' : 'application/octet-stream';
                                  const up = await apiService.uploadMedia(result.uri, result.fileName || 'file', mimeType);
                                  if (up.data?.mediaUrl) {
                                    setPlayMediaUrl(up.data.mediaUrl);
                                    setPlayMediaFilename(up.data.mediaName || result.fileName || 'file');
                                    setPlayMediaFile({ name: result.fileName || 'file', size: result.fileSize || 0 });
                                    setPlayMediaMimeType(up.data.mediaType || mimeType);
                                  }
                                } catch { /* ignore */ } finally { setPlayMediaUploading(false); }
                              }}>
                              {playMediaUploading ? <ActivityIndicator size="small" color={colors.primary} />
                                : <Ionicons name="attach-outline" size={18} color={colors.primary} />}
                              <Text style={styles.locationPickerButtonText}>
                                {playMediaUploading ? 'Uploading…' : playMediaFile ? 'Change File' : `Pick ${playMsgType === 'image' ? 'Image' : playMsgType === 'video' ? 'Video' : 'Document'}`}
                              </Text>
                            </TouchableOpacity>
                          )}
                          {playMediaFile && !showVoiceRecorder && (
                            <View style={styles.locationPreview}>
                              <Ionicons name="document-attach-outline" size={14} color={colors.primary} />
                              <View style={{ flex: 1, marginLeft: spacing.xs }}>
                                <Text style={styles.locationPreviewAddress} numberOfLines={1}>{playMediaFile.name}</Text>
                                {playMediaFile.size > 0 && <Text style={styles.locationPreviewCoords}>{(playMediaFile.size / 1024).toFixed(1)} KB</Text>}
                              </View>
                            </View>
                          )}
                          {playMsgType !== 'audio' && (
                            <View>
                              <Text style={styles.fieldLabel}>Caption (optional)</Text>
                              <TextInput style={styles.input} placeholder="Optional caption" placeholderTextColor={colors.text.disabled}
                                value={playMediaCaption} onChangeText={v => { setPlayMediaCaption(v); setPlayResponse(null); }} />
                            </View>
                          )}
                        </View>
                      )}

                      {playMsgType === 'location' && (
                        <View>
                          <TouchableOpacity style={styles.locationPickerButton} onPress={() => setShowLocationPicker(true)}>
                            <Ionicons name="location-outline" size={18} color={colors.primary} />
                            <Text style={styles.locationPickerButtonText}>{playLat && playLng ? 'Change Location' : 'Pick Location'}</Text>
                          </TouchableOpacity>
                          {playLat && playLng && (
                            <View style={styles.locationPreview}>
                              <Ionicons name="location" size={14} color={colors.primary} />
                              <View style={{ flex: 1, marginLeft: spacing.xs }}>
                                {playLocationAddress ? <Text style={styles.locationPreviewAddress}>{playLocationAddress}</Text> : null}
                                <Text style={styles.locationPreviewCoords}>{parseFloat(playLat).toFixed(6)}, {parseFloat(playLng).toFixed(6)}</Text>
                              </View>
                            </View>
                          )}
                          <MapLocationPicker visible={showLocationPicker} onClose={() => setShowLocationPicker(false)}
                            onLocationSelect={loc => { setPlayLat(String(loc.latitude)); setPlayLng(String(loc.longitude)); setPlayLocationAddress(loc.address); setPlayResponse(null); setShowLocationPicker(false); }} />
                        </View>
                      )}
                    </View>
                  )}

                  {/* Template fields */}
                  {playWindowMode === 'template' && (
                    <View>
                      <Text style={styles.fieldLabel}>Template</Text>
                      {playTemplatesLoading ? (
                        <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.sm }} />
                      ) : playTemplates.length === 0 ? (
                        <View style={styles.playNoKeys}>
                          <Ionicons name="document-outline" size={16} color={colors.text.disabled} />
                          <Text style={styles.playNoKeysText}>No templates found. Create templates in your WhatsApp settings.</Text>
                        </View>
                      ) : (
                        <ScrollView style={{ maxHeight: 160 }} nestedScrollEnabled showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
                          {playTemplates.map(t => {
                            const isSelected = playSelectedTemplate?._id === t._id;
                            return (
                              <TouchableOpacity key={t._id} style={[styles.templateRow, isSelected && styles.templateRowActive]}
                                onPress={() => { setPlaySelectedTemplate(t); setPlayTemplateParams({}); setPlayTemplateHeaderMedia(null); setPlayResponse(null); }}>
                                <View style={{ flex: 1 }}>
                                  <Text style={[styles.listItemTitle, isSelected && { color: colors.primary }]}>{t.templateName}</Text>
                                  <Text style={styles.listItemMeta} numberOfLines={1}>{t.templateBody}</Text>
                                </View>
                                <View style={styles.langBadge}><Text style={styles.langBadgeText}>{t.language}</Text></View>
                                {isSelected && <Ionicons name="checkmark-circle" size={16} color={colors.primary} />}
                              </TouchableOpacity>
                            );
                          })}
                        </ScrollView>
                      )}

                      {playSelectedTemplate && (() => {
                        const t = playSelectedTemplate;
                        const headerAsset = t.headerAsset;
                        const hasHeaderMedia = headerAsset?.type && ['image', 'video', 'document'].includes(headerAsset.type);
                        const scanText = (text: string | undefined, section: 'header' | 'body' | 'footer') => {
                          if (!text) return [];
                          const out: any[] = []; let m: RegExpExecArray | null;
                          const re = /\{\{(\d+)\}\}/g;
                          while ((m = re.exec(text)) !== null) out.push({ position: parseInt(m[1], 10), section });
                          return out;
                        };
                        let params: any[] = t.parameters || [];
                        if (params.length === 0 && t.hasParameters) {
                          const seen = new Set<number>();
                          params = [...scanText(t.templateHeader, 'header'), ...scanText(t.templateBody, 'body'), ...scanText(t.templateFooter, 'footer')]
                            .filter(p => { if (seen.has(p.position)) return false; seen.add(p.position); return true; });
                        }
                        const headerParamDefs = params.filter((p: any) => p.section === 'header').sort((a: any, b: any) => a.position - b.position);
                        const bodyParamDefs = params.filter((p: any) => p.section === 'body').sort((a: any, b: any) => a.position - b.position);
                        const footerParamDefs = params.filter((p: any) => p.section === 'footer').sort((a: any, b: any) => a.position - b.position);
                        if (params.length === 0 && !hasHeaderMedia) return null;
                        return (
                          <View style={styles.templateParamsCard}>
                            <Text style={[styles.fieldLabel, { marginBottom: spacing.xs }]}>Template Parameters</Text>
                            {hasHeaderMedia && !playTemplateHeaderMedia && !(headerAsset.mediaId || headerAsset.url) && (
                              <View style={{ marginBottom: spacing.sm }}>
                                <Text style={styles.fieldLabel}>{`Header ${headerAsset.type.charAt(0).toUpperCase() + headerAsset.type.slice(1)}`}<Text style={styles.fieldHint}> — header</Text></Text>
                                <TouchableOpacity style={styles.locationPickerButton} disabled={playTemplateHeaderUploading}
                                  onPress={async () => {
                                    const result = headerAsset.type === 'image' ? await MediaService.pickImageFromGallery()
                                      : headerAsset.type === 'video' ? await MediaService.pickVideo() : await MediaService.pickDocument();
                                    if (!result) return;
                                    setPlayTemplateHeaderUploading(true);
                                    setPlayResponse(null);
                                    try {
                                      let mimeType = result.mimeType || '';
                                      if (!mimeType.includes('/')) mimeType = headerAsset.type === 'image' ? 'image/jpeg' : headerAsset.type === 'video' ? 'video/mp4' : 'application/pdf';
                                      const up = await apiService.uploadMedia(result.uri, result.fileName || 'file', mimeType);
                                      if (up.data?.mediaUrl) setPlayTemplateHeaderMedia({ url: up.data.mediaUrl, mimeType: up.data.mediaType || mimeType, name: result.fileName || 'file' });
                                    } catch { /* ignore */ } finally { setPlayTemplateHeaderUploading(false); }
                                  }}>
                                  {playTemplateHeaderUploading ? <ActivityIndicator size="small" color={colors.primary} /> : <Ionicons name="attach-outline" size={18} color={colors.primary} />}
                                  <Text style={styles.locationPickerButtonText}>{playTemplateHeaderUploading ? 'Uploading…' : playTemplateHeaderMedia ? 'Change File' : `Pick ${headerAsset.type.charAt(0).toUpperCase() + headerAsset.type.slice(1)}`}</Text>
                                </TouchableOpacity>
                                {playTemplateHeaderMedia && (
                                  <View style={styles.locationPreview}>
                                    <Ionicons name="document-attach-outline" size={14} color={colors.primary} />
                                    <Text style={[styles.locationPreviewAddress, { flex: 1, marginLeft: spacing.xs }]} numberOfLines={1}>{(playTemplateHeaderMedia as any).name}</Text>
                                  </View>
                                )}
                              </View>
                            )}
                            {!hasHeaderMedia && headerParamDefs.map((p: any) => (
                              <View key={`h-${p.position}`} style={{ marginBottom: spacing.sm }}>
                                <Text style={styles.fieldLabel}>{`{{${p.position}}}`}<Text style={styles.fieldHint}> — header</Text></Text>
                                <TextInput style={styles.input} placeholder={p.placeholder || `Header value for {{${p.position}}}`}
                                  placeholderTextColor={colors.text.disabled} value={playTemplateParams[String(p.position)] || ''}
                                  onChangeText={v => { setPlayTemplateParams(prev => ({ ...prev, [String(p.position)]: v })); setPlayResponse(null); }} />
                              </View>
                            ))}
                            {bodyParamDefs.map((p: any) => (
                              <View key={`b-${p.position}`} style={{ marginBottom: spacing.sm }}>
                                <Text style={styles.fieldLabel}>{`{{${p.position}}}`}<Text style={styles.fieldHint}> — body{p.name ? ` · ${p.name}` : ''}</Text></Text>
                                <TextInput style={styles.input} placeholder={p.placeholder || `Value for {{${p.position}}}`}
                                  placeholderTextColor={colors.text.disabled} value={playTemplateParams[String(p.position)] || ''}
                                  onChangeText={v => { setPlayTemplateParams(prev => ({ ...prev, [String(p.position)]: v })); setPlayResponse(null); }} />
                              </View>
                            ))}
                            {footerParamDefs.map((p: any) => (
                              <View key={`f-${p.position}`} style={{ marginBottom: spacing.sm }}>
                                <Text style={styles.fieldLabel}>{`{{${p.position}}}`}<Text style={styles.fieldHint}> — footer</Text></Text>
                                <TextInput style={styles.input} placeholder={p.placeholder || `Footer value for {{${p.position}}}`}
                                  placeholderTextColor={colors.text.disabled} value={playTemplateParams[String(p.position)] || ''}
                                  onChangeText={v => { setPlayTemplateParams(prev => ({ ...prev, [String(p.position)]: v })); setPlayResponse(null); }} />
                              </View>
                            ))}
                            <View style={styles.templatePreview}>
                              <Text style={[styles.fieldLabel, { marginBottom: spacing.xs }]}>Preview</Text>
                              {playSelectedTemplate.templateHeader && (
                                <Text style={styles.templatePreviewHeader}>
                                  {playSelectedTemplate.templateHeader.replace(/\{\{(\d+)\}\}/g, (_: string, pos: string) => playTemplateParams[pos] || `{{${pos}}}`)}
                                </Text>
                              )}
                              <Text style={styles.templatePreviewBody}>
                                {playSelectedTemplate.templateBody.replace(/\{\{(\d+)\}\}/g, (_: string, pos: string) => playTemplateParams[pos] || `{{${pos}}}`)}
                              </Text>
                              {playSelectedTemplate.templateFooter && (
                                <Text style={styles.templatePreviewFooter}>
                                  {playSelectedTemplate.templateFooter.replace(/\{\{(\d+)\}\}/g, (_: string, pos: string) => playTemplateParams[pos] || `{{${pos}}}`)}
                                </Text>
                              )}
                            </View>
                          </View>
                        );
                      })()}
                    </View>
                  )}

                  {/* curl preview */}
                  <View style={{ marginTop: spacing.sm }}>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl Preview</Text>
                      <TouchableOpacity onPress={() => {
                        const body = JSON.stringify(buildPlayBody());
                        const key = rawKey || '<your_key>';
                        const curl = `curl -X POST ${CODE_BASE_URL}/api/v1/whatsapp/messages \\\n  -H "Authorization: Bearer ${key}" \\\n  -H "Content-Type: application/json" \\\n  -d '${body}'`;
                        copyToClipboard(curl, 'curlPreview');
                      }} style={styles.copyBtn}>
                        <Ionicons name={copyConfirm === 'curlPreview' ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === 'curlPreview' ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>
                        {`curl -X POST ${CODE_BASE_URL}/api/v1/whatsapp/messages \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildPlayBody(), null, 0)}'`}
                      </Text>
                    </View>
                  </View>

                  {/* Send button */}
                  <TouchableOpacity style={[styles.primaryBtn, playSending && styles.primaryBtnDisabled]}
                    onPress={handlePlaySend} disabled={playSending}>
                    {playSending ? <ActivityIndicator size="small" color={colors.white} />
                      : <><Ionicons name="send" size={15} color={colors.white} /><Text style={styles.primaryBtnText}>Send Message</Text></>}
                  </TouchableOpacity>

                  {/* Response */}
                  {playResponse && (
                    <View style={[styles.playResponseBox, playResponse.ok ? styles.playResponseOk : styles.playResponseErr]}>
                      <View style={styles.playResponseHeader}>
                        <Ionicons name={playResponse.ok ? 'checkmark-circle' : 'close-circle'} size={16} color={playResponse.ok ? colors.success : colors.error} />
                        <Text style={[styles.playResponseStatus, playResponse.ok ? styles.playResponseStatusOk : styles.playResponseStatusErr]}>
                          {playResponse.status > 0 ? `HTTP ${playResponse.status}` : 'Error'}{playResponse.ok ? ' — Success' : ' — Failed'}
                        </Text>
                        <TouchableOpacity style={{ marginLeft: 'auto' }} onPress={() => copyToClipboard(playResponse.body, 'playResponse')}>
                          <Ionicons name={copyConfirm === 'playResponse' ? 'checkmark' : 'copy-outline'} size={14} color={colors.text.secondary} />
                        </TouchableOpacity>
                      </View>
                      <ScrollView style={styles.playResponseScroll} nestedScrollEnabled>
                        <Text style={styles.playResponseBody} selectable>{playResponse.body}</Text>
                      </ScrollView>
                    </View>
                  )}
                </View>
              )}
            </View>
          );
        })()}

        </View>

        {/* ── X (Twitter) Endpoints group ── */}
        <View style={[styles.card, groupStyle('x-twitter')]}> 
          <View style={styles.epGroupHeader}>
            <Ionicons name="logo-twitter" size={16} color="#111827" />
            <Text style={styles.epGroupTitle}>X (Twitter) Endpoints</Text>
          </View>

          {/* GET /x-twitter/contacts */}
          {(() => {
            const epId = 'x_list_contacts';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.search) p.set('search', inputs.search);
              if (inputs.page) p.set('page', inputs.page);
              if (inputs.limit) p.set('limit', inputs.limit);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/x-twitter/contacts${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/x-twitter/contacts</Text>
                    <Text style={styles.endpointDesc}>List X contacts</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Search <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="name or username" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'search')} onChangeText={v => setEpInput(epId, 'search', v)} autoCapitalize="none" />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Page</Text>
                        <TextInput style={styles.input} placeholder="1" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'page')} onChangeText={v => setEpInput(epId, 'page', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n+  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n+  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /x-twitter/contacts/:contactId */}
          {(() => {
            const epId = 'x_get_contact';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/x-twitter/contacts/${inputs.contactId || ':contactId'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/x-twitter/contacts/:contactId</Text>
                    <Text style={styles.endpointDesc}>Get an X contact</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Contact ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n+  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /x-twitter/contacts/:contactId/conversations */}
          {(() => {
            const epId = 'x_list_conversations';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.page) p.set('page', inputs.page);
              if (inputs.limit) p.set('limit', inputs.limit);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/x-twitter/contacts/${inputs.contactId || ':contactId'}/conversations${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/x-twitter/contacts/:contactId/conversations</Text>
                    <Text style={styles.endpointDesc}>List X conversations for a contact</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Contact ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Page</Text>
                        <TextInput style={styles.input} placeholder="1" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'page')} onChangeText={v => setEpInput(epId, 'page', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n+  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /x-twitter/conversations/:conversationId/messages */}
          {(() => {
            const epId = 'x_list_messages';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.page) p.set('page', inputs.page);
              if (inputs.limit) p.set('limit', inputs.limit);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/x-twitter/conversations/${inputs.conversationId || ':conversationId'}/messages${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/x-twitter/conversations/:conversationId/messages</Text>
                    <Text style={styles.endpointDesc}>List messages in an X conversation</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Conversation ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'conversationId')} onChangeText={v => setEpInput(epId, 'conversationId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Page</Text>
                        <TextInput style={styles.input} placeholder="1" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'page')} onChangeText={v => setEpInput(epId, 'page', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n+  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* POST /x-twitter/messages */}
          {(() => {
            const epId = 'x_send_message';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = () => `${CODE_BASE_URL}/api/v1/x-twitter/messages`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/x-twitter/messages</Text>
                    <Text style={styles.endpointDesc}>Send an X message</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Conversation ID <Text style={styles.fieldHint}>(required)</Text></Text>
                    <TextInput style={styles.input} placeholder="x_conversation_123" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'conversationId')} onChangeText={v => setEpInput(epId, 'conversationId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Message Content <Text style={styles.fieldHint}>(required)</Text></Text>
                    <TextInput style={[styles.input, styles.playTextArea]} placeholder="Hello from DISLIX X API"
                      placeholderTextColor={colors.text.disabled} value={inp(epId, 'content')}
                      onChangeText={v => setEpInput(epId, 'content', v)} multiline numberOfLines={3} />
                    {(() => {
                      const inputs = epInputs[epId] || {};
                      const body: any = {
                        conversationId: inputs.conversationId || '',
                        content: inputs.content || ''
                      };
                      return (
                        <>
                          <View style={styles.codeBlock}>
                            <Text style={styles.codeText} selectable>{`curl -X POST ${buildUrl()} \\\n+  -H "Authorization: Bearer ${rawKey || '<your_key>'}" \\\n+  -H "Content-Type: application/json" \\\n+  -d '${JSON.stringify(body)}'`}</Text>
                          </View>
                          <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                            onPress={() => handleEpPost(epId, buildUrl(), body)} disabled={epSending[epId]}>
                            {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                              : <><Ionicons name="send" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                          </TouchableOpacity>
                        </>
                      );
                    })()}
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}
        </View>

        {/* ── Users Endpoints group ── */}
        <View style={[styles.card, groupStyle('users')]}>
          <View style={styles.epGroupHeader}>
            <Ionicons name="people-outline" size={16} color={colors.primary} />
            <Text style={styles.epGroupTitle}>Users Endpoints</Text>
          </View>

          {/* POST /users */}
          {(() => {
            const epId = 'create_user';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = () => `${CODE_BASE_URL}/api/v1/users`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/users</Text>
                    <Text style={styles.endpointDesc}>Create a user (record or login-enabled)</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Email <Text style={styles.fieldHint}>(required)</Text></Text>
                    <TextInput style={styles.input} placeholder="user@example.com" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'email')} onChangeText={v => setEpInput(epId, 'email', v)} autoCapitalize="none" keyboardType="email-address" />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>First Name</Text>
                        <TextInput style={styles.input} placeholder="John" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'firstName')} onChangeText={v => setEpInput(epId, 'firstName', v)} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Last Name</Text>
                        <TextInput style={styles.input} placeholder="Doe" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'lastName')} onChangeText={v => setEpInput(epId, 'lastName', v)} />
                      </View>
                    </View>
                    <Text style={styles.fieldLabel}>Role <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="member" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'role')} onChangeText={v => setEpInput(epId, 'role', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Department ID <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'departmentId')} onChangeText={v => setEpInput(epId, 'departmentId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Allow Login <Text style={styles.fieldHint}>(true = counts toward subscription limit)</Text></Text>
                    <TextInput style={styles.input} placeholder="false" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'allowLogin')} onChangeText={v => setEpInput(epId, 'allowLogin', v)} autoCapitalize="none" />
                    {(() => {
                      const inputs = epInputs[epId] || {};
                      const body: any = {};
                      if (inputs.email) body.email = inputs.email;
                      if (inputs.firstName) body.firstName = inputs.firstName;
                      if (inputs.lastName) body.lastName = inputs.lastName;
                      if (inputs.role) body.role = inputs.role;
                      if (inputs.departmentId) body.departmentId = inputs.departmentId;
                      if (inputs.allowLogin) body.allowLogin = inputs.allowLogin === 'true';
                      const rawKey = playRawKey || playRawKeyInput.trim();
                      const curl = `curl -X POST ${buildUrl()} \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body, null, 0)}'`;
                      return (
                        <>
                          <View style={styles.curlHeader}>
                            <Text style={styles.fieldLabel}>curl</Text>
                            <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(curl, `curl_${epId}`)}>
                              <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                            </TouchableOpacity>
                          </View>
                          <View style={styles.codeBlock}>
                            <Text style={styles.codeText} selectable>{curl}</Text>
                          </View>
                        </>
                      );
                    })()}
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => {
                        const inputs = epInputs[epId] || {};
                        const body: any = {};
                        if (inputs.email) body.email = inputs.email;
                        if (inputs.firstName) body.firstName = inputs.firstName;
                        if (inputs.lastName) body.lastName = inputs.lastName;
                        if (inputs.role) body.role = inputs.role;
                        if (inputs.departmentId) body.departmentId = inputs.departmentId;
                        if (inputs.allowLogin) body.allowLogin = inputs.allowLogin === 'true';
                        handleEpPost(epId, `${CODE_BASE_URL}/api/v1/users`, body);
                      }} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /users */}
          {(() => {
            const epId = 'list_users';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.departmentId) p.set('departmentId', inputs.departmentId);
              if (inputs.role) p.set('role', inputs.role);
              if (inputs.userStatus) p.set('userStatus', inputs.userStatus);
              if (inputs.page) p.set('page', inputs.page);
              if (inputs.limit) p.set('limit', inputs.limit);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/users${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/users</Text>
                    <Text style={styles.endpointDesc}>List business members</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Department ID <Text style={styles.fieldHint}>(optional filter)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3..." placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'departmentId')} onChangeText={v => setEpInput(epId, 'departmentId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Role <Text style={styles.fieldHint}>(optional filter)</Text></Text>
                    <TextInput style={styles.input} placeholder="member" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'role')} onChangeText={v => setEpInput(epId, 'role', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>User Status <Text style={styles.fieldHint}>(optional filter)</Text></Text>
                    <TextInput style={styles.input} placeholder="active" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'userStatus')} onChangeText={v => setEpInput(epId, 'userStatus', v)} autoCapitalize="none" />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Page</Text>
                        <TextInput style={styles.input} placeholder="1" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'page')} onChangeText={v => setEpInput(epId, 'page', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* DELETE /users/:userId */}
          {(() => {
            const epId = 'delete_user';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/users/${inputs.userId || ':userId'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeDelete]}><Text style={styles.methodText}>DEL</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/users/:userId</Text>
                    <Text style={styles.endpointDesc}>Remove user from business</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>User ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'userId')} onChangeText={v => setEpInput(epId, 'userId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpDelete(epId, buildUrl(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /assignments/contacts/:contactId */}
          {(() => {
            const epId = 'get_contact_assignments_u';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/assignments/contacts/${inputs.contactId || ':contactId'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/assignments/contacts/:contactId</Text>
                    <Text style={styles.endpointDesc}>Get all active assignments for a contact</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Contact ID <Text style={styles.fieldHint}>(required)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* PATCH /assignments/:id — status update */}
          {(() => {
            const epId = 'update_assignment_status_u';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const inputs = epInputs[epId] || {};
            const action = inputs.action || 'cancel';
            const buildUrl = (i: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/assignments/${i.assignmentId || ':id'}`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, string> = { action: i.action || 'cancel' };
              if ((i.action || 'cancel') === 'complete' && i.completionNotes) body.completionNotes = i.completionNotes;
              if ((i.action || 'cancel') === 'transfer') {
                if (i.transferToUserId) body.transferToUserId = i.transferToUserId;
                if (i.transferToDepartmentId) body.transferToDepartmentId = i.transferToDepartmentId;
                if (i.transferReason) body.transferReason = i.transferReason;
              }
              return JSON.stringify(body, null, 2);
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePatch]}><Text style={styles.methodText}>PAT</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/assignments/:id</Text>
                    <Text style={styles.endpointDesc}>Update assignment status (cancel / complete / start / transfer)</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Changes the lifecycle status of a contact assignment. Allowed actions: {'\n'}
                      • <Text style={{ fontWeight: 'bold' }}>cancel</Text> — marks assignment cancelled (preserves audit trail){'\n'}
                      • <Text style={{ fontWeight: 'bold' }}>complete</Text> — marks assignment completed{'\n'}
                      • <Text style={{ fontWeight: 'bold' }}>start</Text> — locks a department assignment and creates a user child assignment{'\n'}
                      • <Text style={{ fontWeight: 'bold' }}>transfer</Text> — transfers to another user or department
                    </Text>
                    <Text style={styles.fieldLabel}>Assignment ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'assignmentId')} onChangeText={v => setEpInput(epId, 'assignmentId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Action</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: spacing.sm }}>
                      {(['cancel', 'complete', 'start', 'transfer'] as const).map(a => (
                        <TouchableOpacity key={a} onPress={() => setEpInput(epId, 'action', a)}
                          style={[styles.keyChip, action === a && styles.keyChipActive]}>
                          <Text style={[styles.keyChipText, action === a && styles.keyChipTextActive]}>{a}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                    {action === 'complete' && (
                      <>
                        <Text style={styles.fieldLabel}>Completion Notes</Text>
                        <TextInput style={styles.input} placeholder="Issue resolved" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'completionNotes')} onChangeText={v => setEpInput(epId, 'completionNotes', v)} />
                      </>
                    )}
                    {action === 'transfer' && (
                      <>
                        <Text style={styles.fieldLabel}>Transfer to User ID</Text>
                        <TextInput style={styles.input} placeholder="userId (or leave blank for dept)" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'transferToUserId')} onChangeText={v => setEpInput(epId, 'transferToUserId', v)} autoCapitalize="none" autoCorrect={false} />
                        <Text style={styles.fieldLabel}>Transfer to Department ID</Text>
                        <TextInput style={styles.input} placeholder="departmentId (or leave blank for user)" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'transferToDepartmentId')} onChangeText={v => setEpInput(epId, 'transferToDepartmentId', v)} autoCapitalize="none" autoCorrect={false} />
                        <Text style={styles.fieldLabel}>Transfer Reason (optional)</Text>
                        <TextInput style={styles.input} placeholder="Out of office" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'transferReason')} onChangeText={v => setEpInput(epId, 'transferReason', v)} />
                      </>
                    )}
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(
                        `curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${buildBody(inputs)}'`,
                        `curl_${epId}`
                      )}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${buildBody(inputs)}'`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPatch(epId, buildUrl(inputs), JSON.parse(buildBody(inputs)))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* DELETE /assignments/contacts/:contactId */}
          {(() => {
            const epId = 'free_contact_assignments_u';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/assignments/contacts/${inputs.contactId || ':contactId'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeDelete]}><Text style={styles.methodText}>DEL</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/assignments/contacts/:contactId</Text>
                    <Text style={styles.endpointDesc}>Free all assignments for a contact</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Removes all active assignments for this contact (user and department). Automated webhook replies resume once all assignments are cleared.
                    </Text>
                    <Text style={styles.fieldLabel}>Contact ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpDelete(epId, buildUrl(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}
        </View>

        {/* ── Departments Endpoints group ── */}
        <View style={[styles.card, groupStyle('departments')]}>
          <View style={styles.epGroupHeader}>
            <Ionicons name="business-outline" size={16} color={colors.primary} />
            <Text style={styles.epGroupTitle}>Departments Endpoints</Text>
          </View>

          {/* POST /departments */}
          {(() => {
            const epId = 'create_department';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = () => `${CODE_BASE_URL}/api/v1/departments`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/departments</Text>
                    <Text style={styles.endpointDesc}>Create a department</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Name <Text style={styles.fieldHint}>(required)</Text></Text>
                    <TextInput style={styles.input} placeholder="Support" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'name')} onChangeText={v => setEpInput(epId, 'name', v)} />
                    <Text style={styles.fieldLabel}>Description <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="Customer support team" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'description')} onChangeText={v => setEpInput(epId, 'description', v)} />
                    <Text style={styles.fieldLabel}>Type <Text style={styles.fieldHint}>(optional — operational | support | management | sales | marketing | customer_service | technical | other)</Text></Text>
                    <TextInput style={styles.input} placeholder="operational" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'type')} onChangeText={v => setEpInput(epId, 'type', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Manager ID <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3..." placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'managerId')} onChangeText={v => setEpInput(epId, 'managerId', v)} autoCapitalize="none" autoCorrect={false} />
                    {(() => {
                      const inputs = epInputs[epId] || {};
                      const body: any = {};
                      if (inputs.name) body.name = inputs.name;
                      if (inputs.description) body.description = inputs.description;
                      if (inputs.type) body.type = inputs.type;
                      if (inputs.managerId) body.managerId = inputs.managerId;
                      const rawKey = playRawKey || playRawKeyInput.trim();
                      const curl = `curl -X POST ${buildUrl()} \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body, null, 0)}'`;
                      return (
                        <>
                          <View style={styles.curlHeader}>
                            <Text style={styles.fieldLabel}>curl</Text>
                            <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(curl, `curl_${epId}`)}>
                              <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                            </TouchableOpacity>
                          </View>
                          <View style={styles.codeBlock}>
                            <Text style={styles.codeText} selectable>{curl}</Text>
                          </View>
                        </>
                      );
                    })()}
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => {
                        const inputs = epInputs[epId] || {};
                        const body: any = {};
                        if (inputs.name) body.name = inputs.name;
                        if (inputs.description) body.description = inputs.description;
                        if (inputs.type) body.type = inputs.type;
                        if (inputs.managerId) body.managerId = inputs.managerId;
                        handleEpPost(epId, `${CODE_BASE_URL}/api/v1/departments`, body);
                      }} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /departments */}
          {(() => {
            const epId = 'list_departments';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.isActive) p.set('isActive', inputs.isActive);
              if (inputs.page) p.set('page', inputs.page);
              if (inputs.limit) p.set('limit', inputs.limit);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/departments${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/departments</Text>
                    <Text style={styles.endpointDesc}>List departments</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Is Active <Text style={styles.fieldHint}>(optional: true / false)</Text></Text>
                    <TextInput style={styles.input} placeholder="true" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'isActive')} onChangeText={v => setEpInput(epId, 'isActive', v)} autoCapitalize="none" />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Page</Text>
                        <TextInput style={styles.input} placeholder="1" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'page')} onChangeText={v => setEpInput(epId, 'page', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* DELETE /departments/:departmentId */}
          {(() => {
            const epId = 'delete_department';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/departments/${inputs.departmentId || ':departmentId'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeDelete]}><Text style={styles.methodText}>DEL</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/departments/:id</Text>
                    <Text style={styles.endpointDesc}>Delete a department</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Department ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'departmentId')} onChangeText={v => setEpInput(epId, 'departmentId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpDelete(epId, buildUrl(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* POST /api/v1/assignments — assign user to dept */}
          {(() => {
            const epId = 'assign_user_to_dept';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = () => `${CODE_BASE_URL}/api/v1/assignments`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/assignments</Text>
                    <Text style={styles.endpointDesc}>Assign user to a department</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Both userId and departmentId are validated to belong to the authenticated business — cross-business assignment is rejected with 404.
                    </Text>
                    <Text style={styles.fieldLabel}>User ID <Text style={styles.fieldHint}>(required — must be a member of your business)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'userId')} onChangeText={v => setEpInput(epId, 'userId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Department ID <Text style={styles.fieldHint}>(required — must belong to your business)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d2" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'departmentId')} onChangeText={v => setEpInput(epId, 'departmentId', v)} autoCapitalize="none" autoCorrect={false} />
                    {(() => {
                      const inputs = epInputs[epId] || {};
                      const body: any = {};
                      if (inputs.userId) body.userId = inputs.userId;
                      if (inputs.departmentId) body.departmentId = inputs.departmentId;
                      const rawKey = playRawKey || playRawKeyInput.trim();
                      const curl = `curl -X POST ${buildUrl()} \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body, null, 0)}'`;
                      return (
                        <>
                          <View style={styles.curlHeader}>
                            <Text style={styles.fieldLabel}>curl</Text>
                            <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(curl, `curl_${epId}`)}>
                              <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                            </TouchableOpacity>
                          </View>
                          <View style={styles.codeBlock}>
                            <Text style={styles.codeText} selectable>{curl}</Text>
                          </View>
                        </>
                      );
                    })()}
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => {
                        const inputs = epInputs[epId] || {};
                        const body: any = {};
                        if (inputs.userId) body.userId = inputs.userId;
                        if (inputs.departmentId) body.departmentId = inputs.departmentId;
                        handleEpPost(epId, buildUrl(), body);
                      }} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /api/v1/assignments — list user-dept assignments */}
          {(() => {
            const epId = 'list_dept_members';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.userId) p.set('userId', inputs.userId);
              if (inputs.departmentId) p.set('departmentId', inputs.departmentId);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/assignments${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/assignments</Text>
                    <Text style={styles.endpointDesc}>List user-department assignments</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>User ID <Text style={styles.fieldHint}>(optional filter)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3..." placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'userId')} onChangeText={v => setEpInput(epId, 'userId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Department ID <Text style={styles.fieldHint}>(optional filter)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3..." placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'departmentId')} onChangeText={v => setEpInput(epId, 'departmentId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* DELETE /api/v1/assignments/:userId — remove user from dept */}
          {(() => {
            const epId = 'remove_dept_member';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/assignments/${inputs.userId || ':userId'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeDelete]}><Text style={styles.methodText}>DEL</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/assignments/:userId</Text>
                    <Text style={styles.endpointDesc}>Remove user from department</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      User must belong to the authenticated business — foreign userId returns 404.
                    </Text>
                    <Text style={styles.fieldLabel}>User ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'userId')} onChangeText={v => setEpInput(epId, 'userId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpDelete(epId, buildUrl(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* POST /assignments/contacts/:contactId/department */}
          {(() => {
            const epId = 'assign_contact_to_dept';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/assignments/contacts/${inputs.contactId || ':contactId'}/department`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/assignments/contacts/:contactId/department</Text>
                    <Text style={styles.endpointDesc}>Assign contact to a department</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Routes the contact to a department queue and pauses automated replies.
                    </Text>
                    <Text style={styles.fieldLabel}>Contact ID <Text style={styles.fieldHint}>(path param, required)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Department ID <Text style={styles.fieldHint}>(body, required)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d2" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'departmentId')} onChangeText={v => setEpInput(epId, 'departmentId', v)} autoCapitalize="none" autoCorrect={false} />
                    {(() => {
                      const inputs = epInputs[epId] || {};
                      const body: any = {};
                      if (inputs.departmentId) body.departmentId = inputs.departmentId;
                      const rawKey = playRawKey || playRawKeyInput.trim();
                      const curl = `curl -X POST ${buildUrl(inputs)} \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body, null, 0)}'`;
                      return (
                        <>
                          <View style={styles.curlHeader}>
                            <Text style={styles.fieldLabel}>curl</Text>
                            <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(curl, `curl_${epId}`)}>
                              <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                            </TouchableOpacity>
                          </View>
                          <View style={styles.codeBlock}>
                            <Text style={styles.codeText} selectable>{curl}</Text>
                          </View>
                        </>
                      );
                    })()}
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => {
                        const inputs = epInputs[epId] || {};
                        const body: any = {};
                        if (inputs.departmentId) body.departmentId = inputs.departmentId;
                        handleEpPost(epId, buildUrl(inputs), body);
                      }} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /assignments/contacts/:contactId */}
          {(() => {
            const epId = 'get_contact_assignments_d';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/assignments/contacts/${inputs.contactId || ':contactId'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/assignments/contacts/:contactId</Text>
                    <Text style={styles.endpointDesc}>Get all active assignments for a contact</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Contact ID <Text style={styles.fieldHint}>(required)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* PATCH /assignments/:id — status update */}
          {(() => {
            const epId = 'update_assignment_status_d';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const inputs = epInputs[epId] || {};
            const action = inputs.action || 'cancel';
            const buildUrl = (i: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/assignments/${i.assignmentId || ':id'}`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, string> = { action: i.action || 'cancel' };
              if ((i.action || 'cancel') === 'complete' && i.completionNotes) body.completionNotes = i.completionNotes;
              if ((i.action || 'cancel') === 'transfer') {
                if (i.transferToUserId) body.transferToUserId = i.transferToUserId;
                if (i.transferToDepartmentId) body.transferToDepartmentId = i.transferToDepartmentId;
                if (i.transferReason) body.transferReason = i.transferReason;
              }
              return JSON.stringify(body, null, 2);
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePatch]}><Text style={styles.methodText}>PAT</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/assignments/:id</Text>
                    <Text style={styles.endpointDesc}>Update assignment status (cancel / complete / start / transfer)</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Changes the lifecycle status of a contact assignment. Allowed actions: {'\n'}
                      • <Text style={{ fontWeight: 'bold' }}>cancel</Text> — marks assignment cancelled (preserves audit trail){'\n'}
                      • <Text style={{ fontWeight: 'bold' }}>complete</Text> — marks assignment completed{'\n'}
                      • <Text style={{ fontWeight: 'bold' }}>start</Text> — locks a department assignment and creates a user child assignment{'\n'}
                      • <Text style={{ fontWeight: 'bold' }}>transfer</Text> — transfers to another user or department
                    </Text>
                    <Text style={styles.fieldLabel}>Assignment ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'assignmentId')} onChangeText={v => setEpInput(epId, 'assignmentId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Action</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: spacing.sm }}>
                      {(['cancel', 'complete', 'start', 'transfer'] as const).map(a => (
                        <TouchableOpacity key={a} onPress={() => setEpInput(epId, 'action', a)}
                          style={[styles.keyChip, action === a && styles.keyChipActive]}>
                          <Text style={[styles.keyChipText, action === a && styles.keyChipTextActive]}>{a}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                    {action === 'complete' && (
                      <>
                        <Text style={styles.fieldLabel}>Completion Notes</Text>
                        <TextInput style={styles.input} placeholder="Issue resolved" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'completionNotes')} onChangeText={v => setEpInput(epId, 'completionNotes', v)} />
                      </>
                    )}
                    {action === 'transfer' && (
                      <>
                        <Text style={styles.fieldLabel}>Transfer to User ID</Text>
                        <TextInput style={styles.input} placeholder="userId (or leave blank for dept)" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'transferToUserId')} onChangeText={v => setEpInput(epId, 'transferToUserId', v)} autoCapitalize="none" autoCorrect={false} />
                        <Text style={styles.fieldLabel}>Transfer to Department ID</Text>
                        <TextInput style={styles.input} placeholder="departmentId (or leave blank for user)" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'transferToDepartmentId')} onChangeText={v => setEpInput(epId, 'transferToDepartmentId', v)} autoCapitalize="none" autoCorrect={false} />
                        <Text style={styles.fieldLabel}>Transfer Reason (optional)</Text>
                        <TextInput style={styles.input} placeholder="Out of office" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'transferReason')} onChangeText={v => setEpInput(epId, 'transferReason', v)} />
                      </>
                    )}
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(
                        `curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${buildBody(inputs)}'`,
                        `curl_${epId}`
                      )}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${buildBody(inputs)}'`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPatch(epId, buildUrl(inputs), JSON.parse(buildBody(inputs)))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* DELETE /assignments/contacts/:contactId */}
          {(() => {
            const epId = 'free_contact_assignments_d';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/assignments/contacts/${inputs.contactId || ':contactId'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeDelete]}><Text style={styles.methodText}>DEL</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/assignments/contacts/:contactId</Text>
                    <Text style={styles.endpointDesc}>Free all assignments for a contact</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Removes all active assignments for this contact (user and department). Automated webhook replies resume once all assignments are cleared.
                    </Text>
                    <Text style={styles.fieldLabel}>Contact ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpDelete(epId, buildUrl(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}
        </View>

        {/* ── Phone Endpoints group ── */}
        <View style={[styles.card, groupStyle('phone')]}>
          <View style={styles.epGroupHeader}>
            <Ionicons name="call-outline" size={16} color={colors.primary as string} />
            <Text style={styles.epGroupTitle}>Phone Endpoints</Text>
          </View>
          <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
            Calls placed through DISLIX's own WebRTC dialer widget are tracked
            automatically. If your own system places/receives calls directly through
            InnoCalls instead, use "Report a call" the moment it happens so this app
            becomes aware of it, then PATCH its status once the call ends.
          </Text>

          {/* POST /phone/calls */}
          {(() => {
            const epId = 'phone_create_call';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = () => `${CODE_BASE_URL}/api/v1/phone/calls`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, any> = {};
              if (i.phoneNumber) body.phoneNumber = i.phoneNumber;
              if (i.providerCallId) body.providerCallId = i.providerCallId;
              if (i.direction) body.direction = i.direction;
              if (i.contactId) body.contactId = i.contactId;
              if (i.assignedTo) body.assignedTo = i.assignedTo;
              if (i.callerNumber) body.callerNumber = i.callerNumber;
              if (i.status) body.status = i.status;
              if (i.startedAt) body.startedAt = i.startedAt;
              if (i.recordingUrl) body.recordingUrl = i.recordingUrl;
              return body;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/phone/calls</Text>
                    <Text style={styles.endpointDesc}>Report a call placed/received outside this app</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      For a call your own system placed/received directly through InnoCalls
                      (not through this app's dialer widget) — call this the moment you place
                      or receive it so this app becomes aware of it, then PATCH
                      /v1/phone/calls/:id (using the id returned here) once the call ends.
                      There's no way for this app to fetch a recording for a call like this
                      on its own (it never went through this app's own WebRTC session) —
                      pass "recordingUrl" yourself on create or on the later PATCH if you
                      want one attached to the record.
                    </Text>
                    <Text style={styles.fieldLabel}>Phone Number <Text style={styles.fieldHint}>(required)</Text></Text>
                    <TextInput style={styles.input} placeholder="+201023356631" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'phoneNumber')} onChangeText={v => setEpInput(epId, 'phoneNumber', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Provider Call ID <Text style={styles.fieldHint}>(required, your own correlation id)</Text></Text>
                    <TextInput style={styles.input} placeholder="your-system-call-id-123" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'providerCallId')} onChangeText={v => setEpInput(epId, 'providerCallId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Direction <Text style={styles.fieldHint}>(optional, defaults to outbound)</Text></Text>
                    <TextInput style={styles.input} placeholder="inbound | outbound" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'direction')} onChangeText={v => setEpInput(epId, 'direction', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Contact ID <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Assigned To (User ID) <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'assignedTo')} onChangeText={v => setEpInput(epId, 'assignedTo', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Caller Number <Text style={styles.fieldHint}>(optional, defaults to the connected number)</Text></Text>
                    <TextInput style={styles.input} placeholder="+966538380401" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'callerNumber')} onChangeText={v => setEpInput(epId, 'callerNumber', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Status <Text style={styles.fieldHint}>(optional, defaults to ringing)</Text></Text>
                    <TextInput style={styles.input} placeholder="ringing | answered | completed | not-responded | cancelled | ended | failed" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'status')} onChangeText={v => setEpInput(epId, 'status', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Started At <Text style={styles.fieldHint}>(optional, ISO timestamp, defaults to now)</Text></Text>
                    <TextInput style={styles.input} placeholder="2026-01-01T12:00:00.000Z" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'startedAt')} onChangeText={v => setEpInput(epId, 'startedAt', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Recording URL <Text style={styles.fieldHint}>(optional — your own hosted copy, or one you resolved yourself)</Text></Text>
                    <TextInput style={styles.input} placeholder="https://example.com/recordings/call-123.mp3" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'recordingUrl')} onChangeText={v => setEpInput(epId, 'recordingUrl', v)} autoCapitalize="none" autoCorrect={false} />
                    {(() => {
                      const inputs = epInputs[epId] || {};
                      const key = playRawKey || playRawKeyInput.trim();
                      const curl = `curl -X POST "${buildUrl()}" \\\n  -H "Authorization: Bearer ${key || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`;
                      return (
                        <>
                          <View style={styles.curlHeader}>
                            <Text style={styles.fieldLabel}>curl</Text>
                            <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(curl, `curl_${epId}`)}>
                              <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                            </TouchableOpacity>
                          </View>
                          <View style={styles.codeBlock}>
                            <Text style={styles.codeText} selectable>{curl}</Text>
                          </View>
                        </>
                      );
                    })()}
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPost(epId, buildUrl(), buildBody(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /phone/calls */}
          {(() => {
            const epId = 'phone_list_calls';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.status) p.set('status', inputs.status);
              if (inputs.direction) p.set('direction', inputs.direction);
              if (inputs.limit) p.set('limit', inputs.limit);
              if (inputs.offset) p.set('offset', inputs.offset);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/phone/calls${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/phone/calls</Text>
                    <Text style={styles.endpointDesc}>List calls</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Status <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="ringing | answered | completed | not-responded | cancelled | ended | failed" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'status')} onChangeText={v => setEpInput(epId, 'status', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Direction <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="inbound | outbound" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'direction')} onChangeText={v => setEpInput(epId, 'direction', v)} autoCapitalize="none" />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Offset</Text>
                        <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'offset')} onChangeText={v => setEpInput(epId, 'offset', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /phone/contacts/:contactId/calls */}
          {(() => {
            const epId = 'phone_calls_by_contact';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.status) p.set('status', inputs.status);
              if (inputs.direction) p.set('direction', inputs.direction);
              if (inputs.limit) p.set('limit', inputs.limit);
              if (inputs.offset) p.set('offset', inputs.offset);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/phone/contacts/${inputs.contactId || ':contactId'}/calls${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/phone/contacts/:contactId/calls</Text>
                    <Text style={styles.endpointDesc}>List calls for a contact</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Contact ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Status <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="ringing | answered | completed | not-responded | cancelled | ended | failed" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'status')} onChangeText={v => setEpInput(epId, 'status', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Direction <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="inbound | outbound" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'direction')} onChangeText={v => setEpInput(epId, 'direction', v)} autoCapitalize="none" />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Offset</Text>
                        <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'offset')} onChangeText={v => setEpInput(epId, 'offset', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /phone/users/:userId/calls */}
          {(() => {
            const epId = 'phone_calls_by_user';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.status) p.set('status', inputs.status);
              if (inputs.direction) p.set('direction', inputs.direction);
              if (inputs.limit) p.set('limit', inputs.limit);
              if (inputs.offset) p.set('offset', inputs.offset);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/phone/users/${inputs.userId || ':userId'}/calls${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/phone/users/:userId/calls</Text>
                    <Text style={styles.endpointDesc}>List calls for user(s)</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>User ID(s) <Text style={styles.fieldHint}>(comma-separated for multiple)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1,64f1a2b3c4d5e6f7a8b9c0d2" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'userId')} onChangeText={v => setEpInput(epId, 'userId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Status <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="ringing | answered | completed | not-responded | cancelled | ended | failed" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'status')} onChangeText={v => setEpInput(epId, 'status', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Direction <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="inbound | outbound" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'direction')} onChangeText={v => setEpInput(epId, 'direction', v)} autoCapitalize="none" />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Offset</Text>
                        <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'offset')} onChangeText={v => setEpInput(epId, 'offset', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /phone/departments/:departmentId/calls */}
          {(() => {
            const epId = 'phone_calls_by_dept';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.status) p.set('status', inputs.status);
              if (inputs.direction) p.set('direction', inputs.direction);
              if (inputs.limit) p.set('limit', inputs.limit);
              if (inputs.offset) p.set('offset', inputs.offset);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/phone/departments/${inputs.departmentId || ':departmentId'}/calls${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/phone/departments/:departmentId/calls</Text>
                    <Text style={styles.endpointDesc}>List calls for department(s)</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Resolves each department's current members and returns calls assigned
                      to any of them — calls aren't tagged with a department directly.
                    </Text>
                    <Text style={styles.fieldLabel}>Department ID(s) <Text style={styles.fieldHint}>(comma-separated for multiple)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1,64f1a2b3c4d5e6f7a8b9c0d2" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'departmentId')} onChangeText={v => setEpInput(epId, 'departmentId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Status <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="ringing | answered | completed | not-responded | cancelled | ended | failed" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'status')} onChangeText={v => setEpInput(epId, 'status', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Direction <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="inbound | outbound" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'direction')} onChangeText={v => setEpInput(epId, 'direction', v)} autoCapitalize="none" />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Offset</Text>
                        <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'offset')} onChangeText={v => setEpInput(epId, 'offset', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /phone/calls/:id */}
          {(() => {
            const epId = 'phone_get_call';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/phone/calls/${inputs.callId || ':id'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/phone/calls/:id</Text>
                    <Text style={styles.endpointDesc}>Get a call</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Call ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'callId')} onChangeText={v => setEpInput(epId, 'callId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* PATCH /phone/calls/:id */}
          {(() => {
            const epId = 'phone_update_call';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const inputs = epInputs[epId] || {};
            const buildUrl = (i: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/phone/calls/${i.callId || ':id'}`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, any> = {};
              if (i.status) body.status = i.status;
              if (i.duration) body.duration = Number(i.duration);
              if (i.startedAt) body.startedAt = i.startedAt;
              if (i.endedAt) body.endedAt = i.endedAt;
              if (i.recordingUrl) body.recordingUrl = i.recordingUrl;
              return body;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePatch]}><Text style={styles.methodText}>PAT</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/phone/calls/:id</Text>
                    <Text style={styles.endpointDesc}>Update call status</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Reports a status change for a call already tracked (e.g. answered,
                      completed, not-responded). Triggers the call.status_changed webhook
                      when status is included.
                    </Text>
                    <Text style={styles.fieldLabel}>Call ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'callId')} onChangeText={v => setEpInput(epId, 'callId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Status <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="completed" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'status')} onChangeText={v => setEpInput(epId, 'status', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Duration (seconds) <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="120" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'duration')} onChangeText={v => setEpInput(epId, 'duration', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Started At <Text style={styles.fieldHint}>(optional, ISO timestamp)</Text></Text>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.xs }]}>
                      Corrects the logged start time — set to the call's creation time by
                      default, since the real answer/response time is often only known once
                      the call is later reported as answered.
                    </Text>
                    <TextInput style={styles.input} placeholder="2026-01-01T12:00:00.000Z" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'startedAt')} onChangeText={v => setEpInput(epId, 'startedAt', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Ended At <Text style={styles.fieldHint}>(optional, ISO timestamp)</Text></Text>
                    <TextInput style={styles.input} placeholder="2026-01-01T12:00:00.000Z" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'endedAt')} onChangeText={v => setEpInput(epId, 'endedAt', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Recording URL <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.xs }]}>
                      This app can't fetch a recording for a call it didn't place through its
                      own dialer, so report one here if you have it — your own hosted copy, or
                      a URL you resolved on your end.
                    </Text>
                    <TextInput style={styles.input} placeholder="https://example.com/recordings/call-123.mp3" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'recordingUrl')} onChangeText={v => setEpInput(epId, 'recordingUrl', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(
                        `curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`,
                        `curl_${epId}`
                      )}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPatch(epId, buildUrl(inputs), buildBody(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* POST /phone/calls/:id/notes */}
          {(() => {
            const epId = 'phone_add_note';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/phone/calls/${inputs.callId || ':id'}/notes`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/phone/calls/:id/notes</Text>
                    <Text style={styles.endpointDesc}>Add a note to a call</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Call ID <Text style={styles.fieldHint}>(path param, required)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'callId')} onChangeText={v => setEpInput(epId, 'callId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Note Text <Text style={styles.fieldHint}>(body, required)</Text></Text>
                    <TextInput style={styles.input} placeholder="Customer requested a callback tomorrow" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'text')} onChangeText={v => setEpInput(epId, 'text', v)} />
                    {(() => {
                      const inputs = epInputs[epId] || {};
                      const body: any = {};
                      if (inputs.text) body.text = inputs.text;
                      const key = playRawKey || playRawKeyInput.trim();
                      const curl = `curl -X POST ${buildUrl(inputs)} \\\n  -H "Authorization: Bearer ${key || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body)}'`;
                      return (
                        <>
                          <View style={styles.curlHeader}>
                            <Text style={styles.fieldLabel}>curl</Text>
                            <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(curl, `curl_${epId}`)}>
                              <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                            </TouchableOpacity>
                          </View>
                          <View style={styles.codeBlock}>
                            <Text style={styles.codeText} selectable>{curl}</Text>
                          </View>
                        </>
                      );
                    })()}
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => {
                        const inputs = epInputs[epId] || {};
                        const body: any = {};
                        if (inputs.text) body.text = inputs.text;
                        handleEpPost(epId, buildUrl(inputs), body);
                      }} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /phone/calls/:id/notes */}
          {(() => {
            const epId = 'phone_list_notes';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/phone/calls/${inputs.callId || ':id'}/notes`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/phone/calls/:id/notes</Text>
                    <Text style={styles.endpointDesc}>List notes on a call</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Call ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'callId')} onChangeText={v => setEpInput(epId, 'callId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}
        </View>

        {/* ── Catalog Endpoints group ── */}
        <View style={[styles.card, groupStyle('catalog')]}>
          <View style={styles.epGroupHeader}>
            <Ionicons name="albums-outline" size={16} color={colors.primary as string} />
            <Text style={styles.epGroupTitle}>Catalog Endpoints</Text>
          </View>
          <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
            Manage a business's product catalogs — the same catalogs used for
            WhatsApp/Facebook commerce channel sync. Pass a Data List id via
            "dataListId" on list requests (Products group) to scope by
            membership in a System Datalist.
          </Text>

          {/* GET /catalogs */}
          {(() => {
            const epId = 'catalog_list';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.search) p.set('search', inputs.search);
              if (inputs.isActive) p.set('isActive', inputs.isActive);
              if (inputs.dataListId) p.set('dataListId', inputs.dataListId);
              if (inputs.limit) p.set('limit', inputs.limit);
              if (inputs.skip) p.set('skip', inputs.skip);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/catalogs${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/catalogs</Text>
                    <Text style={styles.endpointDesc}>List catalogs</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Search <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="summer collection" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'search')} onChangeText={v => setEpInput(epId, 'search', v)} />
                    <Text style={styles.fieldLabel}>Active Only <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="true | false" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'isActive')} onChangeText={v => setEpInput(epId, 'isActive', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Data List ID <Text style={styles.fieldHint}>(optional — scope to a System Datalist's membership)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'dataListId')} onChangeText={v => setEpInput(epId, 'dataListId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Skip</Text>
                        <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'skip')} onChangeText={v => setEpInput(epId, 'skip', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /catalogs/:id */}
          {(() => {
            const epId = 'catalog_get';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/catalogs/${inputs.catalogId || ':id'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/catalogs/:id</Text>
                    <Text style={styles.endpointDesc}>Get a catalog</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Catalog ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'catalogId')} onChangeText={v => setEpInput(epId, 'catalogId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* POST /catalogs */}
          {(() => {
            const epId = 'catalog_create';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = () => `${CODE_BASE_URL}/api/v1/catalogs`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, any> = {};
              if (i.name) body.name = i.name;
              if (i.description) body.description = i.description;
              if (i.currency) body.currency = i.currency;
              if (i.locale) body.locale = i.locale;
              if (i.createdAt) body.createdAt = i.createdAt;
              if (i.isActive) body.isActive = i.isActive === 'true';
              return body;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/catalogs</Text>
                    <Text style={styles.endpointDesc}>Create a catalog</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Name <Text style={styles.fieldHint}>(required)</Text></Text>
                    <TextInput style={styles.input} placeholder="Summer Collection" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'name')} onChangeText={v => setEpInput(epId, 'name', v)} />
                    <Text style={styles.fieldLabel}>Description <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="Seasonal items" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'description')} onChangeText={v => setEpInput(epId, 'description', v)} />
                    <Text style={styles.fieldLabel}>Currency <Text style={styles.fieldHint}>(optional, defaults to USD)</Text></Text>
                    <TextInput style={styles.input} placeholder="USD" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'currency')} onChangeText={v => setEpInput(epId, 'currency', v)} autoCapitalize="characters" />
                    <Text style={styles.fieldLabel}>Locale <Text style={styles.fieldHint}>(optional, defaults to en_US)</Text></Text>
                    <TextInput style={styles.input} placeholder="en_US" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'locale')} onChangeText={v => setEpInput(epId, 'locale', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Active <Text style={styles.fieldHint}>(optional, defaults to true)</Text></Text>
                    <TextInput style={styles.input} placeholder="true | false" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'isActive')} onChangeText={v => setEpInput(epId, 'isActive', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Created At <Text style={styles.fieldHint}>(optional — original date from your system, ISO-8601 or Unix timestamp; defaults to now)</Text></Text>
                    <TextInput style={styles.input} placeholder="2024-03-01T10:30:00Z" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'createdAt')} onChangeText={v => setEpInput(epId, 'createdAt', v)} autoCapitalize="none" autoCorrect={false} />
                    {(() => {
                      const inputs = epInputs[epId] || {};
                      const key = playRawKey || playRawKeyInput.trim();
                      const curl = `curl -X POST "${buildUrl()}" \\\n  -H "Authorization: Bearer ${key || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`;
                      return (
                        <>
                          <View style={styles.curlHeader}>
                            <Text style={styles.fieldLabel}>curl</Text>
                            <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(curl, `curl_${epId}`)}>
                              <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                            </TouchableOpacity>
                          </View>
                          <View style={styles.codeBlock}>
                            <Text style={styles.codeText} selectable>{curl}</Text>
                          </View>
                        </>
                      );
                    })()}
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPost(epId, buildUrl(), buildBody(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* PATCH /catalogs/:id */}
          {(() => {
            const epId = 'catalog_update';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const inputs = epInputs[epId] || {};
            const buildUrl = (i: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/catalogs/${i.catalogId || ':id'}`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, any> = {};
              if (i.name) body.name = i.name;
              if (i.description) body.description = i.description;
              if (i.currency) body.currency = i.currency;
              if (i.locale) body.locale = i.locale;
              if (i.createdAt) body.createdAt = i.createdAt;
              if (i.isActive) body.isActive = i.isActive === 'true';
              return body;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePatch]}><Text style={styles.methodText}>PAT</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/catalogs/:id</Text>
                    <Text style={styles.endpointDesc}>Update a catalog</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Catalog ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'catalogId')} onChangeText={v => setEpInput(epId, 'catalogId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Name <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="Summer Collection" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'name')} onChangeText={v => setEpInput(epId, 'name', v)} />
                    <Text style={styles.fieldLabel}>Description <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="Seasonal items" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'description')} onChangeText={v => setEpInput(epId, 'description', v)} />
                    <Text style={styles.fieldLabel}>Currency <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="USD" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'currency')} onChangeText={v => setEpInput(epId, 'currency', v)} autoCapitalize="characters" />
                    <Text style={styles.fieldLabel}>Active <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="true | false" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'isActive')} onChangeText={v => setEpInput(epId, 'isActive', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Created At <Text style={styles.fieldHint}>(optional — original date from your system, ISO-8601 or Unix timestamp; defaults to now)</Text></Text>
                    <TextInput style={styles.input} placeholder="2024-03-01T10:30:00Z" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'createdAt')} onChangeText={v => setEpInput(epId, 'createdAt', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(
                        `curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`,
                        `curl_${epId}`
                      )}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPatch(epId, buildUrl(inputs), buildBody(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* DELETE /catalogs/:id */}
          {(() => {
            const epId = 'catalog_delete';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/catalogs/${inputs.catalogId || ':id'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeDelete]}><Text style={styles.methodText}>DEL</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/catalogs/:id</Text>
                    <Text style={styles.endpointDesc}>Delete a catalog</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Fails if the catalog still contains products — remove them first.
                    </Text>
                    <Text style={styles.fieldLabel}>Catalog ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'catalogId')} onChangeText={v => setEpInput(epId, 'catalogId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpDelete(epId, buildUrl(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* POST /catalogs/:id/products */}
          {(() => {
            const epId = 'catalog_add_product';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/catalogs/${inputs.catalogId || ':id'}/products`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, any> = {};
              if (i.productId) body.productId = i.productId;
              return body;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/catalogs/:id/products</Text>
                    <Text style={styles.endpointDesc}>Add a product to a catalog</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Catalog ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'catalogId')} onChangeText={v => setEpInput(epId, 'catalogId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Product ID <Text style={styles.fieldHint}>(required)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'productId')} onChangeText={v => setEpInput(epId, 'productId', v)} autoCapitalize="none" autoCorrect={false} />
                    {(() => {
                      const inputs = epInputs[epId] || {};
                      const key = playRawKey || playRawKeyInput.trim();
                      const curl = `curl -X POST "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${key || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`;
                      return (
                        <>
                          <View style={styles.curlHeader}>
                            <Text style={styles.fieldLabel}>curl</Text>
                            <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(curl, `curl_${epId}`)}>
                              <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                            </TouchableOpacity>
                          </View>
                          <View style={styles.codeBlock}>
                            <Text style={styles.codeText} selectable>{curl}</Text>
                          </View>
                        </>
                      );
                    })()}
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPost(epId, buildUrl(epInputs[epId] || {}), buildBody(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* DELETE /catalogs/:id/products/:productId */}
          {(() => {
            const epId = 'catalog_remove_product';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/catalogs/${inputs.catalogId || ':id'}/products/${inputs.productId || ':productId'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeDelete]}><Text style={styles.methodText}>DEL</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/catalogs/:id/products/:productId</Text>
                    <Text style={styles.endpointDesc}>Remove a product from a catalog</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Catalog ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'catalogId')} onChangeText={v => setEpInput(epId, 'catalogId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Product ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'productId')} onChangeText={v => setEpInput(epId, 'productId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpDelete(epId, buildUrl(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}
        </View>

        {/* ── Product Endpoints group ── */}
        <View style={[styles.card, groupStyle('product')]}>
          <View style={styles.epGroupHeader}>
            <Ionicons name="cube-outline" size={16} color={colors.primary as string} />
            <Text style={styles.epGroupTitle}>Product Endpoints</Text>
          </View>
          <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
            Manage a business's products. Filter the list by "catalogueId" to
            see one catalog's products, or "dataListId" to scope by membership
            in a System Datalist.
          </Text>
          {/* GET /products */}
          {(() => {
            const epId = 'product_list';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.catalogueId) p.set('catalogueId', inputs.catalogueId);
              if (inputs.dataListId) p.set('dataListId', inputs.dataListId);
              if (inputs.search) p.set('search', inputs.search);
              if (inputs.limit) p.set('limit', inputs.limit);
              if (inputs.skip) p.set('skip', inputs.skip);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/products${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/products</Text>
                    <Text style={styles.endpointDesc}>List products</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Catalog ID <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'catalogueId')} onChangeText={v => setEpInput(epId, 'catalogueId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Data List ID <Text style={styles.fieldHint}>(optional — scope to a System Datalist's membership)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'dataListId')} onChangeText={v => setEpInput(epId, 'dataListId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Search <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="t-shirt" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'search')} onChangeText={v => setEpInput(epId, 'search', v)} />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Skip</Text>
                        <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'skip')} onChangeText={v => setEpInput(epId, 'skip', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /products/:id */}
          {(() => {
            const epId = 'product_get';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/products/${inputs.productId || ':id'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/products/:id</Text>
                    <Text style={styles.endpointDesc}>Get a product</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Product ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'productId')} onChangeText={v => setEpInput(epId, 'productId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* POST /products */}
          {(() => {
            const epId = 'product_create';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = () => `${CODE_BASE_URL}/api/v1/products`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, any> = {};
              if (i.name) body.name = i.name;
              if (i.price) body.price = Number(i.price);
              if (i.currency) body.currency = i.currency;
              if (i.sku) body.sku = i.sku;
              if (i.brand) body.brand = i.brand;
              if (i.description) body.description = i.description;
              if (i.minimumOrderQuantity) body.minimumOrderQuantity = Number(i.minimumOrderQuantity);
              if (i.createdAt) body.createdAt = i.createdAt;
              if (i.catalogueIds) body.catalogueIds = i.catalogueIds.split(',').map(s => s.trim()).filter(Boolean);
              return body;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/products</Text>
                    <Text style={styles.endpointDesc}>Create a product</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Name <Text style={styles.fieldHint}>(required)</Text></Text>
                    <TextInput style={styles.input} placeholder="Classic T-Shirt" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'name')} onChangeText={v => setEpInput(epId, 'name', v)} />
                    <Text style={styles.fieldLabel}>Price <Text style={styles.fieldHint}>(required)</Text></Text>
                    <TextInput style={styles.input} placeholder="19.99" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'price')} onChangeText={v => setEpInput(epId, 'price', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Currency <Text style={styles.fieldHint}>(optional, defaults to USD)</Text></Text>
                    <TextInput style={styles.input} placeholder="USD" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'currency')} onChangeText={v => setEpInput(epId, 'currency', v)} autoCapitalize="characters" />
                    <Text style={styles.fieldLabel}>SKU <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="TSHIRT-001" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'sku')} onChangeText={v => setEpInput(epId, 'sku', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Minimum Order Quantity <Text style={styles.fieldHint}>(optional — orders must include at least this many, all variants together; 0/null clears)</Text></Text>
                    <TextInput style={styles.input} placeholder="3" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'minimumOrderQuantity')} onChangeText={v => setEpInput(epId, 'minimumOrderQuantity', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Created At <Text style={styles.fieldHint}>(optional — original date from your system, ISO-8601 or Unix timestamp; defaults to now)</Text></Text>
                    <TextInput style={styles.input} placeholder="2024-03-01T10:30:00Z" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'createdAt')} onChangeText={v => setEpInput(epId, 'createdAt', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Brand <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="Acme" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'brand')} onChangeText={v => setEpInput(epId, 'brand', v)} />
                    <Text style={styles.fieldLabel}>Description <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="100% cotton" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'description')} onChangeText={v => setEpInput(epId, 'description', v)} />
                    <Text style={styles.fieldLabel}>Catalog IDs <Text style={styles.fieldHint}>(optional, comma-separated)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1, 64f1a2b3c4d5e6f7a8b9c0d2" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'catalogueIds')} onChangeText={v => setEpInput(epId, 'catalogueIds', v)} autoCapitalize="none" autoCorrect={false} />
                    {(() => {
                      const inputs = epInputs[epId] || {};
                      const key = playRawKey || playRawKeyInput.trim();
                      const curl = `curl -X POST "${buildUrl()}" \\\n  -H "Authorization: Bearer ${key || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`;
                      return (
                        <>
                          <View style={styles.curlHeader}>
                            <Text style={styles.fieldLabel}>curl</Text>
                            <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(curl, `curl_${epId}`)}>
                              <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                            </TouchableOpacity>
                          </View>
                          <View style={styles.codeBlock}>
                            <Text style={styles.codeText} selectable>{curl}</Text>
                          </View>
                        </>
                      );
                    })()}
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPost(epId, buildUrl(), buildBody(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* PATCH /products/:id */}
          {(() => {
            const epId = 'product_update';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const inputs = epInputs[epId] || {};
            const buildUrl = (i: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/products/${i.productId || ':id'}`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, any> = {};
              if (i.name) body.name = i.name;
              if (i.price) body.price = Number(i.price);
              if (i.currency) body.currency = i.currency;
              if (i.sku) body.sku = i.sku;
              if (i.brand) body.brand = i.brand;
              if (i.description) body.description = i.description;
              if (i.minimumOrderQuantity) body.minimumOrderQuantity = Number(i.minimumOrderQuantity);
              if (i.createdAt) body.createdAt = i.createdAt;
              return body;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePatch]}><Text style={styles.methodText}>PAT</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/products/:id</Text>
                    <Text style={styles.endpointDesc}>Update a product</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Product ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'productId')} onChangeText={v => setEpInput(epId, 'productId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Name <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="Classic T-Shirt" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'name')} onChangeText={v => setEpInput(epId, 'name', v)} />
                    <Text style={styles.fieldLabel}>Price <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="19.99" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'price')} onChangeText={v => setEpInput(epId, 'price', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>SKU <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="TSHIRT-001" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'sku')} onChangeText={v => setEpInput(epId, 'sku', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Minimum Order Quantity <Text style={styles.fieldHint}>(optional — orders must include at least this many, all variants together; 0/null clears)</Text></Text>
                    <TextInput style={styles.input} placeholder="3" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'minimumOrderQuantity')} onChangeText={v => setEpInput(epId, 'minimumOrderQuantity', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Created At <Text style={styles.fieldHint}>(optional — original date from your system, ISO-8601 or Unix timestamp; defaults to now)</Text></Text>
                    <TextInput style={styles.input} placeholder="2024-03-01T10:30:00Z" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'createdAt')} onChangeText={v => setEpInput(epId, 'createdAt', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Brand <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="Acme" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'brand')} onChangeText={v => setEpInput(epId, 'brand', v)} />
                    <Text style={styles.fieldLabel}>Description <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="100% cotton" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'description')} onChangeText={v => setEpInput(epId, 'description', v)} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(
                        `curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`,
                        `curl_${epId}`
                      )}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPatch(epId, buildUrl(inputs), buildBody(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* DELETE /products/:id */}
          {(() => {
            const epId = 'product_delete';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/products/${inputs.productId || ':id'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeDelete]}><Text style={styles.methodText}>DEL</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/products/:id</Text>
                    <Text style={styles.endpointDesc}>Delete a product</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Product ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'productId')} onChangeText={v => setEpInput(epId, 'productId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpDelete(epId, buildUrl(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}
        </View>

        {/* ── Order Endpoints group ── */}
        <View style={[styles.card, groupStyle('order')]}>
          <View style={styles.epGroupHeader}>
            <Ionicons name="receipt-outline" size={16} color={colors.primary as string} />
            <Text style={styles.epGroupTitle}>Order Endpoints</Text>
          </View>
          <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
            Manage a business's orders. No delete route — cancel an order via
            PATCH with orderStatus set to "cancelled" (restores any deducted
            inventory automatically).
          </Text>
          {/* GET /orders */}
          {(() => {
            const epId = 'order_list';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.contactId) p.set('contactId', inputs.contactId);
              if (inputs.status) p.set('status', inputs.status);
              if (inputs.dataListId) p.set('dataListId', inputs.dataListId);
              if (inputs.limit) p.set('limit', inputs.limit);
              if (inputs.offset) p.set('offset', inputs.offset);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/orders${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/orders</Text>
                    <Text style={styles.endpointDesc}>List orders</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Contact ID <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Status <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="pending | confirmed | shipped | delivered | cancelled" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'status')} onChangeText={v => setEpInput(epId, 'status', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Data List ID <Text style={styles.fieldHint}>(optional — scope to a System Datalist's membership)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'dataListId')} onChangeText={v => setEpInput(epId, 'dataListId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Offset</Text>
                        <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'offset')} onChangeText={v => setEpInput(epId, 'offset', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /orders/by-number/:orderNumber */}
          {(() => {
            const epId = 'order_get_by_number';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/orders/by-number/${inputs.orderNumber || ':orderNumber'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/orders/by-number/:orderNumber</Text>
                    <Text style={styles.endpointDesc}>Get an order by order number</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Order Number</Text>
                    <TextInput style={styles.input} placeholder="ORD-2026-00042" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'orderNumber')} onChangeText={v => setEpInput(epId, 'orderNumber', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /orders/:id */}
          {(() => {
            const epId = 'order_get';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/orders/${inputs.orderId || ':id'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/orders/:id</Text>
                    <Text style={styles.endpointDesc}>Get an order</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Order ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'orderId')} onChangeText={v => setEpInput(epId, 'orderId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* POST /orders */}
          {(() => {
            const epId = 'order_create';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = () => `${CODE_BASE_URL}/api/v1/orders`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, any> = {};
              if (i.contactId) body.contactId = i.contactId;
              if (i.phoneNumber) body.phoneNumber = i.phoneNumber;
              if (i.contactName) body.contactName = i.contactName;
              if (i.email) body.email = i.email;
              if (i.items) { try { body.items = JSON.parse(i.items); } catch { /* leave unset on invalid JSON */ } }
              if (i.currency) body.currency = i.currency;
              if (i.tax) body.tax = Number(i.tax);
              if (i.shipping) body.shipping = Number(i.shipping);
              if (i.discount) body.discount = Number(i.discount);
              if (i.orderDate) body.orderDate = i.orderDate;
              if (i.createdAt) body.createdAt = i.createdAt;
              return body;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/orders</Text>
                    <Text style={styles.endpointDesc}>Create an order</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Send WHO and WHAT — the platform looks up (or creates) the
                      contact and looks up each product server-side, computing
                      the full order snapshot (product name/price, unitPrice,
                      totalPrice, subtotal, totalPrice) automatically. You never
                      need to send those computed fields yourself.
                    </Text>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Minimums are enforced: every product must reach its
                      minimumOrderQuantity (all variants together), and the items
                      of each vendor must add up to that vendor's
                      minimumOrderAmount (when in the same currency). Otherwise
                      the request fails with 400 and errors[0].message listing
                      what's missing, plus data.minimums with the details.
                    </Text>
                    <Text style={styles.fieldLabel}>Contact ID <Text style={styles.fieldHint}>(use this OR Phone Number below)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1 — an existing contact's id" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Phone Number <Text style={styles.fieldHint}>(found or auto-created if no Contact ID given)</Text></Text>
                    <TextInput style={styles.input} placeholder="+201023356631" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'phoneNumber')} onChangeText={v => setEpInput(epId, 'phoneNumber', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Contact Name <Text style={styles.fieldHint}>(optional — defaults to the phone number if omitted)</Text></Text>
                    <TextInput style={styles.input} placeholder="Jane Doe" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactName')} onChangeText={v => setEpInput(epId, 'contactName', v)} autoCapitalize="words" />
                    <Text style={styles.fieldLabel}>Email <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="jane@example.com" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'email')} onChangeText={v => setEpInput(epId, 'email', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Items <Text style={styles.fieldHint}>(required, JSON array — each item needs productId and/or sku, plus quantity)</Text></Text>
                    <TextInput style={styles.input} placeholder='[{"productId":"64f1a2b3c4d5e6f7a8b9c0d1","quantity":2},{"sku":"MUG-BLUE-01","quantity":1}]' placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'items')} onChangeText={v => setEpInput(epId, 'items', v)} multiline numberOfLines={3} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Currency <Text style={styles.fieldHint}>(optional — defaults to USD)</Text></Text>
                    <TextInput style={styles.input} placeholder="USD" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'currency')} onChangeText={v => setEpInput(epId, 'currency', v)} autoCapitalize="characters" />
                    <Text style={styles.fieldLabel}>Tax <Text style={styles.fieldHint}>(optional — flat amount, added to the total)</Text></Text>
                    <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'tax')} onChangeText={v => setEpInput(epId, 'tax', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Shipping <Text style={styles.fieldHint}>(optional — flat amount, added to the total)</Text></Text>
                    <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'shipping')} onChangeText={v => setEpInput(epId, 'shipping', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Discount <Text style={styles.fieldHint}>(optional — flat amount, subtracted from the total)</Text></Text>
                    <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'discount')} onChangeText={v => setEpInput(epId, 'discount', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Order Date <Text style={styles.fieldHint}>(optional — original date from your system, ISO-8601 or Unix timestamp; defaults to now)</Text></Text>
                    <TextInput style={styles.input} placeholder="2024-03-01T10:30:00Z" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'orderDate')} onChangeText={v => setEpInput(epId, 'orderDate', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Created At <Text style={styles.fieldHint}>(optional — original date from your system, ISO-8601 or Unix timestamp; defaults to now)</Text></Text>
                    <TextInput style={styles.input} placeholder="2024-03-01T10:30:00Z" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'createdAt')} onChangeText={v => setEpInput(epId, 'createdAt', v)} autoCapitalize="none" autoCorrect={false} />
                    {(() => {
                      const inputs = epInputs[epId] || {};
                      const key = playRawKey || playRawKeyInput.trim();
                      const curl = `curl -X POST "${buildUrl()}" \\\n  -H "Authorization: Bearer ${key || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`;
                      return (
                        <>
                          <View style={styles.curlHeader}>
                            <Text style={styles.fieldLabel}>curl</Text>
                            <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(curl, `curl_${epId}`)}>
                              <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                            </TouchableOpacity>
                          </View>
                          <View style={styles.codeBlock}>
                            <Text style={styles.codeText} selectable>{curl}</Text>
                          </View>
                        </>
                      );
                    })()}
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPost(epId, buildUrl(), buildBody(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* PATCH /orders/:id */}
          {(() => {
            const epId = 'order_update';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const inputs = epInputs[epId] || {};
            const buildUrl = (i: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/orders/${i.orderId || ':id'}`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, any> = {};
              if (i.orderStatus) body.orderStatus = i.orderStatus;
              if (i.paymentStatus) body.paymentStatus = i.paymentStatus;
              if (i.trackingNumber) body.trackingNumber = i.trackingNumber;
              if (i.cancelledReason) body.cancelledReason = i.cancelledReason;
              if (i.notes) body.notes = i.notes;
              return body;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePatch]}><Text style={styles.methodText}>PAT</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/orders/:id</Text>
                    <Text style={styles.endpointDesc}>Update an order (status, tracking, cancellation)</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Triggers order.updated (full order with complete product
                      details per item), plus order.status_changed when the
                      status changes (or order.cancelled, when orderStatus is
                      "cancelled" — automatically restores any inventory that was
                      deducted for this order).
                    </Text>
                    <Text style={styles.fieldLabel}>Order ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'orderId')} onChangeText={v => setEpInput(epId, 'orderId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Order Status <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="confirmed | shipped | delivered | cancelled" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'orderStatus')} onChangeText={v => setEpInput(epId, 'orderStatus', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Payment Status <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="paid | pending | refunded" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'paymentStatus')} onChangeText={v => setEpInput(epId, 'paymentStatus', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Tracking Number <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="1Z999AA10123456784" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'trackingNumber')} onChangeText={v => setEpInput(epId, 'trackingNumber', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Cancelled Reason <Text style={styles.fieldHint}>(optional, when cancelling)</Text></Text>
                    <TextInput style={styles.input} placeholder="Customer requested cancellation" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'cancelledReason')} onChangeText={v => setEpInput(epId, 'cancelledReason', v)} />
                    <Text style={styles.fieldLabel}>Notes <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="Left at front desk" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'notes')} onChangeText={v => setEpInput(epId, 'notes', v)} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(
                        `curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`,
                        `curl_${epId}`
                      )}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPatch(epId, buildUrl(inputs), buildBody(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}
        </View>

        {/* ── Vendor Endpoints group ── */}
        <View style={[styles.card, groupStyle('vendor')]}>
          <View style={styles.epGroupHeader}>
            <Ionicons name="business-outline" size={16} color={colors.primary as string} />
            <Text style={styles.epGroupTitle}>Vendor Endpoints</Text>
          </View>
          <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
            Manage a business's vendors and which products each one supplies
            (many-to-many — a product can be linked to multiple vendors).
          </Text>
          {([
            { id: 'vendor_list', method: 'GET' as const, path: '/api/v1/vendors', desc: 'List vendors', fields: [] as const },
            { id: 'vendor_get', method: 'GET' as const, path: '/api/v1/vendors/:id', desc: 'Get a vendor', fields: ['id'] as const },
            { id: 'vendor_create', method: 'POST' as const, path: '/api/v1/vendors', desc: 'Create a vendor', fields: ['name', 'phone', 'email', 'address', 'notes', 'crNumber', 'vatNumber', 'minimumOrderAmount', 'minimumOrderCurrency', 'createdAt'] as const },
            { id: 'vendor_update', method: 'PATCH' as const, path: '/api/v1/vendors/:id', desc: 'Update a vendor', fields: ['id', 'name', 'phone', 'email', 'address', 'notes', 'crNumber', 'vatNumber', 'minimumOrderAmount', 'minimumOrderCurrency', 'createdAt'] as const },
            { id: 'vendor_delete', method: 'DELETE' as const, path: '/api/v1/vendors/:id', desc: 'Delete a vendor', fields: ['id'] as const },
          ]).map((endpoint) => {
            const epId = endpoint.id;
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => `${CODE_BASE_URL}${endpoint.path.replace(':id', inputs.id || ':id')}`;
            const buildBody = (inputs: Record<string, string>) => {
              const body: Record<string, any> = {};
              for (const f of endpoint.fields) {
                if (f === 'id') continue;
                if ((inputs as any)[f]) body[f] = f === 'minimumOrderAmount' ? Number((inputs as any)[f]) : (inputs as any)[f];
              }
              return body;
            };
            const curl = () => {
              const inputs = epInputs[epId] || {};
              const key = rawKey || playRawKey || playRawKeyInput.trim();
              const method: string = endpoint.method;
              if (method === 'GET') return `curl "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${key || '<your_key>'}"`;
              if (method === 'DELETE') return `curl -X DELETE "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${key || '<your_key>'}"`;
              return `curl -X ${method} "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${key || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`;
            };
            const badgeStyle = endpoint.method === 'GET' ? styles.badgeGet : endpoint.method === 'DELETE' ? styles.badgeDelete : endpoint.method === 'PATCH' ? styles.badgePatch : styles.badgePost;
            return (
              <View key={epId} style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, badgeStyle]}><Text style={styles.methodText}>{endpoint.method === 'DELETE' ? 'DEL' : endpoint.method === 'PATCH' ? 'PAT' : endpoint.method}</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>{endpoint.path}</Text>
                    <Text style={styles.endpointDesc}>{endpoint.desc}</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    {(endpoint.fields as readonly string[]).includes('id' as any) && (
                      <>
                        <Text style={styles.fieldLabel}>Vendor ID</Text>
                        <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'id')} onChangeText={v => setEpInput(epId, 'id', v)} autoCapitalize="none" autoCorrect={false} />
                      </>
                    )}
                    {(endpoint.fields as readonly string[]).includes('name' as any) && (
                      <>
                        <Text style={styles.fieldLabel}>Name {endpoint.method === 'POST' ? '(required)' : '(optional)'}</Text>
                        <TextInput style={styles.input} placeholder="Acme Supplies Co." placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'name')} onChangeText={v => setEpInput(epId, 'name', v)} />
                      </>
                    )}
                    {(endpoint.fields as readonly string[]).includes('phone' as any) && (
                      <>
                        <Text style={styles.fieldLabel}>Phone <Text style={styles.fieldHint}>(optional)</Text></Text>
                        <TextInput style={styles.input} placeholder="+201023356631" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'phone')} onChangeText={v => setEpInput(epId, 'phone', v)} autoCapitalize="none" />
                      </>
                    )}
                    {(endpoint.fields as readonly string[]).includes('email' as any) && (
                      <>
                        <Text style={styles.fieldLabel}>Email <Text style={styles.fieldHint}>(optional)</Text></Text>
                        <TextInput style={styles.input} placeholder="vendor@example.com" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'email')} onChangeText={v => setEpInput(epId, 'email', v)} autoCapitalize="none" />
                      </>
                    )}
                    {(endpoint.fields as readonly string[]).includes('address' as any) && (
                      <>
                        <Text style={styles.fieldLabel}>Address <Text style={styles.fieldHint}>(optional)</Text></Text>
                        <TextInput style={styles.input} placeholder="123 Market St, Cairo" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'address')} onChangeText={v => setEpInput(epId, 'address', v)} />
                      </>
                    )}
                    {(endpoint.fields as readonly string[]).includes('notes' as any) && (
                      <>
                        <Text style={styles.fieldLabel}>Notes <Text style={styles.fieldHint}>(optional)</Text></Text>
                        <TextInput style={styles.input} placeholder="Preferred supplier for packaging" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'notes')} onChangeText={v => setEpInput(epId, 'notes', v)} />
                      </>
                    )}
                    {(endpoint.fields as readonly string[]).includes('crNumber' as any) && (
                      <>
                        <Text style={styles.fieldLabel}>CR Number <Text style={styles.fieldHint}>(optional)</Text></Text>
                        <TextInput style={styles.input} placeholder="1234567890" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'crNumber')} onChangeText={v => setEpInput(epId, 'crNumber', v)} autoCapitalize="none" />
                      </>
                    )}
                    {(endpoint.fields as readonly string[]).includes('vatNumber' as any) && (
                      <>
                        <Text style={styles.fieldLabel}>VAT Number <Text style={styles.fieldHint}>(optional)</Text></Text>
                        <TextInput style={styles.input} placeholder="300012345600003" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'vatNumber')} onChangeText={v => setEpInput(epId, 'vatNumber', v)} autoCapitalize="none" />
                      </>
                    )}
                    {(endpoint.fields as readonly string[]).includes('minimumOrderAmount' as any) && (
                      <>
                        <Text style={styles.fieldLabel}>Minimum Order Amount <Text style={styles.fieldHint}>(optional — orders must include at least this value of this vendor's products; 0/null clears)</Text></Text>
                        <TextInput style={styles.input} placeholder="500" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'minimumOrderAmount')} onChangeText={v => setEpInput(epId, 'minimumOrderAmount', v)} keyboardType="numeric" />
                        <Text style={styles.fieldLabel}>Minimum Order Currency <Text style={styles.fieldHint}>(optional, defaults to the business currency)</Text></Text>
                        <TextInput style={styles.input} placeholder="EGP" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'minimumOrderCurrency')} onChangeText={v => setEpInput(epId, 'minimumOrderCurrency', v)} autoCapitalize="characters" />
                      </>
                    )}
                    {(endpoint.fields as readonly string[]).includes('createdAt' as any) && (
                      <>
                        <Text style={styles.fieldLabel}>Created At <Text style={styles.fieldHint}>(optional — original date from your system, ISO-8601 or Unix timestamp; defaults to now)</Text></Text>
                        <TextInput style={styles.input} placeholder="2024-03-01T10:30:00Z" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'createdAt')} onChangeText={v => setEpInput(epId, 'createdAt', v)} autoCapitalize="none" autoCorrect={false} />
                      </>
                    )}
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(curl(), `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{curl()}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => {
                        const inputs = epInputs[epId] || {};
                        if (endpoint.method === 'GET') handleEpSend(epId, (i) => buildUrl(i));
                        else if (endpoint.method === 'POST') handleEpPost(epId, buildUrl(inputs), buildBody(inputs));
                        else if (endpoint.method === 'PATCH') handleEpPatch(epId, buildUrl(inputs), buildBody(inputs));
                        else handleEpDelete(epId, buildUrl(inputs));
                      }}
                      disabled={epSending[epId]}
                    >
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })}

          {/* POST /vendors/:id/products — link an existing product */}
          {(() => {
            const epId = 'vendor_add_product';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (i: Record<string, string>) => `${CODE_BASE_URL}/api/v1/vendors/${i.id || ':id'}/products`;
            const buildBody = (i: Record<string, string>) => ({ productId: i.productId });
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/vendors/:id/products</Text>
                    <Text style={styles.endpointDesc}>Link a product to a vendor</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Vendor ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'id')} onChangeText={v => setEpInput(epId, 'id', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Product ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d2" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'productId')} onChangeText={v => setEpInput(epId, 'productId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X POST "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(epInputs[epId] || {}))}'`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPost(epId, buildUrl(epInputs[epId] || {}), buildBody(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* DELETE /vendors/:id/products/:productId */}
          {(() => {
            const epId = 'vendor_remove_product';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (i: Record<string, string>) => `${CODE_BASE_URL}/api/v1/vendors/${i.id || ':id'}/products/${i.productId || ':productId'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeDelete]}><Text style={styles.methodText}>DEL</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/vendors/:id/products/:productId</Text>
                    <Text style={styles.endpointDesc}>Unlink a product from a vendor</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Vendor ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'id')} onChangeText={v => setEpInput(epId, 'id', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Product ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d2" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'productId')} onChangeText={v => setEpInput(epId, 'productId', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpDelete(epId, buildUrl(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}
        </View>

        {/* ── Quotation Endpoints group ── */}
        <View style={[styles.card, groupStyle('quotation')]}>
          <View style={styles.epGroupHeader}>
            <Ionicons name="document-text-outline" size={16} color={colors.primary as string} />
            <Text style={styles.epGroupTitle}>Quotation Endpoints</Text>
          </View>
          <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
            Manage a business's quotations — real products or custom line
            items, all prices fully overridable. Convert an accepted quotation
            to a real Order (preserving the quotation's prices exactly).
          </Text>

          {/* GET /quotations */}
          {(() => {
            const epId = 'quotation_list';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) => {
              const p = new URLSearchParams();
              if (inputs.contactId) p.set('contactId', inputs.contactId);
              if (inputs.status) p.set('status', inputs.status);
              if (inputs.dataListId) p.set('dataListId', inputs.dataListId);
              if (inputs.search) p.set('search', inputs.search);
              if (inputs.limit) p.set('limit', inputs.limit);
              if (inputs.offset) p.set('offset', inputs.offset);
              const qs = p.toString();
              return `${CODE_BASE_URL}/api/v1/quotations${qs ? '?' + qs : ''}`;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/quotations</Text>
                    <Text style={styles.endpointDesc}>List quotations</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Contact ID <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Status <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="draft | sent | accepted | rejected | expired | converted" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'status')} onChangeText={v => setEpInput(epId, 'status', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Data List ID <Text style={styles.fieldHint}>(optional — scope to a System Datalist's membership)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'dataListId')} onChangeText={v => setEpInput(epId, 'dataListId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Search <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="quotation number or customer name" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'search')} onChangeText={v => setEpInput(epId, 'search', v)} />
                    <View style={styles.inlineRow}>
                      <View style={{ flex: 1, marginRight: spacing.xs }}>
                        <Text style={styles.fieldLabel}>Limit</Text>
                        <TextInput style={styles.input} placeholder="20" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'limit')} onChangeText={v => setEpInput(epId, 'limit', v)} keyboardType="numeric" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.fieldLabel}>Offset</Text>
                        <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                          value={inp(epId, 'offset')} onChangeText={v => setEpInput(epId, 'offset', v)} keyboardType="numeric" />
                      </View>
                    </View>
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`, `curl_${epId}`)}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /quotations/:id */}
          {(() => {
            const epId = 'quotation_get';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (inputs: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/quotations/${inputs.id || ':id'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/quotations/:id</Text>
                    <Text style={styles.endpointDesc}>Get a quotation</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Quotation ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'id')} onChangeText={v => setEpInput(epId, 'id', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* POST /quotations */}
          {(() => {
            const epId = 'quotation_create';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = () => `${CODE_BASE_URL}/api/v1/quotations`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, any> = {};
              if (i.contactId) body.contactId = i.contactId;
              if (i.customerName) body.customer = { name: i.customerName, phone: i.customerPhone, email: i.customerEmail };
              if (i.items) { try { body.items = JSON.parse(i.items); } catch { /* leave unset on invalid JSON */ } }
              if (i.currency) body.currency = i.currency;
              if (i.tax) body.tax = Number(i.tax);
              if (i.shipping) body.shipping = Number(i.shipping);
              if (i.discount) body.discount = Number(i.discount);
              if (i.validUntil) body.validUntil = i.validUntil;
              if (i.templateId) body.templateId = i.templateId;
              if (i.createdAt) body.createdAt = i.createdAt;
              if (i.notes) body.notes = i.notes;
              return body;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/quotations</Text>
                    <Text style={styles.endpointDesc}>Create a quotation</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Each item may reference a real product (productId/sku) or
                      be a custom line with no catalog backing — set
                      productId to "external" and provide name/unitPrice
                      yourself. All prices (unitPrice, tax, shipping, discount,
                      totalPrice) may be freely overridden.
                    </Text>
                    <Text style={styles.fieldLabel}>Contact ID <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'contactId')} onChangeText={v => setEpInput(epId, 'contactId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Customer Name (required)</Text>
                    <TextInput style={styles.input} placeholder="Jane Doe" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'customerName')} onChangeText={v => setEpInput(epId, 'customerName', v)} autoCapitalize="words" />
                    <Text style={styles.fieldLabel}>Customer Phone (required)</Text>
                    <TextInput style={styles.input} placeholder="+201023356631" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'customerPhone')} onChangeText={v => setEpInput(epId, 'customerPhone', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Customer Email <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="jane@example.com" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'customerEmail')} onChangeText={v => setEpInput(epId, 'customerEmail', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Items <Text style={styles.fieldHint}>(required, JSON array)</Text></Text>
                    <TextInput style={styles.input} placeholder='[{"productId":"64f1a2b3c4d5e6f7a8b9c0d1","quantity":2,"unitPrice":45},{"productId":"external","name":"Custom Service","quantity":1,"unitPrice":200}]' placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'items')} onChangeText={v => setEpInput(epId, 'items', v)} multiline numberOfLines={3} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Currency <Text style={styles.fieldHint}>(optional — defaults to USD)</Text></Text>
                    <TextInput style={styles.input} placeholder="USD" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'currency')} onChangeText={v => setEpInput(epId, 'currency', v)} autoCapitalize="characters" />
                    <Text style={styles.fieldLabel}>Tax <Text style={styles.fieldHint}>(optional — flat amount)</Text></Text>
                    <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'tax')} onChangeText={v => setEpInput(epId, 'tax', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Shipping <Text style={styles.fieldHint}>(optional — flat amount)</Text></Text>
                    <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'shipping')} onChangeText={v => setEpInput(epId, 'shipping', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Discount <Text style={styles.fieldHint}>(optional — flat amount)</Text></Text>
                    <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'discount')} onChangeText={v => setEpInput(epId, 'discount', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Valid Until <Text style={styles.fieldHint}>(optional, ISO date)</Text></Text>
                    <TextInput style={styles.input} placeholder="2026-12-31" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'validUntil')} onChangeText={v => setEpInput(epId, 'validUntil', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Created At <Text style={styles.fieldHint}>(optional — original date from your system, ISO-8601 or Unix timestamp; defaults to now)</Text></Text>
                    <TextInput style={styles.input} placeholder="2024-03-01T10:30:00Z" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'createdAt')} onChangeText={v => setEpInput(epId, 'createdAt', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Template ID <Text style={styles.fieldHint}>(optional — defaults to the business's default quotation template)</Text></Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'templateId')} onChangeText={v => setEpInput(epId, 'templateId', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Notes <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="Valid for 30 days" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'notes')} onChangeText={v => setEpInput(epId, 'notes', v)} />
                    {(() => {
                      const inputs = epInputs[epId] || {};
                      const key = playRawKey || playRawKeyInput.trim();
                      const curl = `curl -X POST "${buildUrl()}" \\\n  -H "Authorization: Bearer ${key || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`;
                      return (
                        <>
                          <View style={styles.curlHeader}>
                            <Text style={styles.fieldLabel}>curl</Text>
                            <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(curl, `curl_${epId}`)}>
                              <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                            </TouchableOpacity>
                          </View>
                          <View style={styles.codeBlock}>
                            <Text style={styles.codeText} selectable>{curl}</Text>
                          </View>
                        </>
                      );
                    })()}
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPost(epId, buildUrl(), buildBody(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* PATCH /quotations/:id */}
          {(() => {
            const epId = 'quotation_update';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const inputs = epInputs[epId] || {};
            const buildUrl = (i: Record<string, string>) =>
              `${CODE_BASE_URL}/api/v1/quotations/${i.id || ':id'}`;
            const buildBody = (i: Record<string, string>) => {
              const body: Record<string, any> = {};
              if (i.status) body.quotationStatus = i.status;
              if (i.tax) body.tax = Number(i.tax);
              if (i.shipping) body.shipping = Number(i.shipping);
              if (i.discount) body.discount = Number(i.discount);
              if (i.notes) body.notes = i.notes;
              return body;
            };
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePatch]}><Text style={styles.methodText}>PAT</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/quotations/:id</Text>
                    <Text style={styles.endpointDesc}>Update a quotation (status, prices, notes)</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Quotation ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'id')} onChangeText={v => setEpInput(epId, 'id', v)} autoCapitalize="none" autoCorrect={false} />
                    <Text style={styles.fieldLabel}>Status <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="draft | sent | accepted | rejected | expired" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'status')} onChangeText={v => setEpInput(epId, 'status', v)} autoCapitalize="none" />
                    <Text style={styles.fieldLabel}>Tax <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'tax')} onChangeText={v => setEpInput(epId, 'tax', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Shipping <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'shipping')} onChangeText={v => setEpInput(epId, 'shipping', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Discount <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="0" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'discount')} onChangeText={v => setEpInput(epId, 'discount', v)} keyboardType="numeric" />
                    <Text style={styles.fieldLabel}>Notes <Text style={styles.fieldHint}>(optional)</Text></Text>
                    <TextInput style={styles.input} placeholder="Updated per customer request" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'notes')} onChangeText={v => setEpInput(epId, 'notes', v)} />
                    <View style={styles.curlHeader}>
                      <Text style={styles.fieldLabel}>curl</Text>
                      <TouchableOpacity style={styles.copyBtn} onPress={() => copyToClipboard(
                        `curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`,
                        `curl_${epId}`
                      )}>
                        <Ionicons name={copyConfirm === `curl_${epId}` ? 'checkmark' : 'copy-outline'} size={15} color={copyConfirm === `curl_${epId}` ? colors.success : colors.primary} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X PATCH "${buildUrl(inputs)}" \\\n  -H "Authorization: Bearer ${playRawKey || playRawKeyInput.trim() || '<your_key>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(buildBody(inputs))}'`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPatch(epId, buildUrl(inputs), buildBody(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* DELETE /quotations/:id */}
          {(() => {
            const epId = 'quotation_delete';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (i: Record<string, string>) => `${CODE_BASE_URL}/api/v1/quotations/${i.id || ':id'}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeDelete]}><Text style={styles.methodText}>DEL</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/quotations/:id</Text>
                    <Text style={styles.endpointDesc}>Delete a quotation</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={styles.fieldLabel}>Quotation ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'id')} onChangeText={v => setEpInput(epId, 'id', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X DELETE "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpDelete(epId, buildUrl(epInputs[epId] || {}))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* GET /quotations/:id/pdf */}
          {(() => {
            const epId = 'quotation_pdf';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (i: Record<string, string>) => `${CODE_BASE_URL}/api/v1/quotations/${i.id || ':id'}/pdf${i.regenerate === 'true' ? '?regenerate=true' : ''}`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgeGet]}><Text style={styles.methodText}>GET</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/quotations/:id/pdf</Text>
                    <Text style={styles.endpointDesc}>Generate/fetch quotation PDF</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Returns the existing pdfUrl if already generated; pass
                      regenerate=true to force a fresh render (e.g. after
                      editing the template).
                    </Text>
                    <Text style={styles.fieldLabel}>Quotation ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'id')} onChangeText={v => setEpInput(epId, 'id', v)} autoCapitalize="none" autoCorrect={false} />
                    <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginVertical: 4 }} onPress={() => setEpInput(epId, 'regenerate', inp(epId, 'regenerate') === 'true' ? 'false' : 'true')}>
                      <View style={{
                        width: 20, height: 20, borderRadius: 4,
                        borderWidth: 2, borderColor: inp(epId, 'regenerate') === 'true' ? colors.primary : colors.border,
                        backgroundColor: inp(epId, 'regenerate') === 'true' ? colors.primary : 'transparent',
                        alignItems: 'center', justifyContent: 'center',
                      }}>
                        {inp(epId, 'regenerate') === 'true' && <Ionicons name="checkmark" size={13} color="#fff" />}
                      </View>
                      <Text style={{ fontSize: 14, color: colors.text.primary }}>Force regenerate</Text>
                    </TouchableOpacity>
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpSend(epId, (inputs) => buildUrl(inputs))} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}

          {/* POST /quotations/:id/convert */}
          {(() => {
            const epId = 'quotation_convert';
            const isOpen = expandedEndpoint === epId;
            if (!epVisible(epId)) return null;
            const buildUrl = (i: Record<string, string>) => `${CODE_BASE_URL}/api/v1/quotations/${i.id || ':id'}/convert`;
            return (
              <View style={styles.epItem} ref={r => { epItemRefs.current[epId] = r; }}>
                <TouchableOpacity style={styles.epHeader} onPress={() => setExpandedEndpoint(isOpen ? null : epId)}>
                  <View style={[styles.methodBadge, styles.badgePost]}><Text style={styles.methodText}>POST</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.endpointPath} numberOfLines={1}>/api/v1/quotations/:id/convert</Text>
                    <Text style={styles.endpointDesc}>Convert quotation to an order</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.epBody}>
                    <Text style={[styles.fieldHint, { marginBottom: spacing.sm }]}>
                      Creates a real Order from the quotation's items and
                      customer, preserving the quotation's prices exactly
                      (not live product prices). Sets the quotation's status
                      to "converted" and links it to the new order.
                    </Text>
                    <Text style={styles.fieldLabel}>Quotation ID</Text>
                    <TextInput style={styles.input} placeholder="64f1a2b3c4d5e6f7a8b9c0d1" placeholderTextColor={colors.text.disabled}
                      value={inp(epId, 'id')} onChangeText={v => setEpInput(epId, 'id', v)} autoCapitalize="none" autoCorrect={false} />
                    <View style={styles.codeBlock}>
                      <Text style={styles.codeText} selectable>{`curl -X POST "${buildUrl(epInputs[epId] || {})}" \\\n  -H "Authorization: Bearer ${rawKey || '<your_key>'}"`}</Text>
                    </View>
                    <TouchableOpacity style={[styles.primaryBtn, epSending[epId] && styles.primaryBtnDisabled]}
                      onPress={() => handleEpPost(epId, buildUrl(epInputs[epId] || {}), {})} disabled={epSending[epId]}>
                      {epSending[epId] ? <ActivityIndicator size="small" color={colors.white} />
                        : <><Ionicons name="play" size={14} color={colors.white} /><Text style={styles.primaryBtnText}>Send Request</Text></>}
                    </TouchableOpacity>
                    {renderResponsePanel(epId)}
                  </View>
                )}
              </View>
            );
          })()}
        </View>

        {/* ── Response format reference ── */}
        <View style={styles.card}>
          <Text style={styles.sectionHeading}>X (Twitter) API Reference</Text>
          <Text style={styles.cardDesc}>
            X endpoints follow the same auth pattern as WhatsApp (Bearer key). Use these routes when your plan includes X service.
          </Text>
          <View style={styles.codeBlock}>
            <Text style={styles.codeText} selectable>{`GET  /api/v1/x-twitter/contacts
GET  /api/v1/x-twitter/contacts/:contactId
GET  /api/v1/x-twitter/contacts/:contactId/conversations
GET  /api/v1/x-twitter/conversations/:conversationId/messages
POST /api/v1/x-twitter/messages`}</Text>
          </View>

          <Text style={styles.fieldLabel}>Sample cURL (send X message)</Text>
          <View style={styles.codeBlock}>
            <Text style={styles.codeText} selectable>{`curl -X POST ${CODE_BASE_URL}/api/v1/x-twitter/messages \\
  -H "Authorization: Bearer ${rawKey || '<your_key>'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "conversationId": "<x_conversation_id>",
    "content": "Hello from DISLIX X API"
  }'`}</Text>
          </View>
        </View>

        {/* ── Response format reference ── */}
        <View style={styles.card}>
          <Text style={styles.sectionHeading}>Response Format</Text>
          <Text style={styles.cardDesc}>All endpoints return:</Text>
          <View style={styles.codeBlock}>
            <Text style={styles.codeText} selectable>{`{
  "statusCode": 1000,
  "data": { ... },
  "errors": [],
  "requestIdentifier": "uuid"
}`}</Text>
          </View>
          <View style={styles.tableWrapper}>
            {[['1000', 'Success'], ['1002', 'Not found'], ['1007', 'Missing fields'], ['1011', 'Unauthorized'], ['0', 'Server error']].map(([code, meaning]) => (
              <View key={code} style={styles.tableRow}>
                <Text style={[styles.mono, { width: 50 }]}>{code}</Text>
                <Text style={styles.endpointDesc}>{meaning}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── Webhook reference ── */}
        <View style={styles.card}>
          <Text style={styles.sectionHeading}>Webhook Payload</Text>
          <Text style={styles.cardDesc}>
            DISLIX POSTs to your URL with an HMAC signature header. Automated replies via webhooks are paused for contacts that have an active assignment — a human must handle them first.
          </Text>
          <View style={styles.codeBlock}>
            <Text style={styles.codeText} selectable>{`X-Sendifier-Signature: sha256=<hmac>

{
  "event": "message.received",
  "channel": "whatsapp|facebook_messenger|x_twitter",
  "contact": { "id": "...", "phoneNumber": "+9715x", "name": "..." },
  "message": { "type": "text", "content": "Hello", "direction": "inbound" },
  "assignment": { "userId": "...", "departmentId": "..." }  // present if assigned
}`}</Text>
          </View>
          <Text style={[styles.fieldHint, { marginTop: 0 }]}>For X webhooks, `contact` includes X identity fields (e.g. xUserId, xUsername) and messages map to DM/mention events.</Text>

          <Text style={[styles.fieldLabel, { marginTop: spacing.sm }]}>Assignment Webhook Payload</Text>
          <Text style={[styles.fieldHint, { marginTop: 0 }]}>
            Configured on the separate Assignment Webhooks tab, not a channel webhook — fires when an assignment is
            created, for whichever assignmentType(s) you subscribed to. Event name is type-specific.
          </Text>
          <View style={styles.codeBlock}>
            <Text style={styles.codeText} selectable>{`X-Sendifier-Signature: sha256=<hmac>
X-Sendifier-Event: assignment.contact   // or assignment.task, assignment.deal, etc.

{
  "event": "assignment.contact",
  "data": {
    "assignmentId": "...",
    "assignmentType": "contact",
    "targetType": "contact",
    "targetId": "...",
    "targetName": "...",
    "assignedTo": "userId or null",
    "departmentId": "departmentId or null",
    "assignedBy": "...",
    "assignedAt": "2026-01-01T00:00:00.000Z"
  }
}`}</Text>
          </View>
          <Text style={[styles.fieldLabel, { marginTop: spacing.xs }]}>Verify (Node.js)</Text>
          <View style={styles.codeBlock}>
            <Text style={styles.codeText} selectable>{`const expected = 'sha256=' +
  crypto.createHmac('sha256', secret)
    .update(rawBody).digest('hex');
crypto.timingSafeEqual(
  Buffer.from(expected), Buffer.from(signature)
);`}</Text>
          </View>
        </View>

      </View>
    );
  };

  // ─── Main render ─────────────────────────────────────────────────────

  return (
    <ScrollView
      ref={scrollViewRef}
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    >
      {/* Header */}
      <View style={styles.header}>
        {onBack && (
          <TouchableOpacity style={styles.backBtn} onPress={onBack}>
            <DirectionalIcon name="arrow-back" size={20} color={colors.text.primary} />
          </TouchableOpacity>
        )}
        <View style={{ flex: 1 }}>
          <Text style={styles.headerTitle}>Developer Access</Text>
          <Text style={styles.headerSub}>API keys, inbound webhooks, and integration reference</Text>
        </View>
      </View>

      {/* Initial loading */}
      {initialLoading && (
        <View style={styles.centeredState}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centeredStateText}>Loading developer settings…</Text>
        </View>
      )}

      {/* Access error — show instead of tabs when plan is not enabled */}
      {!initialLoading && accessError && (
        <View style={styles.accessErrorBanner}>
          <View style={styles.accessErrorIcon}>
            <Ionicons name="lock-closed" size={28} color={colors.error} />
          </View>
          <Text style={styles.accessErrorTitle}>Developer Access Unavailable</Text>
          <Text style={styles.accessErrorMessage}>{accessError}</Text>
          <View style={styles.accessErrorHint}>
            <Ionicons name="information-circle-outline" size={16} color={colors.text.secondary} />
            <Text style={styles.accessErrorHintText}>
              Contact your account manager or upgrade your plan to enable API access.
            </Text>
          </View>
          {/* Still allow the docs tab */}
          <TouchableOpacity
            style={[styles.outlineBtn, { marginTop: spacing.md, alignSelf: 'flex-start' }]}
            onPress={() => { setActiveTab('docs'); setAccessError(null); }}
          >
            <Text style={styles.outlineBtnText}>View API Reference</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Tabs — only shown when no access error */}
      {!initialLoading && !accessError && (
        <>
          <View style={styles.tabBar}>
            {renderTab('keys', 'API Keys', 'key-outline')}
            {renderTab('webhooks', 'Webhooks', 'git-network-outline')}
            {renderTab('docs', 'Reference', 'document-text-outline')}
          </View>

          {activeTab === 'keys' && renderKeysTab()}
          {activeTab === 'webhooks' && renderWebhooksTab()}
          {activeTab === 'docs' && renderDocsTab()}
        </>
      )}

      {/* Assignment Webhook trigger — user/department pickers */}
      <BusinessUserPicker
        visible={showAssignedUserPicker}
        onClose={() => setShowAssignedUserPicker(false)}
        onConfirm={(selectedIds) => {
          setAssignmentWebhookForm(f => ({ ...f, userIds: selectedIds }));
          setShowAssignedUserPicker(false);
        }}
        mode="multiple"
        source="users"
        title="Users to Watch"
        confirmText="Done"
        selectedUserIds={assignmentWebhookForm.userIds}
      />
      <BusinessUserPicker
        visible={showAssignedDeptPicker}
        onClose={() => setShowAssignedDeptPicker(false)}
        onConfirm={(selectedIds) => {
          setAssignmentWebhookForm(f => ({ ...f, departmentIds: selectedIds }));
          setShowAssignedDeptPicker(false);
        }}
        mode="multiple"
        source="departments"
        title="Departments to Watch"
        confirmText="Done"
        selectedUserIds={assignmentWebhookForm.departmentIds}
      />
    </ScrollView>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  scrollContent: {
    padding: spacing.md,
    paddingBottom: spacing.xl * 2,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  headerSub: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginTop: 2,
  },

  // Tabs
  tabBar: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginBottom: spacing.lg,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    padding: 4,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
  },
  tabActive: {
    backgroundColor: colors.background.surface,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 1,
  },
  tabLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.medium,
  },
  tabLabelActive: {
    color: colors.primary,
  },

  // Cards
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  cardTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  cardDesc: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    lineHeight: 20,
    marginBottom: spacing.sm,
  },
  sectionHeading: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },

  // Forms
  fieldLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.xs,
    marginTop: spacing.sm,
  },
  fieldHint: {
    fontSize: typography.fontSize.xs,
    color: colors.text.disabled,
    marginBottom: spacing.sm,
    marginTop: -4,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderRadius: spacing.borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: Platform.OS === 'web' ? spacing.sm : 10,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    backgroundColor: colors.background.primary,
    marginBottom: spacing.sm,
  },
  inlineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.xs,
  },
  locationPickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: colors.background.secondary,
    marginBottom: spacing.sm,
    alignSelf: 'flex-start',
  },
  locationPickerButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  locationPreview: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: colors.background.secondary,
    borderRadius: 8,
    padding: spacing.sm,
    marginBottom: spacing.sm,
  },
  locationPreviewAddress: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.medium,
  },
  locationPreviewCoords: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    marginTop: 2,
  },
  segmentRow: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  segmentBtn: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    backgroundColor: colors.background.secondary,
  },
  segmentBtnActive: {
    borderColor: colors.primary,
    backgroundColor: withAlpha(colors.primary, 0.08),
  },
  segmentBtnText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
  segmentBtnTextActive: {
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },

  // Buttons
  primaryBtn: {
    backgroundColor: colors.primary,
    borderRadius: spacing.borderRadius.sm,
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
  },
  primaryBtnDisabled: {
    opacity: 0.6,
  },
  primaryBtnText: {
    color: colors.white,
    fontFamily: typography.fontFamily.semibold,
    fontSize: typography.fontSize.sm,
  },
  outlineBtn: {
    borderRadius: spacing.borderRadius.sm,
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    alignItems: 'center',
    justifyContent: 'center',
  },
  outlineBtnText: {
    color: colors.text.secondary,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  iconBtn: {
    padding: spacing.xs,
    marginLeft: spacing.xs,
  },
  dangerIconBtn: {
    padding: spacing.xs,
    marginLeft: spacing.xs,
  },
  copyBtn: {
    padding: spacing.xs,
  },
  secretActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },

  // Secret banner
  secretBanner: {
    backgroundColor: withAlpha(colors.warning, 0.09),
    borderWidth: 1,
    borderColor: withAlpha(colors.warning, 0.38),
    borderRadius: spacing.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  secretBannerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  secretBannerTitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  secretBannerSub: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginBottom: spacing.sm,
    lineHeight: 18,
  },
  secretBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderWidth: 1,
    borderColor: colors.borderMedium,
  },
  secretText: {
    flex: 1,
    fontSize: typography.fontSize.xs,
    fontFamily: 'monospace',
    color: colors.text.primary,
    letterSpacing: 0.3,
  },

  // List items
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: spacing.sm,
  },
  listItemIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: withAlpha(colors.primary, 0.07),
    alignItems: 'center',
    justifyContent: 'center',
  },
  listItemContent: {
    flex: 1,
  },
  listItemTitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  listItemMeta: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    marginTop: 1,
  },

  // Empty state
  emptyState: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
    gap: spacing.sm,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.disabled,
  },

  // Docs
  codeBlock: {
    backgroundColor: '#1e1e2e',
    borderRadius: spacing.borderRadius.sm,
    padding: spacing.sm,
    marginBottom: spacing.sm,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  codeText: {
    flex: 1,
    fontSize: 12,
    fontFamily: 'monospace',
    color: '#cdd6f4',
    lineHeight: 18,
  },
  endpointRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  methodBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    minWidth: 46,
    alignItems: 'center',
    marginTop: 1,
  },
  badgeGet: {
    backgroundColor: '#e6f3ff',
  },
  badgePost: {
    backgroundColor: '#e6ffe6',
  },
  badgeDelete: {
    backgroundColor: '#ffe6e6',
  },
  badgePatch: {
    backgroundColor: '#fff3e0',
  },
  methodText: {
    fontSize: 10,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    textTransform: 'uppercase',
  },
  endpointPath: {
    fontSize: typography.fontSize.sm,
    fontFamily: 'monospace',
    color: colors.text.primary,
  },
  endpointDesc: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    marginTop: 2,
  },
  tableWrapper: {
    marginTop: spacing.sm,
    gap: spacing.xs,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
  },

  // Access error
  accessErrorBanner: {
    backgroundColor: withAlpha(colors.error, 0.05),
    borderWidth: 1,
    borderColor: withAlpha(colors.error, 0.25),
    borderRadius: spacing.borderRadius.md,
    padding: spacing.lg,
    marginBottom: spacing.md,
    alignItems: 'flex-start',
  },
  accessErrorIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: withAlpha(colors.error, 0.08),
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  accessErrorTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  accessErrorMessage: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    lineHeight: 20,
    marginBottom: spacing.sm,
  },
  accessErrorHint: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xs,
  },
  accessErrorHintText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    lineHeight: 18,
  },

  // Centered loading/empty state
  centeredState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xl * 2,
    gap: spacing.sm,
  },
  centeredStateText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },

  // Playground
  playCard: {
    borderColor: withAlpha(colors.primary, 0.25),
    borderWidth: 1.5,
  },
  playHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  playLiveDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.success,
  },
  playTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  playNoKeys: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.sm,
    marginBottom: spacing.sm,
  },
  playNoKeysText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.disabled,
    flex: 1,
  },
  keyPickerScroll: {
    marginBottom: spacing.sm,
  },
  keyPickerRow: {
    flexDirection: 'row',
    gap: spacing.xs,
    paddingVertical: 2,
  },
  keyChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    borderRadius: spacing.borderRadius.full,
    borderWidth: 1,
    borderColor: withAlpha(colors.primary, 0.31),
    backgroundColor: withAlpha(colors.primary, 0.04),
  },
  keyChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  keyChipText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
    maxWidth: 100,
  },
  keyChipTextActive: {
    color: colors.white,
  },
  keyChipPrefix: {
    fontSize: typography.fontSize.xs,
    color: withAlpha(colors.primary, 0.67),
    fontFamily: 'monospace',
  },
  keyChipPrefixActive: {
    color: withAlpha(colors.white, 0.8),
  },
  playKeyReady: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  playKeyReadyText: {
    fontSize: typography.fontSize.xs,
    color: colors.success,
    fontFamily: typography.fontFamily.medium,
  },
  playTextArea: {
    minHeight: 72,
    textAlignVertical: 'top',
    paddingTop: spacing.sm,
  },
  playResponseBox: {
    marginTop: spacing.md,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    overflow: 'hidden',
  },
  playResponseOk: {
    borderColor: withAlpha(colors.success, 0.38),
    backgroundColor: withAlpha(colors.success, 0.03),
  },
  playResponseErr: {
    borderColor: withAlpha(colors.error, 0.38),
    backgroundColor: withAlpha(colors.error, 0.03),
  },
  playResponseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  playResponseStatus: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
  },
  playResponseStatusOk: {
    color: colors.success,
  },
  playResponseStatusErr: {
    color: colors.error,
  },
  playResponseScroll: {
    maxHeight: 200,
  },
  playResponseBody: {
    fontSize: 12,
    fontFamily: 'monospace',
    color: colors.text.primary,
    padding: spacing.sm,
    lineHeight: 18,
  },
  typeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    borderRadius: spacing.borderRadius.full,
    borderWidth: 1,
    borderColor: withAlpha(colors.primary, 0.31),
    backgroundColor: withAlpha(colors.primary, 0.04),
  },
  typeChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  curlHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  templateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.secondary,
    marginBottom: spacing.xs,
  },
  templateRowActive: {
    borderColor: colors.primary,
    backgroundColor: withAlpha(colors.primary, 0.03),
  },
  langBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: withAlpha(colors.primary, 0.08),
  },
  langBadgeText: {
    fontSize: 10,
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
    textTransform: 'uppercase',
  },

  // Inline field errors
  inputError: {
    borderColor: colors.error,
  },
  fieldError: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    marginTop: spacing.xs,
  },

  // Inline delete confirmation
  inlineConfirm: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
    flexWrap: 'wrap',
  },
  inlineConfirmText: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.medium,
  },
  inlineConfirmCancel: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.borderMedium,
  },
  inlineConfirmCancelText: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.medium,
  },
  inlineConfirmDanger: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.error,
  },
  inlineConfirmDangerText: {
    fontSize: typography.fontSize.xs,
    color: colors.white,
    fontFamily: typography.fontFamily.semibold,
  },
  // Endpoint group + accordion
  epGroupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  epGroupTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  epItem: {
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  epItemActive: {
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.sm,
    paddingHorizontal: spacing.sm,
    marginHorizontal: -spacing.sm,
  },
  epHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  epBody: {
    marginTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: spacing.md,
  },

  templateParamsCard: {
    marginTop: spacing.sm,
    padding: spacing.sm,
    backgroundColor: colors.background.secondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  templatePreview: {
    marginTop: spacing.sm,
    padding: spacing.sm,
    backgroundColor: colors.background.primary,
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
  },
  templatePreviewHeader: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  templatePreviewBody: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.primary,
    color: colors.text.primary,
  },
  templatePreviewFooter: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.primary,
    color: colors.text.secondary,
    marginTop: spacing.xs,
  },
});
